import React, { useState, useMemo, useEffect, useCallback, useDeferredValue } from "react";
import Papa from "papaparse";
import ExcelJS from 'exceljs';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Typography,
  Container,
  Paper,
  Card,
  CardContent,
  Grid,
  Collapse,
  IconButton,
  Divider,
  Switch,
  FormControlLabel,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  ListItemText,
  OutlinedInput,
  Chip,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AssignmentIcon from "@mui/icons-material/Assignment";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import AdminLogin from "./AdminLogin";
import AdminDashboard from "./AdminDashboard";
import UserLogin from "./UserLogin";
import { logoutUser } from "./firebaseConfig";
import { createTestUser } from "./setupTestUser";
import StatusBadge from "./components/StatusBadge";
import VisitStatusCell from "./components/VisitStatusCell";
import {
  calculateHours,
  formatHoursAsUnits,
  calculateTimeDifference,
  detectOverlaps,
} from "./utils/timeHelpers";
import { highlightMatch as highlightMatchUtil } from "./utils/highlightMatch";
import { CSV_DATA_START_ROW } from "./constants/csvColumns";

/* ---------------------------------------------------------------------------
 * GPS Tracer helpers (module scope so they are never re-created per render)
 * ------------------------------------------------------------------------ */
const GPS_RANGE_FT = 1500;

// CSV stores "lng,lat"; Google Maps needs "lat,lng" -> flip.
const gpsToMapCoords = (raw) => {
  if (!raw) return '';
  const parts = String(raw).split(',').map(s => s.trim()).filter(Boolean);
  if (parts.length !== 2) return '';
  return `${parts[1]},${parts[0]}`;
};
const gpsMapsUrl = (raw) => {
  const c = gpsToMapCoords(raw);
  return c ? `https://www.google.com/maps?q=${encodeURIComponent(c)}` : '';
};
const gpsMapsSearchUrl = (addr) => addr ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}` : '';
// Driving route: origin = patient home, destination = caregiver clock GPS.
const gpsDirectionsUrl = (homeRaw, clockRaw, homeAddr) => {
  const dest = gpsToMapCoords(clockRaw);
  if (!dest) return '';
  const origin = gpsToMapCoords(homeRaw) || (homeAddr ? String(homeAddr).trim() : '');
  if (!origin) return '';
  return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(dest)}&travelmode=driving`;
};
const gpsHaversineMiles = (homeRaw, clockRaw) => {
  const h = gpsToMapCoords(homeRaw);
  const c = gpsToMapCoords(clockRaw);
  if (!h || !c) return null;
  const [lat1, lon1] = h.split(',').map(parseFloat);
  const [lat2, lon2] = c.split(',').map(parseFloat);
  if ([lat1, lon1, lat2, lon2].some(isNaN)) return null;
  const R = 3958.8; // miles
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};
// Approximate drive time at 30 mph average city speed
const gpsDriveMinutes = (miles) => miles == null ? null : Math.max(1, Math.round((miles / 30) * 60));
const gpsToFeet = (v) => {
  const n = parseFloat(String(v ?? '').replace(/[^0-9.\-]/g, ''));
  return isNaN(n) ? null : Math.round(n);
};

/* ============================================================================
 * GPS Map Dialog
 * Lightweight in-app map (Leaflet + OpenStreetMap, no API key required).
 * - Red pin   = patient address
 * - Blue pin  = caregiver clock-in / clock-out location
 * - Pins are fixed (non-draggable) and labelled with name + address.
 * - Fully responsive: full-screen on phones, centered card on tablet/desktop.
 * - Closes on backdrop click or Escape key. Locks body scroll while open.
 * ========================================================================== */

const LEAFLET_CDN = 'https://unpkg.com/leaflet@1.9.4/dist';
let __leafletPromise = null;

function ensureLeafletCss() {
  if (typeof document === 'undefined' || document.getElementById('leaflet-css')) return;
  const link = document.createElement('link');
  link.id = 'leaflet-css';
  link.rel = 'stylesheet';
  link.href = `${LEAFLET_CDN}/leaflet.css`;
  document.head.appendChild(link);
}

function loadLeaflet() {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  ensureLeafletCss();
  if (window.L) return Promise.resolve(window.L);
  if (__leafletPromise) return __leafletPromise;

  __leafletPromise = new Promise((resolve, reject) => {
    let script = document.getElementById('leaflet-js');
    if (!script) {
      script = document.createElement('script');
      script.id = 'leaflet-js';
      script.src = `${LEAFLET_CDN}/leaflet.js`;
      script.async = true;
      document.head.appendChild(script);
    }
    const onLoad = () => window.L ? resolve(window.L) : reject(new Error('Leaflet missing after load'));
    if (window.L) return onLoad();
    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', () => reject(new Error('Failed to load map library')), { once: true });
    setTimeout(() => { if (!window.L) reject(new Error('Map library load timed out')); }, 15000);
  });
  return __leafletPromise;
}

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const parseLatLng = (s) => {
  if (!s) return null;
  const [lat, lng] = String(s).split(',').map((v) => parseFloat(v.trim()));
  return (isNaN(lat) || isNaN(lng)) ? null : [lat, lng];
};

const buildPinIcon = (L, color, label, sublabel) => L.divIcon({
  className: 'gps-pin-icon',
  html: `
    <div style="position:relative;display:flex;flex-direction:column;align-items:center;pointer-events:none;">
      <div style="background:${color};color:#fff;font-weight:700;font-size:11px;padding:4px 9px;border-radius:6px;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.3);margin-bottom:4px;max-width:240px;overflow:hidden;text-overflow:ellipsis;letter-spacing:0.2px;">
        ${label}${sublabel ? `<div style="font-weight:500;font-size:10px;opacity:0.92;margin-top:1px;">${sublabel}</div>` : ''}
      </div>
      <svg width="30" height="42" viewBox="0 0 30 42" style="filter:drop-shadow(0 3px 4px rgba(0,0,0,0.4));">
        <path d="M15 0 C6.7 0 0 6.7 0 15 C0 26 15 42 15 42 C15 42 30 26 30 15 C30 6.7 23.3 0 15 0 Z" fill="${color}" stroke="#fff" stroke-width="2.5"/>
        <circle cx="15" cy="15" r="5.5" fill="#fff"/>
      </svg>
    </div>
  `,
  iconSize: [30, 50],
  iconAnchor: [15, 42],
  popupAnchor: [0, -38]
});

function GpsMapDialog({ data, onClose }) {
  const containerRef = React.useRef(null);
  const mapRef = React.useRef(null);
  const [ready, setReady] = React.useState(typeof window !== 'undefined' && !!window.L);
  const [error, setError] = React.useState('');

  // ---- Body scroll lock + Escape-to-close while open --------------------
  React.useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // ---- Lazy-load Leaflet on first open ----------------------------------
  React.useEffect(() => {
    if (ready) return;
    let cancelled = false;
    loadLeaflet()
      .then(() => { if (!cancelled) setReady(true); })
      .catch((e) => { if (!cancelled) setError(e.message || 'Failed to load map'); });
    return () => { cancelled = true; };
  }, [ready]);

  // ---- Mount the map ----------------------------------------------------
  React.useEffect(() => {
    if (!ready || !containerRef.current || !window.L) return;
    const L = window.L;
    const container = containerRef.current;

    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }

    const patientLL = parseLatLng(data.patientCoord);
    const clockLL   = parseLatLng(data.clockCoord);
    const fallback  = [39.8283, -98.5795]; // US center
    const center    = patientLL || clockLL || fallback;
    const kindLabel = data.kind === 'in' ? 'Clocked IN' : 'Clocked OUT';
    const distLabel = data.distanceFt != null ? `${data.distanceFt.toLocaleString()} ft away` : '';
    const patientName = escapeHtml(data.patientName || 'Patient');
    const patientAddr = escapeHtml(data.patientAddress || '');

    let map = null;
    let initialized = false;
    const markers = [];
    const cleanupFns = [];

    const init = () => {
      if (initialized) return;
      if (container.clientWidth < 50 || container.clientHeight < 50) return;
      initialized = true;

      map = L.map(container, {
        center,
        zoom: 14,
        zoomControl: true,
        attributionControl: true,
        scrollWheelZoom: true
      });
      mapRef.current = map;

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>'
      }).addTo(map);

      if (patientLL) {
        const m = L.marker(patientLL, {
          icon: buildPinIcon(L, '#dc2626', `🏠 ${patientName}`, patientAddr),
          draggable: false, keyboard: false, riseOnHover: true
        }).addTo(map).bindPopup(
          `<div style="min-width:180px;"><b style="color:#b91c1c;">🏠 Patient</b><br/>${patientName}` +
          (patientAddr ? `<br/><span style="color:#475569;">${patientAddr}</span>` : '') + `</div>`
        );
        markers.push(m);
      }
      if (clockLL) {
        const m = L.marker(clockLL, {
          icon: buildPinIcon(L, '#2563eb', `📍 ${kindLabel}`, distLabel),
          draggable: false, keyboard: false, riseOnHover: true
        }).addTo(map).bindPopup(
          `<div style="min-width:180px;"><b style="color:#1d4ed8;">📍 Caregiver · ${kindLabel}</b><br/>` +
          `<span style="color:#475569;">${escapeHtml(data.clockCoord || '')}</span>` +
          (distLabel ? `<br/><span style="color:#475569;">${distLabel}</span>` : '') + `</div>`
        );
        markers.push(m);
      }
      if (patientLL && clockLL) {
        L.polyline([patientLL, clockLL], { color: '#7c3aed', weight: 3, opacity: 0.85, dashArray: '6,8' }).addTo(map);
      }

      const fit = () => {
        if (!mapRef.current) return;
        mapRef.current.invalidateSize();
        if (markers.length >= 2) {
          mapRef.current.fitBounds(L.featureGroup(markers).getBounds().pad(0.4));
        } else if (markers.length === 1) {
          mapRef.current.setView(markers[0].getLatLng(), 16);
        }
      };
      fit();
      const t = setTimeout(fit, 250);
      cleanupFns.push(() => clearTimeout(t));
    };

    init();

    // Robust sizing: ResizeObserver + a couple of fallback ticks
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => {
        if (!initialized) init();
        else if (mapRef.current) mapRef.current.invalidateSize();
      });
      ro.observe(container);
      cleanupFns.push(() => ro.disconnect());
    }
    const tickIds = [60, 200, 500].map((ms) => setTimeout(() => {
      if (!initialized) init();
      else if (mapRef.current) mapRef.current.invalidateSize();
    }, ms));
    cleanupFns.push(() => tickIds.forEach(clearTimeout));

    const onWinResize = () => mapRef.current && mapRef.current.invalidateSize();
    window.addEventListener('resize', onWinResize);
    cleanupFns.push(() => window.removeEventListener('resize', onWinResize));

    return () => {
      cleanupFns.forEach((fn) => { try { fn(); } catch (_) {} });
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
      if (container) { try { delete container._leaflet_id; } catch (_) {} }
    };
  }, [ready, data]);

  if (!data) return null;

  const headerSubtitle = [
    data.kind === 'in' ? 'Clock-In' : 'Clock-Out',
    data.distanceFt != null && `${data.distanceFt.toLocaleString()} ft out of range`,
    data.miles != null && `≈ ${data.miles.toFixed(2)} mi`,
    data.driveMinutes != null && `≈ ${data.driveMinutes} min`
  ].filter(Boolean).join(' · ');

  return (
    <Box
      role="dialog"
      aria-modal="true"
      aria-label="Visit map"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        background: 'rgba(15,23,42,0.7)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'center',
        p: { xs: 0, sm: 2, md: 3 },
        animation: 'gpsMapFadeIn 0.18s ease-out',
        '@keyframes gpsMapFadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 }
        }
      }}
    >
      <Box sx={{
        width: '100%',
        maxWidth: 960,
        background: '#fff',
        borderRadius: { xs: 0, sm: 3 },
        overflow: 'hidden',
        boxShadow: '0 25px 60px rgba(0,0,0,0.45)',
        display: 'flex',
        flexDirection: 'column',
        height: { xs: '100%', sm: 'auto' },
        maxHeight: { xs: '100%', sm: '95vh' }
      }}>
        {/* ---------- Header ---------- */}
        <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', color: '#fff', flexShrink: 0 }}>
          <Box sx={{
            px: { xs: 1.75, sm: 2.5 },
            py: { xs: 1.5, sm: 1.75 },
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            flexWrap: 'wrap'
          }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '0.95rem', sm: '1.05rem' }, fontWeight: 800, lineHeight: 1.2 }}>
                <span role="img" aria-hidden="true">🗺️</span>
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {data.patientName || 'Visit Map'}
                </Box>
              </Box>
              {headerSubtitle && (
                <Box sx={{ fontSize: '0.74rem', opacity: 0.85, mt: 0.4, fontWeight: 500 }}>{headerSubtitle}</Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
              {data.directionsUrl && (
                <Box component="a" href={data.directionsUrl} target="_blank" rel="noopener noreferrer" sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  px: 1.5, py: 0.6,
                  background: '#2563eb', color: '#fff',
                  borderRadius: 1.5, fontSize: '0.75rem', fontWeight: 700,
                  textDecoration: 'none',
                  transition: 'background 0.15s ease',
                  '&:hover': { background: '#1d4ed8' }
                }}>🚗 Google Maps</Box>
              )}
              <Box
                component="button"
                type="button"
                onClick={onClose}
                aria-label="Close map"
                sx={{
                  border: 'none', cursor: 'pointer',
                  background: 'rgba(255,255,255,0.12)', color: '#fff',
                  fontSize: '0.85rem', fontWeight: 700,
                  px: 1.4, py: 0.6, borderRadius: 1.5,
                  transition: 'background 0.15s ease',
                  '&:hover': { background: 'rgba(255,255,255,0.22)' }
                }}
              >✕ Close</Box>
            </Box>
          </Box>
          {/* Legend */}
          <Box sx={{
            px: { xs: 1.75, sm: 2.5 },
            py: 0.9,
            background: 'rgba(0,0,0,0.25)',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            gap: { xs: 1.25, sm: 2 },
            flexWrap: 'wrap',
            fontSize: { xs: '0.68rem', sm: '0.72rem' },
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, fontWeight: 600 }}>
              <Box sx={{ width: 11, height: 11, borderRadius: '50%', background: '#dc2626', border: '2px solid #fff', flexShrink: 0 }} />
              <span>Patient address</span>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, fontWeight: 600 }}>
              <Box sx={{ width: 11, height: 11, borderRadius: '50%', background: '#2563eb', border: '2px solid #fff', flexShrink: 0 }} />
              <span>Caregiver clock-{data.kind === 'in' ? 'in' : 'out'}</span>
            </Box>
            <Box sx={{ opacity: 0.65, fontStyle: 'italic' }}>Pins are fixed.</Box>
          </Box>
        </Box>

        {/* ---------- Map ---------- */}
        <Box sx={{
          position: 'relative',
          width: '100%',
          flex: { xs: 1, sm: 'none' },
          height: { sm: 460, md: 540 },
          minHeight: { xs: 280 },
          background: '#e5e7eb'
        }}>
          <Box ref={containerRef} sx={{ position: 'absolute', inset: 0 }} />
          {(!ready && !error) && (
            <Box sx={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: 1.25,
              color: '#475569', fontWeight: 600,
              background: '#f1f5f9',
              pointerEvents: 'none'
            }}>
              <Box sx={{
                width: 36, height: 36,
                border: '3px solid #cbd5e1',
                borderTopColor: '#0ea5e9',
                borderRadius: '50%',
                animation: 'gpsMapSpin 0.8s linear infinite',
                '@keyframes gpsMapSpin': {
                  from: { transform: 'rotate(0deg)' },
                  to: { transform: 'rotate(360deg)' }
                }
              }} />
              <Box sx={{ fontSize: '0.85rem' }}>Loading map…</Box>
            </Box>
          )}
          {error && (
            <Box sx={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: 1.25,
              color: '#b91c1c', fontWeight: 600,
              background: '#fef2f2',
              p: 3, textAlign: 'center'
            }}>
              <Box sx={{ fontSize: '2rem' }}>⚠️</Box>
              <Box sx={{ fontSize: '0.9rem' }}>{error}</Box>
              {data.directionsUrl && (
                <Box component="a" href={data.directionsUrl} target="_blank" rel="noopener noreferrer" sx={{
                  mt: 1, px: 1.75, py: 0.7, background: '#1d4ed8', color: '#fff',
                  borderRadius: 1.5, fontSize: '0.8rem', fontWeight: 700, textDecoration: 'none',
                  '&:hover': { background: '#1e3a8a' }
                }}>🚗 Open in Google Maps instead</Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default function App() {
  // Demo mode - when true, ALL login functionality is completely disabled
  // No secret keys in code - just a simple on/off switch at build time
  const DEMO_MODE = process.env.REACT_APP_DEMO_MODE === 'true';
  
  // Authentication states
  const [authMode, setAuthMode] = useState(null); // null, 'admin', 'user', 'adminLoggedIn', 'userLoggedIn'
  const [currentUser, setCurrentUser] = useState(null);

  // Data states
  const [groupedData, setGroupedData] = useState([]);
  const [expandedPatients, setExpandedPatients] = useState({});
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tableView, setTableView] = useState(true); // Toggle between table/card view
  const [dragActive, setDragActive] = useState(false); // drag & drop visual state
  const [droppedFileName, setDroppedFileName] = useState("");
  const [selectedContracts, setSelectedContracts] = useState([]); // For multi-select filter
  const [availableContracts, setAvailableContracts] = useState([]); // All unique contracts
  const [selectedBranches, setSelectedBranches] = useState([]); // For branch filter
  const [availableBranches, setAvailableBranches] = useState([]); // All unique branches
  const [billedDate, setBilledDate] = useState(""); // User-entered billed date for export
  const [csvFromDate, setCsvFromDate] = useState(""); // From date from CSV row 2 column C
  const [csvEndDate, setCsvEndDate] = useState(""); // End date from CSV row 2 column D
  const [csvAgencyName, setCsvAgencyName] = useState(""); // Agency name from CSV row 1
  const [statusFilter, setStatusFilter] = useState(""); // Status filter: Overlap, Missed In, Missed Out
  const [billedFilter, setBilledFilter] = useState(""); // Billed filter: Yes, No
  const [displayLimit, setDisplayLimit] = useState(50); // Initial display limit
  const [isLoadingMore, setIsLoadingMore] = useState(false); // Loading more indicator
  const [selectedPatientIds, setSelectedPatientIds] = useState([]); // For Print PDF selection
  const [gpsData, setGpsData] = useState([]); // GPS Tracer rows (raw object array)
  const [gpsHeaders, setGpsHeaders] = useState([]); // Column headers from GPS file
  const [gpsFileName, setGpsFileName] = useState(""); // GPS file name for label
  const [gpsSearch, setGpsSearch] = useState(""); // Search filter for GPS table
  const [gpsOnlyOutOfRange, setGpsOnlyOutOfRange] = useState(false); // Toggle: only show out-of-range visits
  const [gpsSelectedKeys, setGpsSelectedKeys] = useState([]); // Selected GPS patient groups for Excel export
  const [gpsVisibleCount, setGpsVisibleCount] = useState(25); // Patient cards rendered at once
  const [gpsLoading, setGpsLoading] = useState(false); // GPS CSV parsing in progress
  const [mapDialog, setMapDialog] = useState(null); // {patientName, patientAddress, patientCoord, clockKind, clockCoord, distanceFt, miles, driveMinutes}

  /* ---- GPS Tracer: parse + group once per uploaded file (not per render) ---- */
  const gpsIndex = useMemo(() => {
    if (gpsData.length === 0 || gpsHeaders.length === 0) return { groups: [], totalOutOfRange: 0 };

    const norm = (s) => String(s).toLowerCase().replace(/[\s_-]/g, '');
    const headerMap = new Map(gpsHeaders.filter(Boolean).map(h => [norm(h), h]));
    const col = (needle) => headerMap.get(norm(needle));
    const cAdmissionId  = col('AdmissionID');
    const cPatientName  = col('PatientName');
    const cMedicaid     = col('MedicaidNumber');
    const cVisitDate    = col('VisitDate');
    const cSchedTime    = col('ScheduledTime');
    const cEvvTime      = col('EVVTime');
    const cCaregiver    = col('CaregiverName');
    const cInDistCol    = col('Clock_In_Distance');
    const cOutDistCol   = col('Clock_Out_Distance');
    const cInAddrCol    = col('PatientClock_In_Address');
    const cOutAddrCol   = col('PatientClock_Out_Address');
    const cInGpsCol     = col('Clock_In_GPS');
    const cOutGpsCol    = col('Clock_Out_GPS');
    const cPatInGpsCol  = col('Patient_Clock_In_Address_GPS');
    const cPatOutGpsCol = col('Patient_Clock_Out_Address_GPS');

    const buildNotes = (r, inDist, outDist) => {
      const notes = [];
      if (inDist != null && inDist > GPS_RANGE_FT) {
        const miles = gpsHaversineMiles(r[cPatInGpsCol], r[cInGpsCol]);
        const addr = String(r[cInAddrCol] ?? '').trim();
        notes.push({
          kind: 'in', distance: inDist, patientAddress: addr,
          patientGpsUrl: gpsMapsUrl(r[cPatInGpsCol]) || gpsMapsSearchUrl(addr),
          patientCoord: gpsToMapCoords(r[cPatInGpsCol]),
          clockGps: gpsToMapCoords(r[cInGpsCol]),
          clockGpsUrl: gpsMapsUrl(r[cInGpsCol]),
          directionsUrl: gpsDirectionsUrl(r[cPatInGpsCol], r[cInGpsCol], r[cInAddrCol]),
          miles, driveMinutes: gpsDriveMinutes(miles)
        });
      }
      if (outDist != null && outDist > GPS_RANGE_FT) {
        const miles = gpsHaversineMiles(r[cPatOutGpsCol], r[cOutGpsCol]);
        const addr = String(r[cOutAddrCol] ?? '').trim();
        notes.push({
          kind: 'out', distance: outDist, patientAddress: addr,
          patientGpsUrl: gpsMapsUrl(r[cPatOutGpsCol]) || gpsMapsSearchUrl(addr),
          patientCoord: gpsToMapCoords(r[cPatOutGpsCol]),
          clockGps: gpsToMapCoords(r[cOutGpsCol]),
          clockGpsUrl: gpsMapsUrl(r[cOutGpsCol]),
          directionsUrl: gpsDirectionsUrl(r[cPatOutGpsCol], r[cOutGpsCol], r[cOutAddrCol]),
          miles, driveMinutes: gpsDriveMinutes(miles)
        });
      }
      return notes;
    };

    const map = new Map();
    let totalOutOfRange = 0;

    for (const r of gpsData) {
      const id   = String(r[cAdmissionId] ?? '').trim() || 'UNKNOWN';
      const name = String(r[cPatientName] ?? '').trim() || 'Unknown Patient';
      const key  = `${id}|||${name}`;
      let g = map.get(key);
      if (!g) {
        g = {
          key, admissionId: id, patientName: name,
          medicaid: String(r[cMedicaid] ?? '').trim(),
          address: '', addressGps: '', rows: [], outOfRange: 0,
          haystackParts: [`${name} ${id} ${String(r[cMedicaid] ?? '')}`]
        };
        map.set(key, g);
      }
      if (!g.address) {
        const addr = String(r[cInAddrCol] ?? r[cOutAddrCol] ?? '').trim();
        if (addr) g.address = addr;
      }
      if (!g.addressGps) {
        const gps = String(r[cPatInGpsCol] ?? r[cPatOutGpsCol] ?? '').trim();
        if (gps) g.addressGps = gps;
      }

      const inDist  = gpsToFeet(r[cInDistCol]);
      const outDist = gpsToFeet(r[cOutDistCol]);
      const notes   = buildNotes(r, inDist, outDist);
      const flat = {
        visitDate: String(r[cVisitDate] ?? ''),
        scheduled: String(r[cSchedTime] ?? ''),
        evv: String(r[cEvvTime] ?? ''),
        caregiver: String(r[cCaregiver] ?? ''),
        inDist, outDist,
        inFlag: inDist != null && inDist > GPS_RANGE_FT,
        outFlag: outDist != null && outDist > GPS_RANGE_FT
      };
      g.rows.push({ notes, flat });
      g.haystackParts.push(Object.values(r).join(' '));
      if (notes.length > 0) { g.outOfRange += 1; totalOutOfRange += 1; }
    }

    // One lowercase blob per patient makes search a single indexOf per patient.
    const groups = Array.from(map.values()).map(g => {
      const haystack = g.haystackParts.join(' ').toLowerCase();
      delete g.haystackParts;
      return { ...g, haystack };
    }).sort((a, b) => a.patientName.localeCompare(b.patientName));

    return { groups, totalOutOfRange };
  }, [gpsData, gpsHeaders]);

  // Keeps typing responsive: the list re-filters at a lower priority.
  const deferredGpsSearch = useDeferredValue(gpsSearch);

  const gpsGroupList = useMemo(() => {
    const q = deferredGpsSearch.trim().toLowerCase();
    let list = q ? gpsIndex.groups.filter(g => g.haystack.includes(q)) : gpsIndex.groups;
    if (gpsOnlyOutOfRange) {
      list = list.reduce((acc, g) => {
        const rows = g.rows.filter(rr => rr.notes.length > 0);
        if (rows.length > 0) acc.push({ ...g, rows, outOfRange: rows.reduce((s, rr) => s + rr.notes.length, 0) });
        return acc;
      }, []);
    }
    return list;
  }, [gpsIndex, deferredGpsSearch, gpsOnlyOutOfRange]);

  useEffect(() => {
    setGpsVisibleCount(25);
  }, [deferredGpsSearch, gpsOnlyOutOfRange, gpsFileName]);

  // Load authentication state from localStorage on mount
  useEffect(() => {
    // In demo mode, don't restore auth unless admin access key is provided
    if (DEMO_MODE && !hasAdminAccess) {
      // Clear any existing auth in demo mode
      localStorage.removeItem('authMode');
      localStorage.removeItem('currentUser');
      return;
    }
    
    const savedAuthMode = localStorage.getItem('authMode');
    const savedUser = localStorage.getItem('currentUser');
    
    if (savedAuthMode && savedUser) {
      setAuthMode(savedAuthMode);
      setCurrentUser(JSON.parse(savedUser));
    }
  }, [DEMO_MODE]);

  // Debounce search input for better performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Reset display limit when filters change
  useEffect(() => {
    setDisplayLimit(50);
  }, [debouncedSearch, selectedContracts, selectedBranches, statusFilter, billedFilter]);

  // Load more function
  const handleLoadMore = () => {
    setIsLoadingMore(true);
    setTimeout(() => {
      setDisplayLimit(prev => prev + 50);
      setIsLoadingMore(false);
    }, 300);
  };

  // Professional styling theme
  const theme = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
    primaryColor: '#059669',
    secondaryColor: '#10b981',
    accentColor: '#34d399',
    warningColor: '#f59e0b',
    errorColor: '#ef4444',
    backgroundColor: '#f0fdf4',
    cardBackground: '#ffffff',
    headerBackground: '#16a34a',
    borderColor: '#d1fae5',
    textPrimary: '#064e3b',
    textSecondary: '#047857',
  };

  // ---- GPS Tracer CSV upload ----
  const handleGpsUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please select a valid CSV file for GPS Tracer');
      e.target.value = '';
      return;
    }
    setGpsLoading(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      worker: true, // keeps the UI responsive on large files
      complete: (results) => {
        const rows = (results.data || []).filter(r => Object.values(r).some(v => v && String(v).trim() !== ''));
        const headers = (results.meta && results.meta.fields) ? results.meta.fields : (rows[0] ? Object.keys(rows[0]) : []);
        setGpsData(rows);
        setGpsHeaders(headers);
        setGpsFileName(file.name);
        setGpsSearch('');
        setGpsSelectedKeys([]);
        setGpsLoading(false);
      },
      error: (err) => {
        setGpsLoading(false);
        alert('Failed to parse GPS file: ' + err.message);
      }
    });
    e.target.value = '';
  };

  const clearGpsData = () => {
    setGpsData([]);
    setGpsHeaders([]);
    setGpsFileName('');
    setGpsSearch('');
    setGpsSelectedKeys([]);
  };

  const toggleGpsSelected = (key) => {
    setGpsSelectedKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  // Export the selected GPS Tracer patients to a print-ready Excel sheet
  // (landscape A4, scaled to one page wide, repeating header row).
  const exportGpsSelectedToExcel = async (groups) => {
    if (!groups || groups.length === 0) {
      alert('Select at least one patient to export.');
      return;
    }

    const C = {
      navy:     'FF0F2A47',
      navySoft: 'FF1D4E7E',
      blue:     'FF1E5F94',
      headBg:   'FF13395C',
      band:     'FFF4F8FC',
      white:    'FFFFFFFF',
      line:     'FFB9CCE0',
      lineSoft: 'FFDCE6F1',
      text:     'FF1F2937',
      muted:    'FF64748B',
      green:    'FF15803D',
      greenBg:  'FFEAF7EE',
      red:      'FFB91C1C',
      redBg:    'FFFDECEC',
      amberBg:  'FFFFF7E6'
    };
    const F = (size, opts = {}) => ({ name: 'Calibri', size, color: { argb: C.text }, ...opts });
    const fill = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });
    const bd = (style, argb) => ({ style, color: { argb } });
    const box = (style = 'thin', argb = C.line) => ({
      top: bd(style, argb), left: bd(style, argb), bottom: bd(style, argb), right: bd(style, argb)
    });

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'GPS Tracer';
    workbook.created = new Date();

    const ws = workbook.addWorksheet('GPS Tracer', {
      views: [{ showGridLines: false }],
      pageSetup: {
        paperSize: 9, // A4
        orientation: 'landscape',
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
        horizontalCentered: true,
        margins: { left: 0.3, right: 0.3, top: 0.55, bottom: 0.55, header: 0.25, footer: 0.25 }
      },
      headerFooter: {
        differentFirst: false,
        // Text after a &<size> code must not start with a digit or Excel absorbs it into the size.
        oddFooter: '&L&"Calibri,Italic"&9GPS Tracer Report&C&"Calibri,Regular"&9Page &P of &N' +
                   `&R&"Calibri,Italic"&9Printed ${new Date().toLocaleDateString()}`
      }
    });

    const HEADERS = ['#', 'Visit Date', 'Scheduled', 'EVV', 'Caregiver', 'Clock In', 'Clock Out', 'Status', 'Range Details'];
    const LAST = HEADERS.length; // 9 -> column I
    const LAST_L = 'I';
    ws.columns = [
      { width: 4.5 }, { width: 12 }, { width: 18 }, { width: 18 }, { width: 23 },
      { width: 11 }, { width: 11 }, { width: 13 }, { width: 62 }
    ];
    const mergeAll = (r) => ws.mergeCells(`A${r}:${LAST_L}${r}`);
    const paint = (r, f, border) => {
      for (let c = 1; c <= LAST; c++) {
        const cell = ws.getRow(r).getCell(c);
        if (f) cell.fill = fill(f);
        if (border) cell.border = border;
      }
    };

    const totalVisits  = groups.reduce((s, g) => s + g.rows.length, 0);
    const totalFlagged = groups.reduce((s, g) => s + g.rows.filter(x => x.notes.length > 0).length, 0);
    const compliance   = totalVisits ? Math.round(((totalVisits - totalFlagged) / totalVisits) * 100) : 100;

    // ---- Title block -------------------------------------------------------
    mergeAll(1);
    const t1 = ws.getCell('A1');
    t1.value = 'GPS TRACER — VISIT LOCATION REPORT';
    t1.font = F(20, { bold: true, color: { argb: C.white } });
    t1.alignment = { horizontal: 'center', vertical: 'middle' };
    paint(1, C.navy);
    ws.getRow(1).height = 36;

    mergeAll(2);
    const t2 = ws.getCell('A2');
    t2.value = `${csvAgencyName || currentUser?.agency || 'Agency Report'}   •   Source: ${gpsFileName || 'GPS file'}   •   Generated ${new Date().toLocaleString()}`;
    t2.font = F(10, { color: { argb: C.white }, italic: true });
    t2.alignment = { horizontal: 'center', vertical: 'middle' };
    paint(2, C.navySoft);
    ws.getRow(2).height = 20;

    // ---- Summary strip -----------------------------------------------------
    const stats = [
      ['PATIENTS', String(groups.length), C.blue],
      ['VISITS', String(totalVisits), C.blue],
      ['OUT OF RANGE', String(totalFlagged), totalFlagged > 0 ? C.red : C.green],
      ['IN RANGE', String(totalVisits - totalFlagged), C.green],
      ['COMPLIANCE', `${compliance}%`, compliance >= 90 ? C.green : C.red]
    ];
    ws.mergeCells('A3:B3'); ws.mergeCells('C3:D3'); ws.mergeCells('E3:F3'); ws.mergeCells('H3:I3');
    const statAnchors = ['A3', 'C3', 'E3', 'G3', 'H3'];
    stats.forEach(([label, value, color], i) => {
      const cell = ws.getCell(statAnchors[i]);
      cell.value = { richText: [
        { text: `${label}   `, font: { name: 'Calibri', size: 9, bold: true, color: { argb: C.muted } } },
        { text: value,         font: { name: 'Calibri', size: 13, bold: true, color: { argb: color } } }
      ]};
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });
    paint(3, C.band, box('thin', C.lineSoft));
    ws.getRow(3).height = 26;

    ws.getRow(4).height = 6; // spacer

    // ---- Column header row -------------------------------------------------
    const HEAD_ROW = 5;
    const headerRow = ws.getRow(HEAD_ROW);
    HEADERS.forEach((h, i) => {
      const cell = headerRow.getCell(i + 1);
      cell.value = h;
      cell.font = F(10, { bold: true, color: { argb: C.white } });
      cell.alignment = { horizontal: i === 4 || i === 8 ? 'left' : 'center', vertical: 'middle', wrapText: true, indent: i === 4 || i === 8 ? 1 : 0 };
      cell.fill = fill(C.headBg);
      cell.border = {
        top: bd('thin', C.headBg), left: bd('thin', C.headBg),
        bottom: bd('medium', C.navy), right: bd('thin', C.headBg)
      };
    });
    headerRow.height = 24;
    ws.views = [{ state: 'frozen', ySplit: HEAD_ROW, showGridLines: false }];
    ws.pageSetup.printTitlesRow = `${HEAD_ROW}:${HEAD_ROW}`;

    // ---- Patient sections --------------------------------------------------
    const noteRich = (n) => {
      const seg = (text, o = {}) => ({ text, font: { name: 'Calibri', size: 9, color: { argb: C.text }, ...o } });
      const out = [
        seg(`${n.kind === 'in' ? 'CLOCK-IN' : 'CLOCK-OUT'} ${n.distance.toLocaleString()} ft out of range`, { bold: true, color: { argb: C.red } }),
        seg('\nHome: ', { bold: true }), seg(n.patientAddress || '—'),
        seg('\nClocked at: ', { bold: true }), seg(n.clockGps || '—')
      ];
      if (n.miles != null) out.push(seg('   |   Approx ', { bold: true }), seg(`${n.miles.toFixed(2)} mi · ${n.driveMinutes} min drive`));
      return { richText: out };
    };

    let r = 6;
    groups.forEach((g, gi) => {
      if (gi > 0) { ws.getRow(r).height = 8; r += 1; } // gap between patients

      // Section header: name (A:F) + counters (G:I)
      const sectionRow = r;
      ws.mergeCells(`A${sectionRow}:F${sectionRow}`);
      ws.mergeCells(`G${sectionRow}:${LAST_L}${sectionRow}`);
      const nameCell = ws.getCell(`A${sectionRow}`);
      nameCell.value = { richText: [
        { text: g.patientName.toUpperCase(), font: { name: 'Calibri', size: 12, bold: true, color: { argb: C.white } } },
        { text: `    Admission #${g.admissionId}${g.medicaid ? `    Medicaid ${g.medicaid}` : ''}`, font: { name: 'Calibri', size: 9, color: { argb: 'FFD6E4F0' } } }
      ]};
      nameCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
      const countCell = ws.getCell(`G${sectionRow}`);
      countCell.value = `${g.rows.length} visit${g.rows.length === 1 ? '' : 's'}   |   ${g.outOfRange > 0 ? `${g.outOfRange} out of range` : 'all in range'}`;
      countCell.font = F(10, { bold: true, color: { argb: C.white } });
      countCell.alignment = { horizontal: 'right', vertical: 'middle', indent: 1 };
      paint(sectionRow, C.blue, { top: bd('medium', C.navy), bottom: bd('thin', C.navy), left: bd('thin', C.blue), right: bd('thin', C.blue) });
      ws.getRow(sectionRow).height = 22;
      r += 1;

      // Address sub-line
      if (g.address) {
        mergeAll(r);
        const addr = ws.getCell(`A${r}`);
        addr.value = `Patient address:  ${g.address}`;
        addr.font = F(9, { italic: true, color: { argb: C.muted } });
        addr.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
        paint(r, C.band, box('thin', C.lineSoft));
        ws.getRow(r).height = 17;
        r += 1;
      }

      g.rows.forEach(({ flat: src, notes }, ri) => {
        const flagged = notes.length > 0;
        const rowBg = flagged ? C.redBg : (ri % 2 === 0 ? C.white : C.band);
        const values = [
          ri + 1,
          src.visitDate || '—',
          src.scheduled || '—',
          src.evv || '—',
          src.caregiver || '—',
          src.inDist == null ? '—' : src.inDist,
          src.outDist == null ? '—' : src.outDist,
          flagged ? 'OUT OF RANGE' : 'IN RANGE',
          null
        ];
        const row = ws.getRow(r);
        values.forEach((v, i) => {
          const cell = row.getCell(i + 1);
          if (v !== null) cell.value = v;
          cell.font = F(10);
          cell.fill = fill(rowBg);
          cell.border = box('thin', C.lineSoft);
          cell.alignment = { horizontal: i === 4 ? 'left' : 'center', vertical: 'middle', indent: i === 4 ? 1 : 0 };
          if (i === 0) cell.font = F(9, { color: { argb: C.muted } });
          if (i === 2 || i === 3) cell.font = F(9, { color: { argb: C.text } });
          if (i === 5 || i === 6) {
            if (typeof v === 'number') cell.numFmt = '#,##0" ft"';
            const over = typeof v === 'number' && v > 1500;
            cell.font = F(10, { bold: true, color: { argb: over ? C.red : C.green } });
          }
          if (i === 7) {
            cell.font = F(9, { bold: true, color: { argb: flagged ? C.red : C.green } });
            cell.fill = fill(flagged ? 'FFFBD9D9' : C.greenBg);
          }
        });

        // Range details column
        const detail = row.getCell(LAST);
        if (flagged) {
          detail.value = notes.length === 1
            ? noteRich(notes[0])
            : { richText: [...noteRich(notes[0]).richText, { text: '\n' }, ...noteRich(notes[1]).richText] };
        } else {
          detail.value = 'Within 1,500 ft — no action needed';
          detail.font = F(9, { color: { argb: C.green } });
        }
        detail.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true, indent: 1 };

        // Left accent bar on flagged rows
        if (flagged) {
          row.getCell(1).border = { ...box('thin', C.lineSoft), left: bd('medium', C.red) };
        }
        row.height = flagged ? (notes.length > 1 ? 60 : 34) : 18;
        r += 1;
      });

      // Section closing rule
      for (let c = 1; c <= LAST; c++) {
        const cell = ws.getRow(r - 1).getCell(c);
        cell.border = { ...(cell.border || box('thin', C.lineSoft)), bottom: bd('medium', C.line) };
      }
    });

    // ---- Legend ------------------------------------------------------------
    r += 1;
    mergeAll(r);
    const legend = ws.getCell(`A${r}`);
    legend.value = {
      richText: [
        { text: 'Legend:  ', font: { name: 'Calibri', size: 9, bold: true, color: { argb: C.muted } } },
        { text: 'IN RANGE', font: { name: 'Calibri', size: 9, bold: true, color: { argb: C.green } } },
        { text: ' = clock punch within 1,500 ft of the patient address.    ', font: { name: 'Calibri', size: 9, color: { argb: C.muted } } },
        { text: 'OUT OF RANGE', font: { name: 'Calibri', size: 9, bold: true, color: { argb: C.red } } },
        { text: ' = punch recorded beyond 1,500 ft; distances are straight-line estimates.', font: { name: 'Calibri', size: 9, color: { argb: C.muted } } }
      ]
    };
    legend.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    paint(r, C.amberBg, box('thin', C.lineSoft));
    ws.getRow(r).height = 20;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `GPS_Tracer_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // CSV upload & grouping
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !file.name.endsWith(".csv")) {
      alert("Please select a valid CSV file");
      return;
    }

    Papa.parse(file, {
      header: false, // Parse without headers first
      skipEmptyLines: true,
      complete: (results) => {
        
        // Row 2 (index 1) contains the date range - C2 is from date, D2 is end date
        const row2 = results.data[1];
        const fromDateCSV = row2?.[2]?.trim() || ''; // Column C (index 2)
        const endDateCSV = row2?.[3]?.trim() || '';  // Column D (index 3)
        setCsvFromDate(fromDateCSV);
        setCsvEndDate(endDateCSV);
        setCsvAgencyName(row2?.[0]?.trim() || '');
        
        // Row 3 (index 2) contains the actual headers
        const headers = results.data[2];

        // Data starts from row 4 (index 3)
        const dataRows = results.data.slice(CSV_DATA_START_ROW);
        
        const grouped = {};
        const contractsSet = new Set(); // Track unique contracts
        const branchesSet = new Set(); // Track unique branches

        dataRows.forEach((row) => {
          // Map using the correct column indices from row 3
          const rowId = row[0]?.trim();                    // RowID
          const admissionId = row[1]?.trim();              // textbox16 (Admission ID)
          const patientName = row[2]?.trim();              // textbox18 (Patient Name)
          const branch = row[3]?.trim();                   // CoordinatorName/Branch (Column D)
          const contract = row[4]?.trim();                 // ContractName (Contract selection)
          const caregiver = row[5]?.trim();                // textbox19 (Caregiver)
          const visitDate = row[7]?.trim();                // textbox9 (Visit Date)
          const actualSchedule = row[8]?.trim();           // textbox24 (Actual Schedule)
          const visitSchedule = row[9]?.trim();            // textbox25 (Visit Schedule)
          const billed = row[10]?.trim() || "No";          // Billed

          if (!admissionId || !patientName) return;

          // Add contract to the set if it exists and is not empty
          if (contract && contract !== "") {
            contractsSet.add(contract);
          }

          // Add branch to the set if it exists and is not empty
          if (branch && branch !== "") {
            branchesSet.add(branch);
          }

          if (!grouped[admissionId]) {
            grouped[admissionId] = {
              patientId: admissionId,
              patientName: patientName,
              branch: branch || "N/A",  // Store branch for display and filtering
              contract: contract || "N/A",  // Store contract for filtering
              visits: [],
              totalHours: 0,
              scheduledHours: 0,
              billableHours: 0,
              billedHours: 0,
              billableScheduledHours: 0,
            };
          }

          const actualHours = calculateHours(actualSchedule);
          const scheduledHours = calculateHours(visitSchedule);
          const timeDiff = calculateTimeDifference(actualSchedule, visitSchedule);
          
          grouped[admissionId].visits.push({
            rowId,
            caregiver,
            date: visitDate,
            actualSchedule: actualSchedule,      // textbox24 (Actual Schedule)
            visitSchedule: visitSchedule,        // textbox25 (Visit Schedule)
            hours: actualHours,
            scheduledHours: scheduledHours,
            billed,
            remark: timeDiff.remark,
            missedMinutes: timeDiff.missedMinutes || 0,
          });
          
          // Use formatted (4-unit) values so sums match Bill/Tot column values
          // NOTE: CSV columns are reversed - actualSchedule field holds SCHEDULED time,
          //       visitSchedule field holds ACTUAL time.
          const formattedScheduledTrue = formatHoursAsUnits(actualHours);   // SCHEDULED
          const formattedActualTrue = formatHoursAsUnits(scheduledHours);   // ACTUAL
          
          // Total = scheduled hours; Billable = scheduled minus rounded late-in/early-out
          //   - Late-in or Early-out  -> deducted (rounded to 15-min units)
          //   - Late-out or Early-in  -> ignored (capped at scheduled)
          grouped[admissionId].totalHours += formattedScheduledTrue;
          grouped[admissionId].scheduledHours += formattedScheduledTrue;
          
          const isMissed = timeDiff.remark === "Missed In/Out";
          const isBilledYes = billed === "Yes";
          const isBillable = !isBilledYes && actualHours > 0 && !isMissed;
          
          // Per-visit billable = min(scheduled, actual) in units.
          // If actual rounds up to full scheduled units (e.g. 9-min early out on a 2h visit
          // still shows 2.00 / 2.00), no deduction is taken.
          const visitBillable = Math.min(formattedScheduledTrue, formattedActualTrue);
          const deduction = Math.max(0, formattedScheduledTrue - visitBillable);
          
          if (isBillable) {
            grouped[admissionId].billableHours += visitBillable;
            grouped[admissionId].billableScheduledHours += formattedScheduledTrue;
          }
          if (isBilledYes && actualHours > 0 && !isMissed) {
            grouped[admissionId].billedHours += visitBillable;
            // Deducted portion (late-in / early-out) stays in Billable for data keeping
            if (deduction > 0) {
              grouped[admissionId].billableHours += deduction;
            }
          }
        });

        const finalData = Object.values(grouped);
        
        setGroupedData(finalData);
        setSearchText("");
        const uniqueContracts = Array.from(contractsSet).sort();
        setAvailableContracts(uniqueContracts); // Set unique contracts
        setSelectedContracts([]); // Reset filter
        const uniqueBranches = Array.from(branchesSet).sort();
        setAvailableBranches(uniqueBranches); // Set unique branches
        setSelectedBranches([]); // Reset branch filter

        // Auto expand first 3 patients
        const autoExpand = {};
        finalData.slice(0, 3).forEach((p) => (autoExpand[p.patientId] = true));
        setExpandedPatients(autoExpand);
      },
    });
  };

  // Drag & Drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Only CSV files are supported');
      return;
    }
    setDroppedFileName(file.name);
    // Reuse the same parser logic
    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: (results) => {
        // Row 2 (index 1) contains the date range - C2 is from date, D2 is end date
        const row2 = results.data[1];
        const fromDateCSV = row2?.[2]?.trim() || ''; // Column C (index 2)
        const endDateCSV = row2?.[3]?.trim() || '';  // Column D (index 3)
        setCsvFromDate(fromDateCSV);
        setCsvEndDate(endDateCSV);
        setCsvAgencyName(row2?.[0]?.trim() || '');
        
        const dataRows = results.data.slice(CSV_DATA_START_ROW);
        const grouped = {};
        const contractsSet = new Set();
        const branchesSet = new Set();
        dataRows.forEach((row) => {
          const rowId = row[0]?.trim();
          const admissionId = row[1]?.trim();
          const patientName = row[2]?.trim();
          const branch = row[3]?.trim();
          const contract = row[4]?.trim();
          const caregiver = row[5]?.trim();
          const visitDate = row[7]?.trim();
          const actualSchedule = row[8]?.trim();
          const visitSchedule = row[9]?.trim();
          const billed = row[10]?.trim() || 'No';
          if (!admissionId || !patientName) return;
          
          if (contract && contract !== "") {
            contractsSet.add(contract);
          }
          
          if (branch && branch !== "") {
            branchesSet.add(branch);
          }
          
          if (!grouped[admissionId]) {
            grouped[admissionId] = { patientId: admissionId, patientName, branch: branch || "N/A", contract: contract || "N/A", visits: [], totalHours: 0, scheduledHours: 0, billableHours: 0, billedHours: 0, billableScheduledHours: 0 };
          }
          const actualHours = calculateHours(actualSchedule);
          const scheduledHours = calculateHours(visitSchedule);
          const timeDiff = calculateTimeDifference(actualSchedule, visitSchedule);
          grouped[admissionId].visits.push({ rowId, caregiver, date: visitDate, actualSchedule, visitSchedule, hours: actualHours, scheduledHours, billed, remark: timeDiff.remark, missedMinutes: timeDiff.missedMinutes || 0 });
          
          // CSV columns are reversed: actualSchedule field = SCHEDULED, visitSchedule = ACTUAL
          const formattedScheduledTrue = formatHoursAsUnits(actualHours);
          const formattedActualTrue = formatHoursAsUnits(scheduledHours);
          
          // Total = scheduled; Billable = scheduled minus rounded late-in/early-out
          grouped[admissionId].totalHours += formattedScheduledTrue;
          grouped[admissionId].scheduledHours += formattedScheduledTrue;
          
          const isMissed = timeDiff.remark === "Missed In/Out";
          const isBilledYes = billed === "Yes";
          const isBillable = !isBilledYes && actualHours > 0 && !isMissed;
          
          // Per-visit billable = min(scheduled, actual) in units.
          const visitBillable = Math.min(formattedScheduledTrue, formattedActualTrue);
          const deduction = Math.max(0, formattedScheduledTrue - visitBillable);
          
          if (isBillable) {
            grouped[admissionId].billableHours += visitBillable;
            grouped[admissionId].billableScheduledHours += formattedScheduledTrue;
          }
          if (isBilledYes && actualHours > 0 && !isMissed) {
            grouped[admissionId].billedHours += visitBillable;
            if (deduction > 0) {
              grouped[admissionId].billableHours += deduction;
            }
          }
        });
        const finalData = Object.values(grouped);
        setGroupedData(finalData);
        const uniqueContracts = Array.from(contractsSet).sort();
        setAvailableContracts(uniqueContracts);
        setSelectedContracts([]);
        const uniqueBranches = Array.from(branchesSet).sort();
        setAvailableBranches(uniqueBranches);
        setSelectedBranches([]);
        const autoExpand = {};
        finalData.slice(0, 3).forEach((p) => (autoExpand[p.patientId] = true));
        setExpandedPatients(autoExpand);
      }
    });
  };

  const togglePatient = useCallback((patientId) => {
    setExpandedPatients((prev) => ({
      ...prev,
      [patientId]: !prev[patientId],
    }));
  }, []);

  // Helper function to check if a visit has overlaps
  const checkVisitHasOverlap = (patient, visitIndex) => {
    const overlaps = detectOverlaps(patient.visits);
    return overlaps.some(o => o.visit1Index === visitIndex || o.visit2Index === visitIndex);
  };

  // Flags (caregiver + date) combos whose total scheduled hours exceed 24 in one day (data-entry error).
  // NOTE: visit.hours = real SCHEDULED, visit.scheduledHours = real ACTUAL (CSV columns are reversed; see parser line ~966).
  const caregiverDayOverflow = useMemo(() => {
    const totals = {};
    groupedData.forEach(patient => {
      patient.visits.forEach(visit => {
        const name = (visit.caregiver || 'Unknown').trim();
        const date = (visit.date || '').trim();
        if (!name || !date) return;
        const key = `${name}||${date}`;
        if (!totals[key]) totals[key] = { scheduled: 0, actual: 0 };
        totals[key].scheduled += visit.hours || 0;
        totals[key].actual += visit.scheduledHours || 0;
      });
    });
    const flagged = {};
    Object.entries(totals).forEach(([key, v]) => {
      if (v.scheduled > 24) {
        flagged[key] = { scheduled: v.scheduled, actual: v.actual };
      }
    });
    return flagged;
  }, [groupedData]);

  // Flags (patient + date) combos whose total scheduled hours across all caregivers exceed 24 in one day.
  const patientDayOverflow = useMemo(() => {
    const totals = {};
    groupedData.forEach(patient => {
      patient.visits.forEach(visit => {
        const date = (visit.date || '').trim();
        if (!date || !patient.patientId) return;
        const key = `${patient.patientId}||${date}`;
        if (!totals[key]) totals[key] = { scheduled: 0, actual: 0 };
        totals[key].scheduled += visit.hours || 0;
        totals[key].actual += visit.scheduledHours || 0;
      });
    });
    const flagged = {};
    Object.entries(totals).forEach(([key, v]) => {
      if (v.scheduled > 24) {
        flagged[key] = { scheduled: v.scheduled, actual: v.actual };
      }
    });
    return flagged;
  }, [groupedData]);

  // Filter by search and contracts - memoized for performance
  const filteredData = useMemo(() => {
    let filtered = groupedData;

    // Apply contract filter first
    if (selectedContracts.length > 0) {
      filtered = filtered.filter((patient) =>
        selectedContracts.includes(patient.contract)
      );
    }

    // Apply branch filter
    if (selectedBranches.length > 0) {
      filtered = filtered.filter((patient) =>
        selectedBranches.includes(patient.branch)
      );
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter((patient) => {
        if (statusFilter === 'Overlap') {
          const overlaps = detectOverlaps(patient.visits);
          return overlaps.length > 0;
        } else if (statusFilter === 'Missed In') {
          return patient.visits.some((visit) => 
            visit.remark && (visit.remark.includes('Late In') || visit.remark === 'Missed In/Out')
          );
        } else if (statusFilter === 'Missed Out') {
          return patient.visits.some((visit) => 
            visit.remark && (visit.remark.includes('Early Out') || visit.remark === 'Missed In/Out')
          );
        } else if (statusFilter === '24 Hrs Cross') {
          return patient.visits.some((visit) => {
            const date = (visit.date || '').trim();
            if (!date) return false;
            const name = (visit.caregiver || 'Unknown').trim();
            return !!patientDayOverflow[`${patient.patientId}||${date}`]
                || !!caregiverDayOverflow[`${name}||${date}`];
          });
        }
        return true;
      });
    }

    // Apply billed filter (matches if any visit has the selected billed value)
    if (billedFilter) {
      const target = billedFilter.toLowerCase();
      filtered = filtered.filter((patient) =>
        patient.visits.some((visit) => (visit.billed || 'No').toLowerCase() === target)
      );
    }

    // Then apply search filter with debounced value
    if (!debouncedSearch.trim()) return filtered;

    const searchTerms = debouncedSearch.split("|").map((t) => t.trim().toLowerCase());

    return filtered.filter((patient) => {
      return searchTerms.some((term) => {
        if (!term) return true;

        const andConditions = term.split("&").map((c) => c.trim());

        return andConditions.every((condition) => {
          // Handle abbreviations: MI = Missed In, MO = Missed Out
          let searchCondition = condition;
          let expandedTerms = [condition];
          
          if (condition === 'mi') {
            expandedTerms = ['late in', 'missed in'];
          } else if (condition === 'mo') {
            expandedTerms = ['early out', 'missed out'];
          } else if (condition === 'overlap') {
            // Check for overlaps
            const overlaps = detectOverlaps(patient.visits);
            if (overlaps.length > 0) return true;
          }

          const patientMatch =
            patient.patientId.toLowerCase().includes(searchCondition) ||
            patient.patientName.toLowerCase().includes(searchCondition) ||
            String(patient.totalHours).toLowerCase().includes(searchCondition);

          const visitsMatch = patient.visits.some((visit) => {
            // Check expanded terms for remark field
            const remarkMatch = expandedTerms.some(term => 
              (visit.remark || '').toLowerCase().includes(term)
            );
            if (remarkMatch) return true;
            
            return Object.values(visit).some((val) =>
              String(val).toLowerCase().includes(searchCondition)
            );
          });

          return patientMatch || visitsMatch;
        });
      });
    });
  }, [groupedData, debouncedSearch, selectedContracts, selectedBranches, statusFilter, billedFilter, patientDayOverflow, caregiverDayOverflow]);

  // Calculate total hours across all patients
  const totalAllHours = useMemo(() => {
    return filteredData.reduce((sum, patient) => sum + patient.totalHours, 0);
  }, [filteredData]);

  const totalAllScheduledHours = useMemo(() => {
    return filteredData.reduce((sum, patient) => sum + (patient.scheduledHours || 0), 0);
  }, [filteredData]);

  const totalAllBillableScheduledHours = useMemo(() => {
    return filteredData.reduce((sum, patient) => sum + (patient.billableScheduledHours || 0), 0);
  }, [filteredData]);

  const totalAllBilledHours = useMemo(() => {
    return filteredData.reduce((sum, patient) => sum + (patient.billedHours || 0), 0);
  }, [filteredData]);

  const totalAllBillableHours = useMemo(() => {
    return filteredData.reduce((sum, patient) => sum + (patient.billableHours || 0), 0);
  }, [filteredData]);

  const caregiverTotals = useMemo(() => {
    const caregiverHours = {};
    filteredData.forEach(patient => {
      patient.visits.forEach(visit => {
        const name = (visit.caregiver || 'Unknown').trim();
        if (!caregiverHours[name]) caregiverHours[name] = 0;
        caregiverHours[name] += visit.hours || 0;
      });
    });
    const count = Object.keys(caregiverHours).length;
    const totalHours = Object.values(caregiverHours).reduce((sum, h) => sum + h, 0);
    return { count, totalHours };
  }, [filteredData]);

  const isVisitOverflow = useCallback((visit, patientId) => {
    if (!visit) return null;
    const date = (visit.date || '').trim();
    if (!date) return null;
    const name = (visit.caregiver || 'Unknown').trim();
    const cgHit = name ? caregiverDayOverflow[`${name}||${date}`] : null;
    const pHit = patientId ? patientDayOverflow[`${patientId}||${date}`] : null;
    if (!cgHit && !pHit) return null;
    // Prefer the larger (more severe) breach, and note the source.
    const hit = (pHit && (!cgHit || Math.max(pHit.scheduled, pHit.actual) >= Math.max(cgHit.scheduled, cgHit.actual)))
      ? { ...pHit, source: 'patient' }
      : { ...cgHit, source: 'caregiver' };
    return hit;
  }, [caregiverDayOverflow, patientDayOverflow]);

  // ---- Patient selection + Print PDF ----
  const togglePatientSelection = (patientId) => {
    setSelectedPatientIds(prev =>
      prev.includes(patientId) ? prev.filter(id => id !== patientId) : [...prev, patientId]
    );
  };

  const allFilteredSelected = filteredData.length > 0 && selectedPatientIds.length === filteredData.length;

  const toggleSelectAllPatients = () => {
    if (allFilteredSelected) {
      setSelectedPatientIds([]);
    } else {
      setSelectedPatientIds(filteredData.map(p => p.patientId));
    }
  };

  const printSelectedPatients = () => {
    if (filteredData.length === 0) {
      alert("No data to print. Load data first.");
      return;
    }
    const ids = selectedPatientIds.length > 0
      ? selectedPatientIds
      : filteredData.map(p => p.patientId);
    if (ids.length === 0) {
      alert("Select at least one patient.");
      return;
    }

    // Make sure all targeted patients are rendered + expanded so their visit
    // tables are present in the DOM when the print stylesheet is applied.
    const maxIdx = ids.reduce((m, id) => {
      const i = filteredData.findIndex(p => p.patientId === id);
      return i > m ? i : m;
    }, -1);
    if (maxIdx >= displayLimit) {
      setDisplayLimit(maxIdx + 1);
    }
    setExpandedPatients(prev => {
      const next = { ...prev };
      ids.forEach(id => { next[id] = true; });
      return next;
    });

    // Inject (or refresh) a print stylesheet that hides everything except the
    // selected patient cards. This keeps the EXACT on-screen colors & layout.
    const STYLE_ID = "__patient_print_style__";
    const old = document.getElementById(STYLE_ID);
    if (old) old.remove();

    const escId = (id) => (window.CSS && CSS.escape) ? CSS.escape(id) : id;
    const selectorList = ids
      .map(id => `.patient-card-print[data-patient-id="${escId(id)}"]`)
      .join(",\n");
    // Last visible card in DOM order: remove its page-break-after so we don't get a trailing blank page.
    const lastIdInDom = filteredData
      .map(p => p.patientId)
      .filter(id => ids.includes(id))
      .pop();
    const lastSelector = lastIdInDom
      ? `.patient-card-print[data-patient-id="${escId(lastIdInDom)}"]`
      : "";

    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      @media print {
        @page { size: letter landscape; margin: 0.3in 0.25in; }
        html, body {
          background: #ffffff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
          font-family: 'Segoe UI', Arial, sans-serif !important;
          font-size: 11px !important;
          line-height: 1.4 !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        body * { visibility: hidden !important; }
        .patient-print-area, .patient-print-area * { visibility: visible !important; }
        .print-report-header, .print-report-header * { visibility: visible !important; }
        .patient-print-area {
          position: absolute !important;
          left: 0; top: 0; right: 0;
          width: 100% !important;
          padding: 0 !important;
          margin: 0 !important;
          background: #ffffff !important;
        }
        /* Report Header */
        .print-report-header {
          display: block !important;
          visibility: visible !important;
          width: 100% !important;
          padding: 14px 20px !important;
          margin-bottom: 10px !important;
          border: 2px solid #1e3a5f !important;
          border-radius: 4px !important;
          background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%) !important;
          page-break-after: avoid !important;
        }
        .print-report-header h1 {
          font-size: 20px !important;
          font-weight: 800 !important;
          color: #ffffff !important;
          margin: 0 0 6px 0 !important;
        }
        .print-report-header p {
          font-size: 11px !important;
          color: #e2e8f0 !important;
          margin: 2px 0 !important;
          font-weight: 500 !important;
        }
        /* Patient cards */
        .patient-card-print { display: none !important; }
        ${selectorList} {
          display: block !important;
          page-break-inside: avoid;
          break-inside: avoid;
          break-after: page;
          page-break-after: always;
          margin: 0 0 12px 0 !important;
          padding: 0 !important;
          box-shadow: none !important;
          border: 2px solid #334155 !important;
          border-radius: 4px !important;
          overflow: visible !important;
          font-size: 11px !important;
        }
        ${lastSelector ? `${lastSelector} { break-after: auto !important; page-break-after: auto !important; }` : ""}
        /* Remove shadows & transforms */
        ${selectorList} * {
          box-shadow: none !important;
          text-shadow: none !important;
          transform: none !important;
          transition: none !important;
        }
        /* Patient header bar - bold, visible */
        ${selectorList} > .MuiBox-root:first-child {
          padding: 10px 16px !important;
          background: #1e293b !important;
        }
        ${selectorList} > .MuiBox-root:first-child * {
          font-size: 11px !important;
          color: #ffffff !important;
        }
        ${selectorList} > .MuiBox-root:first-child .MuiTypography-caption {
          font-size: 9px !important;
          color: #cbd5e1 !important;
        }
        /* Content area - full width, no extra padding */
        ${selectorList} .MuiBox-root {
          padding-top: 4px !important;
          padding-bottom: 4px !important;
        }
        ${selectorList} .MuiPaper-root {
          padding: 4px !important;
          margin: 0 !important;
          box-shadow: none !important;
          border-radius: 0 !important;
        }
        /* ===== EXCEL-STYLE TABLE ===== */
        ${selectorList} table {
          font-size: 11px !important;
          width: 100% !important;
          border-collapse: collapse !important;
          border: 2px solid #1e293b !important;
          margin: 4px 0 !important;
        }
        ${selectorList} table th {
          padding: 8px 10px !important;
          line-height: 1.3 !important;
          font-size: 10px !important;
          font-weight: 800 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.4px !important;
          white-space: nowrap !important;
          background: #1e293b !important;
          color: #ffffff !important;
          border: 1px solid #334155 !important;
          border-bottom: 3px solid #0f172a !important;
        }
        ${selectorList} table td {
          padding: 7px 10px !important;
          line-height: 1.4 !important;
          font-size: 11px !important;
          border: 1px solid #cbd5e1 !important;
          color: #1e293b !important;
          font-weight: 500 !important;
          vertical-align: middle !important;
        }
        ${selectorList} table tbody tr:nth-child(even) td {
          background: #f1f5f9 !important;
        }
        ${selectorList} table tbody tr:nth-child(odd) td {
          background: #ffffff !important;
        }
        /* Status badges - readable */
        ${selectorList} span[style*="border-radius"] {
          font-size: 9px !important;
          padding: 2px 6px !important;
          line-height: 1.4 !important;
          font-weight: 700 !important;
        }
        /* Card view caregiver section */
        ${selectorList} .MuiCollapse-root {
          padding: 8px !important;
        }
        ${selectorList} .MuiCollapse-root .MuiBox-root {
          padding: 6px 10px !important;
        }
        /* Scrollable containers - show all */
        ${selectorList} [style*="overflow"] {
          overflow: visible !important;
        }
        ${selectorList} table[style*="min-width"] {
          min-width: unset !important;
        }
        /* Summary stats boxes */
        ${selectorList} [style*="border-radius"][style*="background"] {
          border: 1px solid #94a3b8 !important;
        }
        /* Hide interactive-only elements */
        .no-print { display: none !important; }
      }
    `;
    document.head.appendChild(style);

    // Inject a professional report header for the printed PDF
    const HEADER_ID = "__print_report_header__";
    const oldHeader = document.getElementById(HEADER_ID);
    if (oldHeader) oldHeader.remove();
    const printArea = document.querySelector('.patient-print-area');
    if (printArea) {
      const header = document.createElement('div');
      header.id = HEADER_ID;
      header.className = 'print-report-header no-screen';
      const agency = csvAgencyName || 'Patient Visit Report';
      const dateRange = (csvFromDate && csvEndDate) ? `${csvFromDate} — ${csvEndDate}` : '';
      const patientCount = ids.length;
      const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      header.innerHTML = `
        <h1>${agency.replace(/</g, '&lt;')}</h1>
        <p><strong>Patient Visit Report</strong>${dateRange ? ` &nbsp;|&nbsp; Period: ${dateRange}` : ''} &nbsp;|&nbsp; Patients: ${patientCount} &nbsp;|&nbsp; Generated: ${today}</p>
      `;
      header.style.cssText = 'display:none;';
      printArea.insertBefore(header, printArea.firstChild);
      const screenStyle = document.createElement('style');
      screenStyle.id = HEADER_ID + '_screen';
      screenStyle.textContent = `@media screen { .print-report-header { display: none !important; } }`;
      const oldScreenStyle = document.getElementById(HEADER_ID + '_screen');
      if (oldScreenStyle) oldScreenStyle.remove();
      document.head.appendChild(screenStyle);
    }

    // Defer to next frame so the style is applied before the print dialog opens
    requestAnimationFrame(() => {
      setTimeout(() => window.print(), 250);
    });
  };

  // Export Caregiver Hours Summary to Excel
  const exportCaregiverHours = async () => {
    if (filteredData.length === 0) {
      alert("No data to export");
      return;
    }

    const caregiverData = {};
    
    filteredData.forEach((patient) => {
      patient.visits.forEach((visit) => {
        const name = (visit.caregiver || 'Unknown').trim();
        if (!caregiverData[name]) {
          caregiverData[name] = {
            totalHours: 0,
            dates: []
          };
        }
        caregiverData[name].totalHours += formatHoursAsUnits(visit.hours || 0);
        if (visit.date) {
          caregiverData[name].dates.push(visit.date);
        }
      });
    });

    // Calculate date ranges for each caregiver
    const caregiverSummary = Object.entries(caregiverData).map(([name, data]) => {
      // Sort dates and get first/last
      const sortedDates = data.dates.sort((a, b) => {
        const parseDate = (d) => {
          if (!d) return new Date(0);
          const parts = d.split('/');
          if (parts.length === 3) {
            return new Date(parts[2], parts[0] - 1, parts[1]);
          }
          return new Date(d);
        };
        return parseDate(a) - parseDate(b);
      });
      
      const firstDate = sortedDates[0] || 'N/A';
      const lastDate = sortedDates[sortedDates.length - 1] || 'N/A';
      
      return {
        name,
        totalHours: data.totalHours,
        firstDate,
        lastDate,
        visitCount: data.dates.length
      };
    });

    // Sort by name
    caregiverSummary.sort((a, b) => a.name.localeCompare(b.name));

    // Create workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Caregiver Hours');

    // Set column widths
    worksheet.columns = [
      { key: 'A', width: 35 },  // Caregiver Name
      { key: 'B', width: 18 },  // Total Hours
      { key: 'C', width: 15 },  // First Date
      { key: 'D', width: 15 },  // Last Date
      { key: 'E', width: 15 }   // Visit Count
    ];

    // Row 1: Title
    worksheet.mergeCells('A1:E1');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = 'Caregiver Hours Summary';
    titleCell.font = { name: 'Calibri', size: 20, bold: true, color: { argb: 'FF047857' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } };
    worksheet.getRow(1).height = 35;

    // Row 2: Date range info
    const allDates = filteredData.flatMap(p => p.visits.map(v => v.date)).filter(Boolean);
    const sortedAllDates = allDates.sort((a, b) => {
      const parseDate = (d) => {
        if (!d) return new Date(0);
        const parts = d.split('/');
        if (parts.length === 3) {
          return new Date(parts[2], parts[0] - 1, parts[1]);
        }
        return new Date(d);
      };
      return parseDate(a) - parseDate(b);
    });
    const overallFirstDate = sortedAllDates[0] || csvFromDate || 'N/A';
    const overallLastDate = sortedAllDates[sortedAllDates.length - 1] || csvEndDate || 'N/A';

    worksheet.mergeCells('A2:E2');
    const dateRangeCell = worksheet.getCell('A2');
    dateRangeCell.value = `Date Range: ${overallFirstDate} - ${overallLastDate}`;
    dateRangeCell.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FF1E40AF' } };
    dateRangeCell.alignment = { horizontal: 'center', vertical: 'middle' };
    dateRangeCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBEAFE' } };
    worksheet.getRow(2).height = 25;

    // Row 3: Headers
    const headers = ['Caregiver Name', 'Total Hours', 'First Visit', 'Last Visit', 'Visit Count'];
    const headerRow = worksheet.getRow(3);
    headers.forEach((header, idx) => {
      const cell = headerRow.getCell(idx + 1);
      cell.value = header;
      cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF059669' } };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });
    worksheet.getRow(3).height = 25;

    // Data rows
    let rowNum = 4;
    let grandTotalHours = 0;

    caregiverSummary.forEach((caregiver, idx) => {
      const row = worksheet.getRow(rowNum);
      const isEven = idx % 2 === 0;
      const bgColor = isEven ? 'FFF0FDF4' : 'FFFFFFFF';

      // Caregiver Name
      const nameCell = row.getCell(1);
      nameCell.value = caregiver.name;
      nameCell.font = { name: 'Calibri', size: 11, bold: true };
      nameCell.alignment = { horizontal: 'left', vertical: 'middle' };
      nameCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
      nameCell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

      // Total Hours
      const hoursCell = row.getCell(2);
      hoursCell.value = parseFloat(caregiver.totalHours.toFixed(2));
      hoursCell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FF047857' } };
      hoursCell.alignment = { horizontal: 'center', vertical: 'middle' };
      hoursCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
      hoursCell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

      // First Date
      const firstDateCell = row.getCell(3);
      firstDateCell.value = caregiver.firstDate;
      firstDateCell.font = { name: 'Calibri', size: 10 };
      firstDateCell.alignment = { horizontal: 'center', vertical: 'middle' };
      firstDateCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
      firstDateCell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

      // Last Date
      const lastDateCell = row.getCell(4);
      lastDateCell.value = caregiver.lastDate;
      lastDateCell.font = { name: 'Calibri', size: 10 };
      lastDateCell.alignment = { horizontal: 'center', vertical: 'middle' };
      lastDateCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
      lastDateCell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

      // Visit Count
      const countCell = row.getCell(5);
      countCell.value = caregiver.visitCount;
      countCell.font = { name: 'Calibri', size: 10 };
      countCell.alignment = { horizontal: 'center', vertical: 'middle' };
      countCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
      countCell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };

      grandTotalHours += caregiver.totalHours;
      rowNum++;
    });

    // Total row
    const totalRow = worksheet.getRow(rowNum);
    
    const totalLabelCell = totalRow.getCell(1);
    totalLabelCell.value = 'GRAND TOTAL';
    totalLabelCell.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
    totalLabelCell.alignment = { horizontal: 'right', vertical: 'middle' };
    totalLabelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E40AF' } };
    totalLabelCell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'thin' } };

    const totalHoursCell = totalRow.getCell(2);
    totalHoursCell.value = parseFloat(grandTotalHours.toFixed(2));
    totalHoursCell.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
    totalHoursCell.alignment = { horizontal: 'center', vertical: 'middle' };
    totalHoursCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E40AF' } };
    totalHoursCell.border = { top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'medium' }, right: { style: 'thin' } };

    // Empty cells for total row
    for (let i = 3; i <= 5; i++) {
      const emptyCell = totalRow.getCell(i);
      emptyCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E40AF' } };
      emptyCell.border = { top: { style: 'medium' }, left: { style: 'thin' }, bottom: { style: 'medium' }, right: i === 5 ? { style: 'medium' } : { style: 'thin' } };
    }

    worksheet.getRow(rowNum).height = 28;

    // Force a thin black border on every used cell
    const _lastRow = rowNum;
    const _lastCol = 5;
    const _thin = { style: 'thin', color: { argb: 'FF000000' } };
    for (let rr = 1; rr <= _lastRow; rr++) {
      for (let cc = 1; cc <= _lastCol; cc++) {
        const cell = worksheet.getCell(rr, cc);
        cell.border = { top: _thin, left: _thin, bottom: _thin, right: _thin };
      }
    }

    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const fileName = `Caregiver_Hours_${overallFirstDate.replace(/\//g, '-')}_to_${overallLastDate.replace(/\//g, '-')}.xlsx`;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ---- Payroll Export (Reg / OT, weekly bucket, 40h cap) ----
  const exportPayroll = async () => {
    if (filteredData.length === 0) {
      alert("No data to export");
      return;
    }

    // Helper: parse "MM/DD/YYYY" to Date (local)
    const parseDate = (d) => {
      if (!d) return null;
      const parts = String(d).split('/');
      if (parts.length === 3) {
        const m = parseInt(parts[0], 10) - 1;
        const day = parseInt(parts[1], 10);
        const y = parseInt(parts[2], 10);
        const dt = new Date(y, m, day);
        return isNaN(dt) ? null : dt;
      }
      const dt = new Date(d);
      return isNaN(dt) ? null : dt;
    };
    const fmtDate = (dt) => `${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getDate()).padStart(2,'0')}/${dt.getFullYear()}`;

    // Determine the overall date range from the data
    const allDates = filteredData.flatMap(p => p.visits.map(v => parseDate(v.date))).filter(Boolean);
    if (allDates.length === 0) {
      alert("No visit dates found in data.");
      return;
    }
    const overallStart = new Date(Math.min(...allDates.map(d => d.getTime())));
    const overallEnd = new Date(Math.max(...allDates.map(d => d.getTime())));

    // Build week buckets starting from the FIRST imported date.
    // Week 1 = days 0-6 from overallStart, Week 2 = days 7-13, etc.
    // Per caregiver per week: reg = min(weekHours, 40), ot = max(0, weekHours - 40)
    const anchor = new Date(overallStart.getFullYear(), overallStart.getMonth(), overallStart.getDate());
    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    const weekIndex = (dt) => {
      const d = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
      return Math.floor((d - anchor) / MS_PER_DAY / 7);
    };
    const weekKey = (dt) => String(weekIndex(dt));

    // caregiverData[name] = { weeks: { weekKey: hours }, dates: [Date], totalHours, regHours, otHours }
    const caregiverData = {};
    filteredData.forEach((patient) => {
      patient.visits.forEach((visit) => {
        const name = (visit.caregiver || 'Unknown').trim();
        const dt = parseDate(visit.date);
        // PAYROLL = BILLED hours only.
        // Per visit: if billed === 'Yes' and not missed -> min(scheduled, actual) in units.
        // Otherwise 0 (unbilled visits don't pay).
        const isMissed = visit.remark === 'Missed In/Out';
        const isBilledYes = visit.billed === 'Yes';
        const sched = formatHoursAsUnits(visit.scheduledHours || 0);
        const act = formatHoursAsUnits(visit.hours || 0);
        const hrs = (isBilledYes && !isMissed && act > 0) ? Math.min(sched, act) : 0;
        if (!caregiverData[name]) {
          caregiverData[name] = { weeks: {}, dates: [], totalHours: 0, regHours: 0, otHours: 0 };
        }
        if (dt) {
          const k = weekKey(dt);
          caregiverData[name].weeks[k] = (caregiverData[name].weeks[k] || 0) + hrs;
          caregiverData[name].dates.push(dt);
        }
        caregiverData[name].totalHours += hrs;
      });
    });

    // Compute Reg / OT per week, then sum
    Object.values(caregiverData).forEach(c => {
      let reg = 0, ot = 0;
      Object.values(c.weeks).forEach(w => {
        reg += Math.min(40, w);
        ot += Math.max(0, w - 40);
      });
      c.regHours = reg;
      c.otHours = ot;
    });

    // Build summary rows, sorted by name
    const summary = Object.entries(caregiverData).map(([name, c]) => {
      const sortedDates = c.dates.slice().sort((a, b) => a - b);
      return {
        name,
        regHours: c.regHours,
        otHours: c.otHours,
        totalHours: c.totalHours,
        firstVisit: sortedDates.length ? fmtDate(sortedDates[0]) : 'N/A',
        lastVisit: sortedDates.length ? fmtDate(sortedDates[sortedDates.length - 1]) : 'N/A',
        visitCount: c.dates.length,
      };
    }).sort((a, b) => a.name.localeCompare(b.name));

    // Build the workbook
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet('Payroll');

    ws.columns = [
      { key: 'name',   width: 32 },
      { key: 'reg',    width: 12 },
      { key: 'ot',     width: 12 },
      { key: 'total',  width: 14 },
      { key: 'first',  width: 14 },
      { key: 'last',   width: 14 },
      { key: 'visits', width: 12 },
    ];

    // Title
    ws.mergeCells('A1:G1');
    const t = ws.getCell('A1');
    t.value = 'Payroll Summary (Billed Hours – Reg / OT)';
    t.font = { name: 'Calibri', size: 18, bold: true, color: { argb: 'FFFFFFFF' } };
    t.alignment = { horizontal: 'center', vertical: 'middle' };
    t.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E293B' } };
    ws.getRow(1).height = 32;

    // Date range
    ws.mergeCells('A2:G2');
    const r = ws.getCell('A2');
    const dayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    const startDayName = dayNames[overallStart.getDay()];
    const week1End = new Date(overallStart); week1End.setDate(week1End.getDate() + 6);
    const endDayName = dayNames[week1End.getDay()];
    r.value = `Pay Period: ${fmtDate(overallStart)} - ${fmtDate(overallEnd)}   |   Week starts ${startDayName} → ends ${endDayName} (7-day cycle)   |   Rule: 40 hrs/week regular, > 40 hrs = OT`;
    r.font = { name: 'Calibri', size: 11, italic: true, color: { argb: 'FF334155' } };
    r.alignment = { horizontal: 'center', vertical: 'middle' };
    r.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
    ws.getRow(2).height = 22;

    // Headers
    const headers = ['Caregiver Name', 'Reg Hrs', 'OT Hrs', 'Total Hours', 'First Visit', 'Last Visit', 'Visit Count'];
    const hr = ws.getRow(3);
    headers.forEach((h, i) => {
      const c = hr.getCell(i + 1);
      c.value = h;
      c.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
      c.alignment = { horizontal: 'center', vertical: 'middle' };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F46E5' } };
      c.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });
    hr.height = 26;

    // Data rows
    let row = 4;
    let totalReg = 0, totalOt = 0, totalAll = 0, totalVisits = 0;

    summary.forEach((s, idx) => {
      const bg = idx % 2 === 0 ? 'FFF8FAFC' : 'FFFFFFFF';
      const r2 = ws.getRow(row);

      const c1 = r2.getCell(1);
      c1.value = s.name;
      c1.font = { name: 'Calibri', size: 11, bold: true };
      c1.alignment = { horizontal: 'left', vertical: 'middle' };
      c1.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };

      const setNum = (col, val, color) => {
        const cell = r2.getCell(col);
        cell.value = parseFloat(val.toFixed(2));
        cell.numFmt = '0.00';
        cell.font = { name: 'Calibri', size: 11, bold: true, color: color ? { argb: color } : undefined };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
      };
      setNum(2, s.regHours, 'FF15803D');                 // Reg = green
      setNum(3, s.otHours, s.otHours > 0 ? 'FFB45309' : undefined); // OT = amber if > 0
      setNum(4, s.totalHours, 'FF1E293B');               // Total = slate

      const setText = (col, val) => {
        const cell = r2.getCell(col);
        cell.value = val;
        cell.font = { name: 'Calibri', size: 10 };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
      };
      setText(5, s.firstVisit);
      setText(6, s.lastVisit);
      setText(7, s.visitCount);

      // Borders all cells
      for (let i = 1; i <= 7; i++) {
        r2.getCell(i).border = { top: { style: 'thin', color: { argb: 'FFE2E8F0' } }, left: { style: 'thin', color: { argb: 'FFE2E8F0' } }, bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } }, right: { style: 'thin', color: { argb: 'FFE2E8F0' } } };
      }

      totalReg += s.regHours;
      totalOt += s.otHours;
      totalAll += s.totalHours;
      totalVisits += s.visitCount;
      row++;
    });

    // Grand total row
    const tr = ws.getRow(row);
    const total = (col, val, isNum) => {
      const c = tr.getCell(col);
      c.value = isNum ? parseFloat(val.toFixed(2)) : val;
      if (isNum) c.numFmt = '0.00';
      c.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
      c.alignment = { horizontal: col === 1 ? 'right' : 'center', vertical: 'middle' };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E293B' } };
      c.border = { top: { style: 'medium' }, bottom: { style: 'medium' }, left: { style: 'thin' }, right: { style: 'thin' } };
    };
    total(1, 'GRAND TOTAL', false);
    total(2, totalReg, true);
    total(3, totalOt, true);
    total(4, totalAll, true);
    total(5, '', false);
    total(6, '', false);
    total(7, totalVisits, false);
    tr.height = 28;

    ws.views = [{ state: 'frozen', ySplit: 3 }];

    // Force a thin black border on every used cell
    const lastRow = row;
    const lastCol = 7;
    const thin = { style: 'thin', color: { argb: 'FF000000' } };
    for (let rr = 1; rr <= lastRow; rr++) {
      for (let cc = 1; cc <= lastCol; cc++) {
        const cell = ws.getCell(rr, cc);
        cell.border = { top: thin, left: thin, bottom: thin, right: thin };
      }
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Payroll_${fmtDate(overallStart).replace(/\//g,'-')}_to_${fmtDate(overallEnd).replace(/\//g,'-')}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Export to Excel using ExcelJS
  const exportToCSV = async () => {
    if (filteredData.length === 0) {
      alert("No data to export");
      return;
    }

    // Group patients by contract
    const contractGroups = {};
    filteredData.forEach((patient) => {
      const contract = patient.contract || "N/A";
      if (!contractGroups[contract]) {
        contractGroups[contract] = [];
      }
      contractGroups[contract].push(patient);
    });

    // Create workbook
    const workbook = new ExcelJS.Workbook();

    // Variables to store first contract info for filename
    let firstContractName = '';
    let firstEndDate = '';

    // Create a sheet for each contract
    for (const [contractName, patients] of Object.entries(contractGroups)) {
      // Calculate totals
      const totalActualHours = patients.reduce((sum, p) => {
        const allVisitHours = p.visits.reduce((vSum, visit) => vSum + visit.hours, 0);
        return sum + allVisitHours;
      }, 0);
      const totalBillableHours = patients.reduce((sum, p) => {
        const billable = p.visits.reduce((vSum, visit) => {
          return vSum + (visit.billed !== 'Yes' ? visit.hours : 0);
        }, 0);
        return sum + billable;
      }, 0);

      // Use dates from CSV row 2 (C2 = from date, D2 = end date)
      // Format: Billing from: fromDate - endDate
      const formattedStartDate = csvFromDate || 'N/A';
      const formattedEndDate = csvEndDate || 'N/A';
      const formatDateForBilled = d => d ? `${(d.getMonth()+1).toString().padStart(2,'0')}/${d.getDate().toString().padStart(2,'0')}/${d.getFullYear()}` : 'N/A';
      const billedOnDate = billedDate || formatDateForBilled(new Date());

      // Store first contract info for filename
      if (!firstContractName) {
        // Extract contract name based on specific patterns
        let contractNameForFile = contractName;
        if (contractName.includes('PA Health')) {
          contractNameForFile = 'PA Health';
        } else if (contractName.includes('UPMC')) {
          contractNameForFile = 'UPMC';
        } else if (contractName.includes('AmeriHealth')) {
          contractNameForFile = 'AmeriHealth';
        }
        
        firstContractName = contractNameForFile;
        firstEndDate = formattedEndDate !== 'N/A' ? formattedEndDate.replace(/\//g, '_') : new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).replace(/\//g, '_');
      }

      // Create worksheet
      const worksheet = workbook.addWorksheet(contractName.substring(0, 31));

      // Set column widths
      worksheet.columns = [
        { key: 'A', width: 15 },  // Admission ID
        { key: 'B', width: 30 },  // Patient Name
        { key: 'C', width: 20 },  // Actual Hours
        { key: 'D', width: 20 },  // Billable Hrs
        { key: 'E', width: 25 },  // Differences
        { key: 'F', width: 30 },  // Note
        { key: 'G', width: 25 }   // Branch
      ];

      // Row 1: Contract Name
      worksheet.mergeCells('A1:G1');
      const headerRow = worksheet.getCell('A1');
      headerRow.value = contractName;
      headerRow.font = { name: 'Calibri', size: 24, bold: true, color: { argb: 'FF7030A0' } };
      headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
      worksheet.getRow(1).height = 35;

      // Row 2: Headers row - Billing and Totals headers aligned
      worksheet.mergeCells('A2:B2');
      const billingHeaderCell = worksheet.getCell('A2');
      billingHeaderCell.value = 'Billing Information';
      billingHeaderCell.font = { name: 'Calibri', size: 11, bold: true };
      billingHeaderCell.alignment = { horizontal: 'center', vertical: 'middle' };
      billingHeaderCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF00' } };
      billingHeaderCell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };

      // C2: Totals Actual Hours header
      const actualHeaderCell = worksheet.getCell('C2');
      actualHeaderCell.value = 'Totals Actual Hours';
      actualHeaderCell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
      actualHeaderCell.alignment = { horizontal: 'center', vertical: 'middle' };
      actualHeaderCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
      actualHeaderCell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };

      // D2: Totals Billable Hrs header
      const billableHeaderCell = worksheet.getCell('D2');
      billableHeaderCell.value = 'Totals Billable Hrs';
      billableHeaderCell.font = { name: 'Calibri', size: 11, bold: true };
      billableHeaderCell.alignment = { horizontal: 'center', vertical: 'middle' };
      billableHeaderCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF92D050' } };
      billableHeaderCell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };

      // E2: Totals Differences header
      const differencesHeaderCell = worksheet.getCell('E2');
      differencesHeaderCell.value = 'Totals Differences';
      differencesHeaderCell.font = { name: 'Calibri', size: 11, bold: true };
      differencesHeaderCell.alignment = { horizontal: 'center', vertical: 'middle' };
      differencesHeaderCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF92D050' } };
      differencesHeaderCell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };

      worksheet.getRow(2).height = 25;

      // F2 and G2: Empty cells to match structure
      const f2Cell = worksheet.getCell('F2');
      f2Cell.value = '';
      f2Cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
      
      const g2Cell = worksheet.getCell('G2');
      g2Cell.value = '';
      g2Cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };

      // Row 3: Values row - Billing dates and Totals values aligned
      worksheet.mergeCells('A3:B3');
      const billingCell = worksheet.getCell('A3');
      billingCell.value = `Billing from: ${formattedStartDate} - ${formattedEndDate}\nBilled on : ${billedOnDate}`;
      billingCell.font = { name: 'Calibri', size: 11, bold: true };
      billingCell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
      billingCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFF00' } };
      billingCell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };

      worksheet.getRow(3).height = 40;

      // F3 and G3: Empty cells to match structure
      const f3Cell = worksheet.getCell('F3');
      f3Cell.value = '';
      f3Cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
      
      const g3Cell = worksheet.getCell('G3');
      g3Cell.value = '';
      g3Cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };

      // Calculate last row for SUM formula (row 5 is first data row, + number of patients)
      const lastDataRow = 5 + patients.length - 1;

      // C3: Total Actual Hours value
      const actualTotalCell = worksheet.getCell('C3');
      actualTotalCell.value = { formula: `SUM(C5:C${lastDataRow})` };
      actualTotalCell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
      actualTotalCell.alignment = { horizontal: 'center', vertical: 'middle' };
      actualTotalCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
      actualTotalCell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };

      // D3: Total Billable Hours value
      const billableTotalCell = worksheet.getCell('D3');
      billableTotalCell.value = { formula: `SUM(D5:D${lastDataRow})` };
      billableTotalCell.font = { name: 'Calibri', size: 11, bold: true };
      billableTotalCell.alignment = { horizontal: 'center', vertical: 'middle' };
      billableTotalCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF92D050' } };
      billableTotalCell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };

      // E3: Difference total - use formula for automatic calculation
      const differenceTotalCell = worksheet.getCell('E3');
      differenceTotalCell.value = { formula: 'C3-D3' };
      differenceTotalCell.font = { name: 'Calibri', size: 11, bold: true };
      differenceTotalCell.alignment = { horizontal: 'center', vertical: 'middle' };
      differenceTotalCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF92D050' } };
      differenceTotalCell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };

      worksheet.getRow(4).height = 20;

      // Row 4: Column Headers
      const headerRowData = worksheet.getRow(4);
      headerRowData.values = ['Admission ID', 'Patient Name', 'Actual Hours', 'Billable Hrs', 'Differences', 'Note', 'Branch'];
      headerRowData.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
      headerRowData.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      headerRowData.height = 30;
      headerRowData.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      // Data rows
      let rowIndex = 5;
      patients.forEach((patient) => {
        // Actual Hours = patient's total hours
        const actualHours = patient.totalHours || 0;
        // Billable Hours = patient's billable hours
        const billableHours = patient.billableHours || 0;

        const dataRow = worksheet.getRow(rowIndex);
        dataRow.values = [
          patient.patientId,
          patient.patientName,
          parseFloat(actualHours.toFixed(2)),
          parseFloat(billableHours.toFixed(2)),
          { formula: `C${rowIndex}-D${rowIndex}` }, // Excel formula for automatic calculation
          '', // Note column - empty for user to fill
          patient.branch || 'N/A' // Branch column
        ];
        dataRow.font = { name: 'Calibri', size: 11 };
        dataRow.alignment = { horizontal: 'center', vertical: 'middle' };
        dataRow.height = 20;
        dataRow.eachCell((cell) => {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } };
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        });
        rowIndex++;
      });

      // Add data validation dropdown for Branch column (column G)
      // Get unique branches from all patients
      const uniqueBranches = [...new Set(patients.map(p => p.branch || 'N/A'))].sort();
      const branchList = uniqueBranches.join(',');
      
      // Apply dropdown validation to Branch column for all data rows
      for (let i = 5; i <= 5 + patients.length - 1; i++) {
        const branchCell = worksheet.getCell(`G${i}`);
        branchCell.dataValidation = {
          type: 'list',
          allowBlank: false,
          formulae: [`"${branchList}"`],
          showErrorMessage: true,
          errorStyle: 'error',
          errorTitle: 'Invalid Branch',
          error: 'Please select a branch from the dropdown list'
        };
      }
    }

    // Generate Excel file and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${firstContractName} ${firstEndDate}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  // Highlight search matches - stable identity for memoized children.
  const highlightMatch = useCallback((text, searchText) => highlightMatchUtil(text, searchText), []);

  // Authentication routing
  if (authMode === null) {
    // Demo mode landing page (no login buttons - completely disabled)
    if (DEMO_MODE) {
      return (
        <Box sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#4f46e5',
          padding: { xs: 1.5, sm: 2 }
        }}>
          <Paper elevation={10} sx={{
            padding: { xs: 3, sm: 5 },
            maxWidth: 500,
            width: '100%',
            borderRadius: 3,
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)'
          }}>
            <UploadFileIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: '#667eea', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#333', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              CSV Upload Portal
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, color: '#666', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              Welcome to our CSV Upload Portal
            </Typography>
            <Typography variant="body2" sx={{ mb: { xs: 3, sm: 4 }, color: '#999', fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
              This is a demo preview. Login functionality is restricted.
            </Typography>
            <Box sx={{ 
              p: 3, 
              backgroundColor: '#f5f5f5', 
              borderRadius: 2,
              textAlign: 'left'
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
                Features:
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>✓ CSV File Upload & Processing</Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>✓ Data Visualization</Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>✓ Export Functionality</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>✓ User Management (Admin)</Typography>
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 3, color: '#bbb' }}>
              Contact admin for access credentials
            </Typography>
          </Paper>
        </Box>
      );
    }
    
    // Initial selection screen (normal mode or admin access)
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#4f46e5',
        padding: { xs: 1.5, sm: 2 }
      }}>
        <Paper elevation={10} sx={{
          padding: { xs: 3, sm: 5 },
          maxWidth: 500,
          width: '100%',
          borderRadius: 3,
          textAlign: 'center',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)'
        }}>
          <UploadFileIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: '#667eea', mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#333', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            CSV Upload Portal
          </Typography>
          <Typography variant="body1" sx={{ mb: { xs: 3, sm: 4 }, color: '#666', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            Welcome! Please select your access level
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => setAuthMode('admin')}
              sx={{
                background: '#4f46e5',
                padding: { xs: '12px', sm: '15px' },
                fontWeight: 600,
                fontSize: { xs: '1rem', sm: '1.1rem' },
                '&:hover': {
                  background: '#4338ca',
                }
              }}
            >
              Admin Login
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => setAuthMode('user')}
              sx={{
                background: '#0284c7',
                padding: { xs: '12px', sm: '15px' },
                fontWeight: 600,
                fontSize: { xs: '1rem', sm: '1.1rem' },
                '&:hover': {
                  background: '#0369a1',
                }
              }}
            >
              User Login
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={async () => {
                try {
                  await createTestUser();
                  alert('Test user created!\n\nUsername: testuser\nPassword: test123\n\nYou can now use these credentials to login.');
                } catch (err) {
                  alert('Error creating user: ' + err.message);
                }
              }}
              sx={{
                mt: 2,
                color: '#999',
                borderColor: '#ddd',
                fontSize: '0.8rem',
                '&:hover': {
                  borderColor: '#999',
                  background: '#f5f5f5'
                }
              }}
            >
              Setup: Create Test User
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (authMode === 'admin') {
    return <AdminLogin 
      onLogin={(adminUser) => {
        const authData = { email: adminUser.email, role: 'admin' };
        setAuthMode('adminLoggedIn');
        setCurrentUser(authData);
        localStorage.setItem('authMode', 'adminLoggedIn');
        localStorage.setItem('currentUser', JSON.stringify(authData));
      }} 
      onBack={() => setAuthMode(null)} 
    />;
  }

  if (authMode === 'adminLoggedIn') {
    return <AdminDashboard 
      onLogout={async () => {
        await logoutUser(); // Firebase sign out
        setAuthMode(null);
        setCurrentUser(null);
        localStorage.removeItem('authMode');
        localStorage.removeItem('currentUser');
      }} 
    />;
  }

  if (authMode === 'user') {
    return (
      <UserLogin 
        onLogin={(user) => {
          setCurrentUser(user);
          setAuthMode('userLoggedIn');
          localStorage.setItem('authMode', 'userLoggedIn');
          localStorage.setItem('currentUser', JSON.stringify(user));
        }}
        onBack={() => setAuthMode(null)}
      />
    );
  }

  // Main CSV Upload Application (only accessible after user login)
  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.85;
              transform: scale(0.98);
            }
          }
        `}
      </style>
      <Box sx={{ 
        backgroundColor: "#f8fafc", 
        minHeight: "100vh", 
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 1, sm: 2 },
        color: '#0f172a',
        fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif'
      }}>
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        {/* User Info and Logout Bar */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 },
          mb: 3,
          p: { xs: 2.5, sm: 3 },
          background: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          border: '2px solid #10b981',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative corner element */}
          <Box sx={{
            position: 'absolute',
            top: -30,
            right: -30,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'rgba(16, 185, 129, 0.1)',
            zIndex: 0
          }} />
          
          <Box sx={{ textAlign: { xs: 'center', sm: 'left' }, position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              <Typography sx={{ fontSize: '1rem' }}>👋</Typography>
              <Typography variant="body2" sx={{ color: '#047857', fontSize: '0.75rem', fontWeight: 600 }}>
                Welcome,
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#065f46', fontSize: '0.85rem' }}>
                {currentUser?.username} {currentUser?.agency && `(${currentUser.agency})`}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            onClick={() => {
              setAuthMode(null);
              setCurrentUser(null);
              setGroupedData([]);
              localStorage.removeItem('authMode');
              localStorage.removeItem('currentUser');
            }}
            sx={{ 
              background: '#dc2626',
              fontWeight: 700,
              minWidth: 'fit-content',
              px: 2,
              py: 0.5,
              fontSize: '0.75rem',
              borderRadius: 2,
              boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
              position: 'relative',
              zIndex: 1,
              '&:hover': {
                background: '#b91c1c',
                boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                },
              transition: 'all 0.3s ease'
            }}
          >
            🚪 Logout
          </Button>
        </Box>

        {/* Title - Compact */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.25
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
            <Typography sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem' } }}>📊</Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 800, 
                color: '#0f172a',
                letterSpacing: '-0.5px',
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                lineHeight: 1.2,
                whiteSpace: 'nowrap'
              }}
            >
              Billing Hours & Visit Tracker
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ 
            color: '#9ca3af', 
            fontSize: '0.7rem',
            fontWeight: 500
          }}>
            Track billable hours and monitor visit status
          </Typography>
        </Box>

        {/* Action, Summary & Search Card */}
        <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: { xs: 2, sm: 3 }, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5, md: 2 }, flexWrap: 'wrap', alignItems: 'center', flexDirection: 'row', width: '100%' }}>
            {groupedData.length === 0 && gpsData.length === 0 && (
              <Button
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={() => document.getElementById('csvInput').click()}
                sx={{
                  background: '#16a34a',
                  px: { xs: 2, sm: 3, md: 4 },
                  py: { xs: 1, sm: 1.5 },
                  fontWeight: 700,
                  fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' },
                  borderRadius: 3,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                  textTransform: 'none',
                  minWidth: 'fit-content',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    background: '#15803d',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                    border: '2px solid rgba(255, 255, 255, 0.5)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                📥 Import Visit Log
              </Button>
            )}
            {currentUser?.gpsAccess === true && (
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={() => document.getElementById('gpsInput').click()}
              sx={{
                background: '#0ea5e9',
                px: { xs: 2, sm: 3 },
                py: { xs: 1, sm: 1.25 },
                fontWeight: 700,
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                borderRadius: 3,
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                textTransform: 'none',
                minWidth: 'fit-content',
                color: '#fff',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  background: '#0284c7',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                  border: '2px solid rgba(255, 255, 255, 0.5)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              📍 GPS Tracer
            </Button>
            )}
            <Box sx={{
              minWidth: { xs: '120px', sm: '150px' },
              position: 'relative'
            }}>
              <TextField
                label="📅 Billed Date"
                value={billedDate}
                onChange={(e) => setBilledDate(e.target.value)}
                placeholder="MM/DD/YYYY"
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: '#d97706',
                    borderRadius: 2,
                    height: '38px',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    color: 'white',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    transition: 'all 0.3s ease',
                    '& input': {
                      color: 'white',
                      fontWeight: 700,
                      padding: '8px 12px',
                      '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontWeight: 600,
                        opacity: 1
                      }
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                    '&:hover': {
                      background: '#b45309',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                      border: '2px solid rgba(255, 255, 255, 0.5)'
                    },
                    '&.Mui-focused': {
                      background: '#b45309',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                      border: '2px solid rgba(255, 255, 255, 0.6)'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    color: 'rgba(255, 255, 255, 0.9)',
                    '&.Mui-focused': {
                      color: 'white',
                      fontWeight: 800
                    }
                  }
                }}
              />
            </Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportToCSV}
              disabled={filteredData.length === 0}
              sx={{
                background: filteredData.length === 0 ? '#e5e7eb' : '#0284c7',
                px: 2,
                height: '38px',
                fontWeight: 700,
                fontSize: '0.8rem',
                borderRadius: 2,
                boxShadow: filteredData.length === 0 ? 'none' : '0 1px 2px rgba(0,0,0,0.06)',
                textTransform: 'none',
                minWidth: '100px',
                border: filteredData.length === 0 ? 'none' : '2px solid rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  background: filteredData.length === 0 ? '#e5e7eb' : '#0369a1',
                  boxShadow: filteredData.length === 0 ? 'none' : '0 2px 6px rgba(0,0,0,0.08)',
                  transform: filteredData.length === 0 ? 'none' : 'translateY(-2px)',
                  border: filteredData.length === 0 ? 'none' : '2px solid rgba(255, 255, 255, 0.5)'
                },
                transition: 'all 0.3s ease',
                '&.Mui-disabled': {
                  background: '#e5e7eb',
                  color: '#9ca3af',
                  border: 'none'
                }
              }}
            >
              📊 Export
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportCaregiverHours}
              disabled={filteredData.length === 0}
              sx={{
                background: filteredData.length === 0 ? '#e5e7eb' : '#15803d',
                px: 2,
                height: '38px',
                fontWeight: 700,
                fontSize: '0.8rem',
                borderRadius: 2,
                boxShadow: filteredData.length === 0 ? 'none' : '0 1px 2px rgba(0,0,0,0.06)',
                textTransform: 'none',
                minWidth: '100px',
                border: filteredData.length === 0 ? 'none' : '2px solid rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  background: filteredData.length === 0 ? '#e5e7eb' : '#15803d',
                  boxShadow: filteredData.length === 0 ? 'none' : '0 2px 6px rgba(0,0,0,0.08)',
                  transform: filteredData.length === 0 ? 'none' : 'translateY(-2px)',
                  border: filteredData.length === 0 ? 'none' : '2px solid rgba(255, 255, 255, 0.5)'
                },
                transition: 'all 0.3s ease',
                '&.Mui-disabled': {
                  background: '#e5e7eb',
                  color: '#9ca3af',
                  border: 'none'
                }
              }}
            >
              👤 Caregiver
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportPayroll}
              disabled={filteredData.length === 0}
              sx={{
                background: filteredData.length === 0 ? '#e5e7eb' : '#4f46e5',
                px: 2,
                height: '38px',
                fontWeight: 700,
                fontSize: '0.8rem',
                borderRadius: 2,
                boxShadow: filteredData.length === 0 ? 'none' : '0 1px 2px rgba(0,0,0,0.06)',
                textTransform: 'none',
                minWidth: '100px',
                color: '#fff',
                border: filteredData.length === 0 ? 'none' : '2px solid rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  background: filteredData.length === 0 ? '#e5e7eb' : '#4338ca',
                  boxShadow: filteredData.length === 0 ? 'none' : '0 2px 6px rgba(0,0,0,0.08)',
                  transform: filteredData.length === 0 ? 'none' : 'translateY(-2px)',
                  border: filteredData.length === 0 ? 'none' : '2px solid rgba(255, 255, 255, 0.5)'
                },
                transition: 'all 0.3s ease',
                '&.Mui-disabled': {
                  background: '#e5e7eb',
                  color: '#9ca3af',
                  border: 'none'
                }
              }}
            >
              💵 Payroll
            </Button>
            <Button
              variant="contained"
              onClick={printSelectedPatients}
              disabled={filteredData.length === 0}
              sx={{
                background: filteredData.length === 0 ? '#e5e7eb' : '#0f172a',
                px: 2,
                height: '38px',
                fontWeight: 700,
                fontSize: '0.8rem',
                borderRadius: 2,
                textTransform: 'none',
                minWidth: '110px',
                color: '#fff',
                '&:hover': { background: filteredData.length === 0 ? '#e5e7eb' : '#1e293b' },
                '&.Mui-disabled': { background: '#e5e7eb', color: '#9ca3af' }
              }}
            >
              🖨️ Print PDF{selectedPatientIds.length > 0 ? ` (${selectedPatientIds.length})` : ''}
            </Button>
            {filteredData.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                onClick={toggleSelectAllPatients}
                sx={{
                  height: '38px',
                  borderColor: '#cbd5e1',
                  color: '#0f172a',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  borderRadius: 2,
                  '&:hover': { borderColor: '#4f46e5', background: '#eef2ff' }
                }}
              >
                {allFilteredSelected ? '☐ Unselect All' : '☑ Select All'}
              </Button>
            )}
            {groupedData.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 0.75, sm: 1 },
                  background: '#4f46e5',
                  color: 'white',
                  px: { xs: 1.5, sm: 2 },
                  height: '38px',
                  borderRadius: 2,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                  border: '2px solid rgba(255,255,255,0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                    }
                }}
              >
                {/* Billable/Total */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>📊</Typography>
                  <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 600, opacity: 0.9 }}>Billed/Billable/Totals:</Typography>
                  <Typography component="span" sx={{ 
                    fontSize: { xs: '0.8rem', sm: '0.9rem' }, 
                    fontWeight: 800, 
                    color: '#c4b5fd',
                    textShadow: '0 0 8px rgba(196, 181, 253, 0.6), 0 0 16px rgba(196, 181, 253, 0.3)'
                  }}>
                    {totalAllBilledHours.toFixed(2)}
                  </Typography>
                  <Typography component="span" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, fontWeight: 800 }}>/</Typography>
                  <Typography component="span" sx={{ 
                    fontSize: { xs: '0.8rem', sm: '0.9rem' }, 
                    fontWeight: 800, 
                    color: '#00ff88',
                    textShadow: '0 0 8px rgba(0, 255, 136, 0.6), 0 0 16px rgba(0, 255, 136, 0.3)'
                  }}>
                    {totalAllBillableHours.toFixed(2)}
                  </Typography>
                  <Typography component="span" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, fontWeight: 800 }}>
                    /{totalAllHours.toFixed(2)}
                  </Typography>
                </Box>
                
                <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, fontWeight: 700, opacity: 0.5 }}>|</Typography>
                
                {/* Caregivers */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>👤</Typography>
                  <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 600, opacity: 0.9 }}>Caregivers:</Typography>
                  <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, fontWeight: 800 }}>
                    {caregiverTotals.count} ({formatHoursAsUnits(caregiverTotals.totalHours).toFixed(2)} hrs)
                  </Typography>
                </Box>
              </Box>
            )}
            <FormControlLabel
              control={<Switch checked={tableView} onChange={() => setTableView(!tableView)} />}
              label={tableView ? 'Table View' : 'Card View'}
              sx={{ mr: 0, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'center', sm: 'flex-start' } }}
            />
          </Box>
          {/* Drag & Drop zone (shown until data loaded) */}
          {groupedData.length === 0 && gpsData.length === 0 && (
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                mt: 3,
                p: { xs: 3, sm: 4 },
                border: '3px dashed',
                borderColor: dragActive ? '#10b981' : '#10b981',
                borderRadius: 3,
                textAlign: 'center',
                background: dragActive 
                  ? '#f0fdf4'
                  : '#ffffff',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 6px 20px rgba(5, 150, 105, 0.2)',
                '&:hover': {
                  borderColor: '#059669',
                  background: '#f8fafc',
                  boxShadow: '0 8px 24px rgba(5, 150, 105, 0.25)',
                  },
                '&::before': dragActive ? {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.1) 0%, transparent 70%)',
                  animation: 'pulse 2s ease-in-out infinite'
                } : {}
              }}
              onClick={() => document.getElementById('csvInput').click()}
            >
              <Box sx={{ 
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: { xs: 60, sm: 70 },
                height: { xs: 60, sm: 70 },
                borderRadius: '50%',
                background: dragActive 
                  ? '#16a34a'
                  : '#16a34a',
                mb: 2,
                transition: 'all 0.3s ease',
                boxShadow: '0 6px 20px rgba(5, 150, 105, 0.35)'
              }}>
                <UploadFileIcon sx={{ 
                  fontSize: { xs: 30, sm: 36 }, 
                  color: 'white',
                  transition: 'all 0.3s ease'
                }} />
              </Box>
              
              <Typography variant="h6" sx={{ 
                fontWeight: 700, 
                mb: 1,
                color: dragActive ? '#059669' : '#1f2937',
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                transition: 'all 0.3s ease'
              }}>
                {dragActive ? '📂 Release to Upload' : '📤 Upload CSV File'}
              </Typography>
              
              <Typography variant="body2" sx={{ 
                mb: 1.5,
                color: '#6b7280',
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                fontWeight: 500
              }}>
                Drag and drop your CSV file here
              </Typography>
              
              <Box sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: dragActive ? 'rgba(255,255,255,0.9)' : '#f3f4f6',
                px: 2.5,
                py: 1,
                borderRadius: 2,
                border: '1px solid',
                borderColor: dragActive ? '#10b981' : '#e5e7eb',
                transition: 'all 0.3s ease'
              }}>
                <Typography variant="caption" sx={{ 
                  color: '#6b7280',
                  fontWeight: 600,
                  fontSize: '0.8rem'
                }}>
                  or click to browse
                </Typography>
              </Box>
              
              {droppedFileName && (
                <Box sx={{ 
                  mt: 2, 
                  pt: 2, 
                  borderTop: '2px dashed #e5e7eb'
                }}>
                  <Typography variant="caption" sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    color: '#059669',
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}>
                    ✓ Last uploaded: <strong>{droppedFileName}</strong>
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          {groupedData.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                {/* Search Bar - Compact */}
                <TextField
                  placeholder="🔍 Search (MI, MO, Overlap)..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  size="small"
                  sx={{
                    flex: { xs: '1 1 100%', sm: '1 1 45%', md: '0 1 auto' },
                    minWidth: { xs: '100%', sm: '150px', md: '180px' },
                    maxWidth: { xs: '100%', sm: '200px', md: '280px' },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      background: '#ffffff',
                      transition: 'all 0.3s ease',
                      border: '2px solid #d1fae5',
                      '&:hover': {
                        border: '2px solid #10b981',
                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)'
                      },
                      '&.Mui-focused': {
                        border: '2px solid #059669',
                        boxShadow: '0 4px 16px rgba(16, 185, 129, 0.35)',
                        background: 'white'
                      },
                      '& fieldset': {
                        border: 'none'
                      }
                    },
                    '& .MuiInputBase-input': {
                      padding: '6px 10px',
                      fontSize: { xs: '0.75rem', sm: '0.8rem' },
                      fontWeight: 500,
                      color: '#047857'
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#10b981',
                      opacity: 0.7
                    }
                  }}
                />
                
                {/* Status Filter Dropdown */}
                <FormControl size="small" sx={{ 
                  minWidth: { xs: '100%', sm: '48%', md: '130px' },
                  flex: { xs: '1 1 100%', sm: '1 1 45%', md: '0 1 auto' },
                  width: { xs: '100%', sm: 'auto' }
                }}>
                  <Select
                    displayEmpty
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    input={<OutlinedInput />}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#dc2626' }}>⚠️ Status</Box>;
                      }
                      return (
                        <Chip 
                          label={selected} 
                          size="small"
                          sx={{
                            height: '20px',
                            backgroundColor: selected === '24 Hrs Cross' ? '#b91c1c' : (selected === 'Overlap' ? '#ef4444' : '#f59e0b'),
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      );
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          borderRadius: 8,
                          boxShadow: '0 8px 24px rgba(239, 68, 68, 0.2)',
                        },
                      },
                    }}
                    sx={{
                      borderRadius: 3,
                      background: '#ffffff',
                      border: '2px solid #fecaca',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        border: '2px solid #ef4444',
                        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)',
                      },
                      '&.Mui-focused': {
                        border: '2px solid #dc2626',
                        boxShadow: '0 4px 16px rgba(239, 68, 68, 0.35)',
                        background: 'white'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none'
                      },
                      '& .MuiSelect-select': {
                        padding: '6px 10px',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: '#dc2626'
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>All Status</em>
                    </MenuItem>
                    <MenuItem value="Overlap">⚠️ Overlap</MenuItem>
                    <MenuItem value="24 Hrs Cross">🚫 24 Hrs Cross</MenuItem>
                  </Select>
                </FormControl>
                
                {/* Contract Filter Dropdown */}
                {availableContracts.length > 0 && (
                  <FormControl size="small" sx={{ 
                    minWidth: { xs: '100%', sm: '48%', md: '150px' },
                    flex: { xs: '1 1 100%', sm: '1 1 45%', md: '0 1 auto' },
                    width: { xs: '100%', sm: 'auto' }
                  }}>
                    <Select
                      multiple
                      displayEmpty
                      value={selectedContracts}
                      onChange={(e) => setSelectedContracts(e.target.value)}
                      input={<OutlinedInput />}
                      renderValue={(selected) => {
                        if (selected.length === 0) {
                          return <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#7c3aed' }}>📋 Contract</Box>;
                        }
                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.slice(0, 1).map((value) => (
                              <Chip 
                                key={value} 
                                label={value} 
                                size="small"
                                sx={{
                                  height: '20px',
                                  backgroundColor: '#7c3aed',
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  '& .MuiChip-label': { px: 1 }
                                }}
                              />
                            ))}
                            {selected.length > 1 && (
                              <Chip 
                                label={`+${selected.length - 1}`} 
                                size="small"
                                sx={{
                                  height: '20px',
                                  backgroundColor: '#a855f7',
                                  color: 'white',
                                  fontWeight: 700,
                                  fontSize: '0.7rem',
                                  '& .MuiChip-label': { px: 1 }
                                }}
                              />
                            )}
                          </Box>
                        );
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            borderRadius: 8,
                            boxShadow: '0 8px 24px rgba(124, 58, 237, 0.2)',
                          },
                        },
                      }}
                      sx={{
                        borderRadius: 3,
                        background: '#ffffff',
                        border: '2px solid #e9d5ff',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          border: '2px solid #7c3aed',
                          boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)',
                        },
                        '&.Mui-focused': {
                          border: '2px solid #6d28d9',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                          background: 'white'
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '& .MuiSelect-select': {
                          padding: '8px 12px',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          color: '#6d28d9'
                        }
                      }}
                    >
                      {availableContracts.map((contract) => (
                        <MenuItem key={contract} value={contract}>
                          <Checkbox 
                            checked={selectedContracts.indexOf(contract) > -1}
                            sx={{
                              color: '#d1d5db',
                              '&.Mui-checked': {
                                color: '#059669',
                              },
                            }}
                          />
                          <ListItemText 
                            primary={contract}
                            primaryTypographyProps={{
                              fontSize: '0.9rem',
                              fontWeight: 500,
                            }}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Billed Filter Dropdown */}
                <FormControl size="small" sx={{
                  minWidth: { xs: '100%', sm: '48%', md: '150px' },
                  flex: { xs: '1 1 100%', sm: '1 1 45%', md: '0 1 auto' },
                  width: { xs: '100%', sm: 'auto' }
                }}>
                  <Select
                    displayEmpty
                    value={billedFilter}
                    onChange={(e) => setBilledFilter(e.target.value)}
                    input={<OutlinedInput />}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#0369a1' }}>💵 Billed</Box>;
                      }
                      return (
                        <Chip
                          label={selected === 'Yes' ? 'Billed: Yes' : 'Billed: No'}
                          size="small"
                          sx={{
                            height: '20px',
                            backgroundColor: selected === 'Yes' ? '#059669' : '#f59e0b',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      );
                    }}
                    sx={{
                      borderRadius: 3,
                      background: '#ffffff',
                      border: '2px solid #bae6fd',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        border: '2px solid #0ea5e9',
                        boxShadow: '0 4px 12px rgba(14, 165, 233, 0.25)',
                      },
                      '&.Mui-focused': {
                        border: '2px solid #0369a1',
                        boxShadow: '0 4px 16px rgba(14, 165, 233, 0.35)',
                        background: 'white'
                      },
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '& .MuiSelect-select': {
                        padding: '8px 12px',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        color: '#0369a1'
                      }
                    }}
                  >
                    <MenuItem value="">
                      <em>All (Billed & Not Billed)</em>
                    </MenuItem>
                    <MenuItem value="Yes">✅ Billed: Yes</MenuItem>
                    <MenuItem value="No">⚠️ Billed: No (Need to Bill)</MenuItem>
                  </Select>
                </FormControl>

                {/* Branch Filter Dropdown */}
                {availableBranches.length > 0 && (
                  <FormControl size="small" sx={{ 
                    minWidth: { xs: '100%', sm: '48%', md: '150px' },
                    flex: { xs: '1 1 100%', sm: '1 1 45%', md: '0 1 auto' },
                    width: { xs: '100%', sm: 'auto' }
                  }}>
                    <Select
                      multiple
                      displayEmpty
                      value={selectedBranches}
                      onChange={(e) => setSelectedBranches(e.target.value)}
                      input={<OutlinedInput />}
                      renderValue={(selected) => {
                        if (selected.length === 0) {
                          return <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#f97316' }}>🏢 Branch</Box>;
                        }
                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.slice(0, 1).map((value) => (
                              <Chip 
                                key={value} 
                                label={value} 
                                size="small"
                                sx={{
                                  height: '20px',
                                  backgroundColor: '#f97316',
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  '& .MuiChip-label': { px: 1 }
                                }}
                              />
                            ))}
                            {selected.length > 1 && (
                              <Chip 
                                label={`+${selected.length - 1}`} 
                                size="small"
                                sx={{
                                  height: '20px',
                                  backgroundColor: '#fb923c',
                                  color: 'white',
                                  fontWeight: 700,
                                  fontSize: '0.7rem',
                                  '& .MuiChip-label': { px: 1 }
                                }}
                              />
                            )}
                          </Box>
                        );
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            borderRadius: 8,
                            boxShadow: '0 8px 24px rgba(249, 115, 22, 0.2)',
                          },
                        },
                      }}
                      sx={{
                        borderRadius: 3,
                        background: '#ffffff',
                        border: '2px solid #fed7aa',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          border: '2px solid #f97316',
                          boxShadow: '0 4px 12px rgba(249, 115, 22, 0.25)',
                        },
                        '&.Mui-focused': {
                          border: '2px solid #ea580c',
                          boxShadow: '0 4px 16px rgba(249, 115, 22, 0.35)',
                          background: 'white'
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '& .MuiSelect-select': {
                          padding: '8px 12px',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          color: '#ea580c'
                        }
                      }}
                    >
                      {availableBranches.map((branch) => (
                        <MenuItem key={branch} value={branch}>
                          <Checkbox 
                            checked={selectedBranches.indexOf(branch) > -1}
                            sx={{
                              color: '#d1d5db',
                              '&.Mui-checked': {
                                color: '#3b82f6',
                              },
                            }}
                          />
                          <ListItemText 
                            primary={branch}
                            primaryTypographyProps={{
                              fontSize: '0.9rem',
                              fontWeight: 500,
                            }}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
              <Box sx={{ 
                mt: 1.5, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                px: 1,
                flexWrap: 'wrap',
                display: { xs: 'none', sm: 'flex' }
              }}>
                <Typography variant="caption" sx={{ 
                  color: '#6b7280',
                  fontSize: '0.75rem',
                  fontWeight: 500
                }}>
                  💡 Tips:
                </Typography>
                <Box component="span" sx={{ 
                  bgcolor: '#fef3c7', 
                  px: 0.75, 
                  py: 0.25, 
                  borderRadius: 0.5,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  color: '#d97706',
                  border: '1px solid #fcd34d'
                }}>
                  MI
                </Box>
                <Typography variant="caption" sx={{ 
                  color: '#6b7280',
                  fontSize: '0.75rem',
                  fontWeight: 500
                }}>
                  = Missed In,
                </Typography>
                <Box component="span" sx={{ 
                  bgcolor: '#fef3c7', 
                  px: 0.75, 
                  py: 0.25, 
                  borderRadius: 0.5,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  color: '#d97706',
                  border: '1px solid #fcd34d'
                }}>
                  MO
                </Box>
                <Typography variant="caption" sx={{ 
                  color: '#6b7280',
                  fontSize: '0.75rem',
                  fontWeight: 500
                }}>
                  = Missed Out,
                </Typography>
                <Box component="span" sx={{ 
                  bgcolor: '#f3f4f6', 
                  px: 0.75, 
                  py: 0.25, 
                  borderRadius: 0.5,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  color: '#059669',
                  border: '1px solid #e5e7eb'
                }}>
                  &
                </Box>
                <Typography variant="caption" sx={{ 
                  color: '#6b7280',
                  fontSize: '0.75rem',
                  fontWeight: 500
                }}>
                  AND,
                </Typography>
                <Box component="span" sx={{ 
                  bgcolor: '#f3f4f6', 
                  px: 0.75, 
                  py: 0.25, 
                  borderRadius: 0.5,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  color: '#059669',
                  border: '1px solid #e5e7eb'
                }}>
                  |
                </Box>
                <Typography variant="caption" sx={{ 
                  color: '#6b7280',
                  fontSize: '0.75rem',
                  fontWeight: 500
                }}>
                  OR
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>

        <input
          type="file"
          accept=".csv"
          id="csvInput"
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />
        {currentUser?.gpsAccess === true && (
        <input
          type="file"
          accept=".csv"
          id="gpsInput"
          onChange={handleGpsUpload}
          style={{ display: "none" }}
        />
        )}

        {/* Search moved into card above */}

        {currentUser?.gpsAccess === true && gpsLoading && (
          <Paper className="no-print" elevation={0} sx={{
            mb: { xs: 2, sm: 3 }, p: 2, borderRadius: 3,
            border: '1px solid #bae6fd', background: '#f0f9ff',
            display: 'flex', alignItems: 'center', gap: 1.5
          }}>
            <CircularProgress size={20} sx={{ color: '#0284c7' }} />
            <Typography sx={{ fontWeight: 700, color: '#0c4a6e', fontSize: '0.9rem' }}>
              Reading GPS file…
            </Typography>
          </Paper>
        )}

        {/* GPS Tracer Table - Grouped by Patient */}
        {currentUser?.gpsAccess === true && gpsData.length > 0 && (() => {
          const groupList = gpsGroupList;
          const totalOutOfRange = gpsIndex.totalOutOfRange;
          const visibleGroups = groupList.slice(0, gpsVisibleCount);
          const searchPending = gpsSearch !== deferredGpsSearch;

          const visibleKeys = groupList.map(g => g.key);
          const selectedVisibleCount = visibleKeys.filter(k => gpsSelectedKeys.includes(k)).length;
          const allVisibleSelected = visibleKeys.length > 0 && selectedVisibleCount === visibleKeys.length;
          const toggleSelectAllVisible = () => setGpsSelectedKeys(allVisibleSelected ? [] : visibleKeys);
          const exportSelectedGps = () => exportGpsSelectedToExcel(groupList.filter(g => gpsSelectedKeys.includes(g.key)));

          return (
            <Paper className="no-print" elevation={0} sx={{
              mb: { xs: 2, sm: 3 },
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)',
              background: '#fff'
            }}>
              {/* Top accent strip */}
              <Box sx={{
                height: 4,
                background: totalOutOfRange > 0
                  ? 'linear-gradient(90deg, #0ea5e9 0%, #6366f1 50%, #ef4444 100%)'
                  : 'linear-gradient(90deg, #0ea5e9 0%, #06b6d4 50%, #10b981 100%)'
              }} />

              {/* Header — stacked rows for clean responsive layout */}
              <Box sx={{
                px: { xs: 1.5, sm: 2.5 },
                py: { xs: 1.5, sm: 2 },
                borderBottom: '1px solid #f1f5f9',
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 1.25, sm: 1.5 }
              }}>
                {/* Row 1 — title + clear */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  minWidth: 0
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
                    <Box sx={{
                      width: 40, height: 40, borderRadius: 2,
                      background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: '1.2rem',
                      boxShadow: '0 4px 12px rgba(14,165,233,0.35)',
                      flexShrink: 0
                    }}>
                      📍
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.95rem', sm: '1.05rem' }, color: '#0f172a', lineHeight: 1.2 }}>
                        GPS Tracer
                      </Typography>
                      <Typography sx={{
                        fontSize: '0.72rem',
                        color: '#64748b',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        📄 {gpsFileName}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    size="small"
                    onClick={clearGpsData}
                    sx={{
                      color: '#64748b',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.78rem',
                      height: 36,
                      px: 1.25,
                      borderRadius: 2,
                      flexShrink: 0,
                      border: '1px solid #e2e8f0',
                      '&:hover': { background: '#fef2f2', color: '#b91c1c', borderColor: '#fecaca' }
                    }}
                  >
                    ✕ Clear
                  </Button>
                </Box>

                {/* Row 2 — stat tiles (responsive grid, always visible) */}
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: { xs: 0.75, sm: 1 }
                }}>
                  <Box sx={{
                    px: { xs: 1, sm: 1.5 }, py: 0.75, borderRadius: 2,
                    background: '#f1f5f9', border: '1px solid #e2e8f0',
                    textAlign: 'center', minWidth: 0
                  }}>
                    <Typography sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{groupList.length}</Typography>
                    <Typography sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' }, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mt: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Patients</Typography>
                  </Box>
                  <Box sx={{
                    px: { xs: 1, sm: 1.5 }, py: 0.75, borderRadius: 2,
                    background: '#eff6ff', border: '1px solid #bfdbfe',
                    textAlign: 'center', minWidth: 0
                  }}>
                    <Typography sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 800, color: '#1d4ed8', lineHeight: 1 }}>{gpsData.length}</Typography>
                    <Typography sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' }, color: '#1d4ed8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mt: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Visits</Typography>
                  </Box>
                  <Box sx={{
                    px: { xs: 1, sm: 1.5 }, py: 0.75, borderRadius: 2,
                    background: totalOutOfRange > 0 ? '#fef2f2' : '#f0fdf4',
                    border: totalOutOfRange > 0 ? '1px solid #fecaca' : '1px solid #bbf7d0',
                    textAlign: 'center', minWidth: 0
                  }}>
                    <Typography sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 800, color: totalOutOfRange > 0 ? '#b91c1c' : '#15803d', lineHeight: 1 }}>
                      {totalOutOfRange > 0 ? `⚠️ ${totalOutOfRange}` : '✓ 0'}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem' }, color: totalOutOfRange > 0 ? '#b91c1c' : '#15803d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mt: 0.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      &gt; {GPS_RANGE_FT} ft
                    </Typography>
                  </Box>
                </Box>

                {/* Row 3 — search + filter */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1, sm: 1.25 },
                  flexWrap: { xs: 'wrap', sm: 'nowrap' }
                }}>
                  <TextField
                    placeholder="Search patient, address, visit…"
                    value={gpsSearch}
                    onChange={(e) => setGpsSearch(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <Box sx={{
                          color: gpsSearch ? '#0ea5e9' : '#94a3b8',
                          mr: 1,
                          fontSize: '1.05rem',
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'color 0.15s ease'
                        }}>🔍</Box>
                      ),
                      endAdornment: gpsSearch ? (
                        <Box
                          onClick={() => setGpsSearch('')}
                          title="Clear search"
                          sx={{
                            cursor: 'pointer',
                            width: 22, height: 22,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#64748b',
                            background: '#e2e8f0',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            ml: 0.5,
                            transition: 'all 0.15s ease',
                            '&:hover': { background: '#cbd5e1', color: '#0f172a' }
                          }}
                        >
                          ✕
                        </Box>
                      ) : null
                    }}
                    sx={{
                      flex: 1,
                      minWidth: { xs: '100%', sm: 0 },
                      '& .MuiOutlinedInput-root': {
                        height: 42,
                        fontSize: '0.88rem',
                        background: '#fff',
                        borderRadius: 2,
                        boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                        transition: 'all 0.15s ease',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#cbd5e1' },
                        '&.Mui-focused': {
                          background: '#fff',
                          boxShadow: '0 0 0 4px rgba(14,165,233,0.12), 0 1px 2px rgba(15,23,42,0.04)'
                        },
                        '&.Mui-focused fieldset': { borderColor: '#0ea5e9', borderWidth: 1.5 }
                      },
                      '& .MuiOutlinedInput-input::placeholder': {
                        color: '#94a3b8',
                        opacity: 1,
                        fontWeight: 500
                      }
                    }}
                  />
                  {/* Segmented filter — clearer labels with live counts */}
                  <Box
                    role="tablist"
                    aria-label="Filter visits"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'stretch',
                      background: '#f1f5f9',
                      borderRadius: 2,
                      p: 0.5,
                      border: '1px solid #e2e8f0',
                      flexShrink: 0,
                      gap: 0.4,
                      width: { xs: '100%', sm: 'auto' }
                    }}
                  >
                    <Box
                      role="tab"
                      aria-selected={!gpsOnlyOutOfRange}
                      onClick={() => setGpsOnlyOutOfRange(false)}
                      title="Show all patients"
                      sx={{
                        flex: { xs: 1, sm: '0 0 auto' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.65,
                        px: { xs: 1.25, sm: 1.75 },
                        py: 0.7,
                        borderRadius: 1.5,
                        cursor: 'pointer',
                        userSelect: 'none',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        color: !gpsOnlyOutOfRange ? '#fff' : '#64748b',
                        background: !gpsOnlyOutOfRange
                          ? 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)'
                          : 'transparent',
                        boxShadow: !gpsOnlyOutOfRange ? '0 2px 6px rgba(14,165,233,0.4)' : 'none',
                        transition: 'all 0.15s ease',
                        '&:hover': { color: !gpsOnlyOutOfRange ? '#fff' : '#0f172a' }
                      }}
                    >
                      <Box component="span" sx={{ fontSize: '0.95rem', lineHeight: 1 }}>📋</Box>
                      <span>All</span>
                      <Box component="span" sx={{
                        ml: 0.25,
                        px: 0.7,
                        py: 0.1,
                        borderRadius: 999,
                        fontSize: '0.68rem',
                        fontWeight: 800,
                        background: !gpsOnlyOutOfRange ? 'rgba(255,255,255,0.25)' : '#e2e8f0',
                        color: !gpsOnlyOutOfRange ? '#fff' : '#64748b',
                        minWidth: 22,
                        textAlign: 'center',
                        lineHeight: 1.4
                      }}>
                        {gpsData.length}
                      </Box>
                    </Box>
                    <Box
                      role="tab"
                      aria-selected={gpsOnlyOutOfRange}
                      onClick={() => setGpsOnlyOutOfRange(true)}
                      title={`Show only visits > ${GPS_RANGE_FT} ft out of range`}
                      sx={{
                        flex: { xs: 1, sm: '0 0 auto' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.65,
                        px: { xs: 1.25, sm: 1.75 },
                        py: 0.7,
                        borderRadius: 1.5,
                        cursor: 'pointer',
                        userSelect: 'none',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        color: gpsOnlyOutOfRange ? '#fff' : '#64748b',
                        background: gpsOnlyOutOfRange
                          ? 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)'
                          : 'transparent',
                        boxShadow: gpsOnlyOutOfRange ? '0 2px 6px rgba(185,28,28,0.4)' : 'none',
                        transition: 'all 0.15s ease',
                        '&:hover': { color: gpsOnlyOutOfRange ? '#fff' : '#0f172a' }
                      }}
                    >
                      <Box component="span" sx={{ fontSize: '0.95rem', lineHeight: 1 }}>⚠️</Box>
                      <span>Out of range</span>
                      <Box component="span" sx={{
                        ml: 0.25,
                        px: 0.7,
                        py: 0.1,
                        borderRadius: 999,
                        fontSize: '0.68rem',
                        fontWeight: 800,
                        background: gpsOnlyOutOfRange ? 'rgba(255,255,255,0.25)' : (totalOutOfRange > 0 ? '#fee2e2' : '#e2e8f0'),
                        color: gpsOnlyOutOfRange ? '#fff' : (totalOutOfRange > 0 ? '#b91c1c' : '#64748b'),
                        minWidth: 22,
                        textAlign: 'center',
                        lineHeight: 1.4
                      }}>
                        {totalOutOfRange}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Selection + Excel export toolbar */}
              <Box sx={{
                px: { xs: 1.5, sm: 2.5 },
                py: 1,
                borderBottom: '1px solid #f1f5f9',
                background: '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Checkbox
                    size="small"
                    checked={allVisibleSelected}
                    indeterminate={selectedVisibleCount > 0 && !allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    disabled={visibleKeys.length === 0}
                    sx={{ p: 0.5 }}
                  />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#334155' }}>
                    Select all ({selectedVisibleCount}/{visibleKeys.length} selected)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {selectedVisibleCount > 0 && (
                    <Button
                      size="small"
                      onClick={() => setGpsSelectedKeys([])}
                      sx={{
                        textTransform: 'none', fontWeight: 600, fontSize: '0.78rem',
                        color: '#64748b', borderRadius: 2, px: 1.25,
                        border: '1px solid #e2e8f0',
                        '&:hover': { background: '#fef2f2', color: '#b91c1c', borderColor: '#fecaca' }
                      }}
                    >
                      Clear selection
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    disabled={selectedVisibleCount === 0}
                    onClick={exportSelectedGps}
                    sx={{
                      textTransform: 'none', fontWeight: 700, fontSize: '0.78rem',
                      borderRadius: 2, px: 1.75,
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      boxShadow: '0 2px 8px rgba(5,150,105,0.3)',
                      '&:hover': { background: 'linear-gradient(135deg, #047857 0%, #065f46 100%)' }
                    }}
                  >
                    🖨️ Print / Export Selected to Excel
                  </Button>
                </Box>
              </Box>

              {/* Patient Cards */}
              <Box sx={{
                p: { xs: 1.25, sm: 2 },
                background: '#f8fafc',
                opacity: searchPending ? 0.55 : 1,
                transition: 'opacity 0.15s ease'
              }}>
                {groupList.length === 0 ? (
                  <Box sx={{
                    p: 6, textAlign: 'center',
                    color: '#94a3b8',
                    background: '#fff',
                    borderRadius: 2,
                    border: '1px dashed #cbd5e1'
                  }}>
                    <Box sx={{ fontSize: '2.5rem', mb: 1 }}>🔍</Box>
                    <Typography sx={{ fontWeight: 600 }}>No matching patients</Typography>
                    <Typography sx={{ fontSize: '0.8rem', mt: 0.5 }}>Try a different search or toggle the filter.</Typography>
                  </Box>
                ) : visibleGroups.map((g, gi) => {
                  const accentColor = g.outOfRange > 0 ? '#dc2626' : '#10b981';
                  return (
                    <Paper key={gi} elevation={0} sx={{
                      mb: { xs: 1.5, sm: 2 },
                      borderRadius: 2.5,
                      overflow: 'hidden',
                      border: '1px solid #e5e7eb',
                      borderLeft: `4px solid ${accentColor}`,
                      background: '#fff',
                      boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                      transition: 'all 0.2s ease',
                      '&:hover': { boxShadow: '0 4px 12px rgba(15,23,42,0.08)', transform: 'translateY(-1px)' }
                    }}>
                      {/* Patient header */}
                      <Box sx={{
                        background: '#fff',
                        borderBottom: '1px solid #f1f5f9',
                        px: { xs: 1.5, sm: 2.5 },
                        py: { xs: 1.25, sm: 1.5 },
                        display: 'flex',
                        alignItems: { xs: 'stretch', sm: 'center' },
                        justifyContent: 'space-between',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 1, sm: 1.5 }
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, flexWrap: 'wrap', minWidth: 0 }}>
                          <Checkbox
                            size="small"
                            checked={gpsSelectedKeys.includes(g.key)}
                            onChange={() => toggleGpsSelected(g.key)}
                            sx={{ p: 0.5, flexShrink: 0 }}
                            inputProps={{ 'aria-label': `Select ${g.patientName}` }}
                          />
                          <Box sx={{
                            width: { xs: 38, sm: 44 }, height: { xs: 38, sm: 44 }, borderRadius: '50%',
                            background: g.outOfRange > 0
                              ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
                              : 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                            color: g.outOfRange > 0 ? '#b91c1c' : '#1d4ed8',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 800, fontSize: { xs: '1rem', sm: '1.1rem' },
                            flexShrink: 0,
                            border: g.outOfRange > 0 ? '2px solid #fca5a5' : '2px solid #93c5fd'
                          }}>
                            {(g.patientName || '?').charAt(0).toUpperCase()}
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{
                              fontWeight: 700,
                              fontSize: { xs: '0.92rem', sm: '1.05rem' },
                              lineHeight: 1.2,
                              color: '#0f172a',
                              wordBreak: 'break-word'
                            }}>
                              {g.patientName}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, mt: 0.4, flexWrap: 'wrap', alignItems: 'center' }}>
                              <Box sx={{
                                background: '#f1f5f9',
                                color: '#475569',
                                px: 0.85, py: 0.2,
                                borderRadius: 0.75,
                                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                fontWeight: 700,
                                letterSpacing: 0.3,
                                fontFamily: 'monospace'
                              }}>
                                #{g.admissionId}
                              </Box>
                              {g.address && (
                                <Box sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  background: '#eff6ff',
                                  color: '#1d4ed8',
                                  border: '1px solid #bfdbfe',
                                  px: 0.85, py: 0.25,
                                  borderRadius: 0.75,
                                  fontSize: { xs: '0.65rem', sm: '0.72rem' },
                                  fontWeight: 600,
                                  maxWidth: { xs: 220, sm: 360, md: 480 },
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                                title={g.address}
                                >
                                  <span>🏠</span>
                                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {g.address}
                                  </Box>
                                  {(g.addressGps || g.address) && (
                                    <Box
                                      component="a"
                                      href={(g.addressGps && gpsMapsUrl(g.addressGps)) || gpsMapsSearchUrl(g.address)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      sx={{
                                        ml: 0.25,
                                        color: '#1d4ed8',
                                        textDecoration: 'none',
                                        fontWeight: 800,
                                        '&:hover': { textDecoration: 'underline' }
                                      }}
                                    >
                                      🗺️
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          <Box sx={{
                            background: '#f8fafc',
                            color: '#475569',
                            border: '1px solid #e2e8f0',
                            px: 1.25, py: 0.5,
                            borderRadius: 1.5,
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            fontWeight: 700,
                            display: 'flex', alignItems: 'center', gap: 0.5
                          }}>
                            <span style={{ fontSize: '0.85em' }}>📅</span>
                            {g.rows.length} visit{g.rows.length === 1 ? '' : 's'}
                          </Box>
                          <Box sx={{
                            background: g.outOfRange > 0 ? '#fef2f2' : '#f0fdf4',
                            color: g.outOfRange > 0 ? '#b91c1c' : '#15803d',
                            border: g.outOfRange > 0 ? '1px solid #fecaca' : '1px solid #bbf7d0',
                            px: 1.25, py: 0.5,
                            borderRadius: 1.5,
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            fontWeight: 800
                          }}>
                            {g.outOfRange > 0 ? `⚠️ ${g.outOfRange} out of range` : '✓ All in range'}
                          </Box>
                        </Box>
                      </Box>

                      {/* === DESKTOP: Visits table === */}
                      <Box sx={{ display: { xs: 'none', md: 'block' }, overflow: 'auto' }}>
                        <Box component="table" sx={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '0.8rem',
                          minWidth: 980,
                          tableLayout: 'fixed',
                          '& th, & td': {
                            border: '1px solid #e2e8f0',
                            verticalAlign: 'middle'
                          }
                        }}>
                          <Box component="colgroup">
                            <Box component="col" sx={{ width: '44px' }} />
                            <Box component="col" sx={{ width: '110px' }} />
                            <Box component="col" sx={{ width: '120px' }} />
                            <Box component="col" sx={{ width: '120px' }} />
                            <Box component="col" sx={{ width: '180px' }} />
                            <Box component="col" sx={{ width: '110px' }} />
                            <Box component="col" sx={{ width: '110px' }} />
                            <Box component="col" />
                          </Box>
                          <Box component="thead">
                            <Box component="tr" sx={{ background: '#f1f5f9' }}>
                              <Box component="th" sx={{ color: '#64748b', p: '10px 8px', textAlign: 'center', fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>#</Box>
                              <Box component="th" sx={{ color: '#475569', p: '10px 12px', textAlign: 'center', fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Visit Date</Box>
                              <Box component="th" sx={{ color: '#475569', p: '10px 12px', textAlign: 'center', fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Scheduled</Box>
                              <Box component="th" sx={{ color: '#475569', p: '10px 12px', textAlign: 'center', fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>EVV</Box>
                              <Box component="th" sx={{ color: '#475569', p: '10px 12px', textAlign: 'left', fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Caregiver</Box>
                              <Box component="th" sx={{ color: '#475569', p: '10px 12px', textAlign: 'center', fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Clock In</Box>
                              <Box component="th" sx={{ color: '#475569', p: '10px 12px', textAlign: 'center', fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Clock Out</Box>
                              <Box component="th" sx={{ color: '#475569', p: '10px 12px', textAlign: 'left', fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Range Note</Box>
                            </Box>
                          </Box>
                          <Box component="tbody">
                            {g.rows.map(({ flat, notes }, ri) => {
                              const flagged = notes.length > 0;
                              const { inDist, outDist, inFlag, outFlag } = flat;
                              const rowBg = flagged ? '#fffafa' : (ri % 2 === 0 ? '#ffffff' : '#fafbfc');
                              return (
                                <Box key={ri} component="tr" sx={{ background: rowBg, '&:hover': { background: flagged ? '#fef2f2' : '#f1f5f9' }, transition: 'background 0.15s ease' }}>
                                  <Box component="td" sx={{ p: '10px 8px', textAlign: 'center', color: '#94a3b8', fontWeight: 700, fontSize: '0.78rem' }}>{ri + 1}</Box>
                                  <Box component="td" sx={{ p: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap', fontWeight: 700, color: '#0f172a', fontSize: '0.82rem' }}>{flat.visitDate}</Box>
                                  <Box component="td" sx={{ p: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap', color: '#475569', fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace', fontSize: '0.78rem' }}>{flat.scheduled}</Box>
                                  <Box component="td" sx={{ p: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap', color: '#475569', fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace', fontSize: '0.78rem' }}>{flat.evv}</Box>
                                  <Box component="td" sx={{ p: '10px 12px', whiteSpace: 'nowrap', color: '#1e293b', fontWeight: 600, fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>{flat.caregiver}</Box>
                                  <Box component="td" sx={{ p: '8px 10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                    <Box component="span" sx={{
                                      display: 'inline-block', px: 1.25, py: 0.4, borderRadius: 1.5,
                                      background: inFlag ? '#fef2f2' : '#f0fdf4',
                                      color: inFlag ? '#b91c1c' : '#15803d',
                                      border: inFlag ? '1px solid #fecaca' : '1px solid #bbf7d0',
                                      fontWeight: 700, fontSize: '0.74rem', minWidth: 78, textAlign: 'center',
                                      fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace'
                                    }}>
                                      {inDist != null ? inDist.toLocaleString() + ' ft' : '—'}
                                    </Box>
                                  </Box>
                                  <Box component="td" sx={{ p: '8px 10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                    <Box component="span" sx={{
                                      display: 'inline-block', px: 1.25, py: 0.4, borderRadius: 1.5,
                                      background: outFlag ? '#fef2f2' : '#f0fdf4',
                                      color: outFlag ? '#b91c1c' : '#15803d',
                                      border: outFlag ? '1px solid #fecaca' : '1px solid #bbf7d0',
                                      fontWeight: 700, fontSize: '0.74rem', minWidth: 78, textAlign: 'center',
                                      fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace'
                                    }}>
                                      {outDist != null ? outDist.toLocaleString() + ' ft' : '—'}
                                    </Box>
                                  </Box>
                                  <Box component="td" sx={{
                                    p: '10px 12px',
                                    verticalAlign: 'top',
                                    whiteSpace: 'normal',
                                    minWidth: 320,
                                    fontSize: '0.78rem'
                                  }}>
                                    {flagged
                                      ? notes.map((n, i) => (
                                          <Box key={i} sx={{
                                            mb: i < notes.length - 1 ? 1 : 0,
                                            p: 1,
                                            borderLeft: '3px solid #dc2626',
                                            background: '#fff5f5',
                                            borderRadius: 0.75
                                          }}>
                                            <Box sx={{ fontWeight: 800, color: '#991b1b', mb: 0.4 }}>
                                              🚨 {n.kind === 'in' ? 'Clock-In' : 'Clock-Out'} {n.distance.toLocaleString()} ft out of range
                                            </Box>
                                            <Box sx={{ color: '#374151', fontWeight: 500, mb: 0.25, wordBreak: 'break-word' }}>
                                              <Box component="span" sx={{ fontWeight: 700 }}>🏠 Patient:</Box> {n.patientAddress || '—'}
                                              {(n.patientCoord || n.clockGps) && (
                                                <Box component="button" type="button"
                                                     onClick={() => setMapDialog({ patientName: g.patientName, patientAddress: n.patientAddress, patientCoord: n.patientCoord, kind: n.kind, clockCoord: n.clockGps, distanceFt: n.distance, miles: n.miles, driveMinutes: n.driveMinutes, directionsUrl: n.directionsUrl })}
                                                     title="View map: red pin = patient, blue pin = caregiver"
                                                     sx={{ ml: 0.75, color: '#2563eb', fontWeight: 700, textDecoration: 'none', background: 'transparent', border: 'none', cursor: 'pointer', font: 'inherit', p: 0, '&:hover': { textDecoration: 'underline' } }}>
                                                  🗺️ Show Map
                                                </Box>
                                              )}
                                            </Box>
                                            <Box sx={{ color: '#374151', fontWeight: 500, mb: 0.5, wordBreak: 'break-word' }}>
                                              <Box component="span" sx={{ fontWeight: 700 }}>📍 Clocked at:</Box> {n.clockGps || '—'}
                                              {(n.patientCoord || n.clockGps) && (
                                                <Box component="button" type="button"
                                                     onClick={() => setMapDialog({ patientName: g.patientName, patientAddress: n.patientAddress, patientCoord: n.patientCoord, kind: n.kind, clockCoord: n.clockGps, distanceFt: n.distance, miles: n.miles, driveMinutes: n.driveMinutes, directionsUrl: n.directionsUrl })}
                                                     title="View map: red pin = patient, blue pin = caregiver"
                                                     sx={{ ml: 0.75, color: '#dc2626', fontWeight: 700, textDecoration: 'none', background: 'transparent', border: 'none', cursor: 'pointer', font: 'inherit', p: 0, '&:hover': { textDecoration: 'underline' } }}>
                                                  🗺️ Show Map
                                                </Box>
                                              )}
                                            </Box>
                                            {n.directionsUrl && (
                                              <Box component="a" href={n.directionsUrl} target="_blank" rel="noopener noreferrer"
                                                   sx={{
                                                     display: 'inline-flex',
                                                     alignItems: 'center',
                                                     gap: 0.75,
                                                     mt: 0.25,
                                                     px: 1.25,
                                                     py: 0.5,
                                                     background: '#1e40af',
                                                     color: '#fff',
                                                     borderRadius: 1,
                                                     fontSize: '0.72rem',
                                                     fontWeight: 700,
                                                     textDecoration: 'none',
                                                     boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                                                     '&:hover': { background: '#1e3a8a' }
                                                   }}>
                                                🚗 Show Route
                                                {n.miles != null && <span>· ≈ {n.miles.toFixed(2)} mi</span>}
                                                {n.driveMinutes != null && <span>· ≈ {n.driveMinutes} min</span>}
                                              </Box>
                                            )}
                                          </Box>
                                        ))
                                      : <Box sx={{ color: '#16a34a', fontWeight: 700 }}>✅ In range</Box>}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      </Box>

                      {/* === MOBILE / TABLET: stacked visit cards === */}
                      <Box sx={{ display: { xs: 'block', md: 'none' }, p: 1, background: '#fafafa' }}>
                        {g.rows.map(({ flat, notes }, ri) => {
                          const flagged = notes.length > 0;
                          const { inDist, outDist, inFlag, outFlag } = flat;
                          return (
                            <Box key={ri} sx={{
                              mb: 1,
                              p: 1.25,
                              borderRadius: 1.5,
                              background: flagged ? '#fff5f5' : '#fff',
                              border: flagged ? '1px solid #fecaca' : '1px solid #e5e7eb',
                              borderLeft: flagged ? '4px solid #dc2626' : '4px solid #cbd5e1'
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                  <Box sx={{ color: '#94a3b8', fontWeight: 700, fontSize: '0.7rem' }}>#{ri + 1}</Box>
                                  <Box sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.85rem' }}>📅 {flat.visitDate}</Box>
                                </Box>
                                <Box sx={{
                                  fontSize: '0.7rem',
                                  px: 0.75,
                                  py: 0.2,
                                  borderRadius: 0.75,
                                  fontWeight: 700,
                                  background: flagged ? '#fee2e2' : '#dcfce7',
                                  color: flagged ? '#991b1b' : '#166534'
                                }}>
                                  {flagged ? '🚨 Out of range' : '✅ In range'}
                                </Box>
                              </Box>

                              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, fontSize: '0.75rem', color: '#475569', mb: 0.75 }}>
                                <Box><Box component="span" sx={{ fontWeight: 700, color: '#1e293b' }}>📋 Sched:</Box> <span style={{ fontFamily: 'monospace' }}>{flat.scheduled}</span></Box>
                                <Box><Box component="span" sx={{ fontWeight: 700, color: '#1e293b' }}>✅ EVV:</Box> <span style={{ fontFamily: 'monospace' }}>{flat.evv}</span></Box>
                                <Box sx={{ gridColumn: '1 / span 2' }}><Box component="span" sx={{ fontWeight: 700, color: '#1e293b' }}>👨‍⚕️ Caregiver:</Box> {flat.caregiver}</Box>
                              </Box>

                              <Box sx={{ display: 'flex', gap: 0.75, mb: flagged ? 0.75 : 0, flexWrap: 'wrap' }}>
                                <Box sx={{
                                  px: 1, py: 0.3, borderRadius: 1, fontSize: '0.72rem', fontWeight: 700,
                                  background: inFlag ? '#fee2e2' : '#dcfce7',
                                  color: inFlag ? '#991b1b' : '#166534'
                                }}>
                                  📍 In: {inDist != null ? inDist.toLocaleString() + ' ft' : '—'}
                                </Box>
                                <Box sx={{
                                  px: 1, py: 0.3, borderRadius: 1, fontSize: '0.72rem', fontWeight: 700,
                                  background: outFlag ? '#fee2e2' : '#dcfce7',
                                  color: outFlag ? '#991b1b' : '#166534'
                                }}>
                                  📍 Out: {outDist != null ? outDist.toLocaleString() + ' ft' : '—'}
                                </Box>
                              </Box>

                              {flagged && notes.map((n, i) => (
                                <Box key={i} sx={{
                                  mt: 1,
                                  p: 1,
                                  borderLeft: '3px solid #dc2626',
                                  background: '#fff',
                                  borderRadius: 0.75,
                                  fontSize: '0.75rem'
                                }}>
                                  <Box sx={{ fontWeight: 800, color: '#991b1b', mb: 0.4 }}>
                                    🚨 {n.kind === 'in' ? 'Clock-In' : 'Clock-Out'} {n.distance.toLocaleString()} ft out of range
                                  </Box>
                                  <Box sx={{ color: '#374151', fontWeight: 500, mb: 0.25, wordBreak: 'break-word' }}>
                                    <Box component="span" sx={{ fontWeight: 700 }}>🏠 Patient:</Box> {n.patientAddress || '—'}
                                    {(n.patientCoord || n.clockGps) && (
                                      <Box component="button" type="button"
                                           onClick={() => setMapDialog({ patientName: g.patientName, patientAddress: n.patientAddress, patientCoord: n.patientCoord, kind: n.kind, clockCoord: n.clockGps, distanceFt: n.distance, miles: n.miles, driveMinutes: n.driveMinutes, directionsUrl: n.directionsUrl })}
                                           title="View map: red pin = patient, blue pin = caregiver"
                                           sx={{ ml: 0.75, color: '#2563eb', fontWeight: 700, textDecoration: 'none', background: 'transparent', border: 'none', cursor: 'pointer', font: 'inherit', p: 0 }}>🗺️ Show Map</Box>
                                    )}
                                  </Box>
                                  <Box sx={{ color: '#374151', fontWeight: 500, mb: 0.5, wordBreak: 'break-word' }}>
                                    <Box component="span" sx={{ fontWeight: 700 }}>📍 Clocked at:</Box> {n.clockGps || '—'}
                                    {(n.patientCoord || n.clockGps) && (
                                      <Box component="button" type="button"
                                           onClick={() => setMapDialog({ patientName: g.patientName, patientAddress: n.patientAddress, patientCoord: n.patientCoord, kind: n.kind, clockCoord: n.clockGps, distanceFt: n.distance, miles: n.miles, driveMinutes: n.driveMinutes, directionsUrl: n.directionsUrl })}
                                           title="View map: red pin = patient, blue pin = caregiver"
                                           sx={{ ml: 0.75, color: '#dc2626', fontWeight: 700, textDecoration: 'none', background: 'transparent', border: 'none', cursor: 'pointer', font: 'inherit', p: 0 }}>🗺️ Show Map</Box>
                                    )}
                                  </Box>
                                  {n.directionsUrl && (
                                    <Box component="a" href={n.directionsUrl} target="_blank" rel="noopener noreferrer"
                                         sx={{
                                           display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                           px: 1.25, py: 0.5, background: '#1e40af', color: '#fff',
                                           borderRadius: 1, fontSize: '0.7rem', fontWeight: 700,
                                           textDecoration: 'none', flexWrap: 'wrap'
                                         }}>
                                      🚗 Show Route
                                      {n.miles != null && <span>· ≈ {n.miles.toFixed(2)} mi</span>}
                                      {n.driveMinutes != null && <span>· ≈ {n.driveMinutes} min</span>}
                                    </Box>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          );
                        })}
                      </Box>
                    </Paper>
                  );
                })}

                {groupList.length > visibleGroups.length && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, py: 1.5 }}>
                    <Typography sx={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>
                      Showing {visibleGroups.length} of {groupList.length} patients
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setGpsVisibleCount(c => c + 25)}
                      sx={{
                        textTransform: 'none', fontWeight: 700, fontSize: '0.8rem',
                        px: 2.5, borderRadius: 2, color: '#0369a1',
                        border: '1px solid #bae6fd', background: '#f0f9ff',
                        '&:hover': { background: '#e0f2fe', borderColor: '#7dd3fc' }
                      }}
                    >
                      Load 25 more
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>
          );
        })()}

        {/* GPS Map Dialog (in-app, non-draggable pins) */}
        {mapDialog && <GpsMapDialog data={mapDialog} onClose={() => setMapDialog(null)} />}

        {/* Table View */}
        {tableView && groupedData.length > 0 && (
          <Box className="patient-print-area">
            {filteredData.length > displayLimit && (
              <Typography className="no-print" sx={{ 
                mb: 2, 
                p: 2, 
                bgcolor: '#e0f2fe', 
                color: '#0c4a6e',
                borderRadius: 2,
                fontWeight: 600,
                fontSize: '0.9rem',
                textAlign: 'center',
                border: '2px solid #38bdf8'
              }}>
                📊 Showing {displayLimit} of {filteredData.length} patients. Scroll down to load more.
              </Typography>
            )}
            {filteredData.slice(0, Math.min(filteredData.length, displayLimit)).map((patient) => {
              // Detect overlaps for this patient
              const overlaps = detectOverlaps(patient.visits);
              const hasOverlaps = overlaps.length > 0;
              const totalOverlapMins = overlaps.reduce((sum, o) => sum + o.overlapMinutes, 0);
              
              // Create overlap map for quick lookup
              const overlapMap = {};
              overlaps.forEach(overlap => {
                if (!overlapMap[overlap.visit1Index]) overlapMap[overlap.visit1Index] = [];
                if (!overlapMap[overlap.visit2Index]) overlapMap[overlap.visit2Index] = [];
                overlapMap[overlap.visit1Index].push(overlap);
                overlapMap[overlap.visit2Index].push(overlap);
              });

              return (
                <Paper key={patient.patientId} className="patient-card-print" data-patient-id={patient.patientId} sx={{ 
                  mb: { xs: 4, sm: 5 }, 
                  overflow: "hidden", 
                  borderRadius: 2, 
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: '2px solid #e5e7eb',
                  transition: 'all 0.3s ease',
                  background: 'white',
                  position: 'relative',
                  '&:not(:last-child)': {
                    paddingBottom: 3,
                    borderBottom: '3px dashed #cbd5e1',
                    marginBottom: 5
                  },
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    borderColor: '#10b981'
                  }
                }}>
                  {/* Patient Header */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: { xs: 1, sm: 1.5, md: 2 },
                      p: { xs: 1.5, sm: 2 },
                      background: "#10b981",
                      color: "white",
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {/* Selection checkbox for Print PDF */}
                    <Box
                      onClick={(e) => { e.stopPropagation(); togglePatientSelection(patient.patientId); }}
                      sx={{
                        width: 22, height: 22, flex: '0 0 auto',
                        borderRadius: '4px',
                        border: '2px solid rgba(255,255,255,0.85)',
                        background: selectedPatientIds.includes(patient.patientId) ? '#fff' : 'transparent',
                        color: '#10b981',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer',
                        fontWeight: 800,
                        fontSize: '0.85rem',
                        userSelect: 'none'
                      }}
                      title="Select for Print PDF"
                    >
                      {selectedPatientIds.includes(patient.patientId) ? '✓' : ''}
                    </Box>

                    {/* Column 1: Admission Number */}
                    <Box sx={{ minWidth: { xs: '45%', sm: '80px', md: '100px' }, flex: { xs: '1 1 45%', sm: '0 0 auto' } }}>
                      <Typography variant="caption" sx={{ 
                        fontWeight: 600, 
                        fontSize: { xs: '0.55rem', sm: '0.6rem' },
                        opacity: 0.85,
                        textTransform: 'uppercase',
                        display: 'block',
                        mb: 0.2
                      }}>
                        ID
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 700, 
                        fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.85rem' },
                        wordBreak: 'break-word'
                      }}>
                        {highlightMatch(patient.patientId, searchText)}
                      </Typography>
                    </Box>
                    
                    {/* Column 2: Patient Name */}
                    <Box sx={{ minWidth: { xs: '45%', sm: '120px', md: '180px' }, flex: { xs: '1 1 45%', sm: '1 1 auto' }, maxWidth: { xs: '55%', sm: 'none' } }}>
                      <Typography variant="caption" sx={{ 
                        fontWeight: 600, 
                        fontSize: { xs: '0.55rem', sm: '0.6rem' },
                        opacity: 0.85,
                        textTransform: 'uppercase',
                        display: 'block',
                        mb: 0.2
                      }}>
                        Patient
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.85rem' }, 
                        fontWeight: 700,
                        wordBreak: 'break-word',
                        lineHeight: 1.3
                      }}>
                        {highlightMatch(patient.patientName, searchText)}
                      </Typography>
                    </Box>
                    
                    {/* Column 3: Branch */}
                    <Box sx={{ minWidth: { xs: '45%', sm: '80px', md: '100px' }, flex: { xs: '1 1 45%', sm: '0 0 auto' } }}>
                      <Typography variant="caption" sx={{ 
                        fontWeight: 600, 
                        fontSize: { xs: '0.55rem', sm: '0.6rem' },
                        opacity: 0.85,
                        textTransform: 'uppercase',
                        display: 'block',
                        mb: 0.2
                      }}>
                        Branch
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' }, 
                        fontWeight: 600,
                        fontStyle: patient.branch === 'N/A' ? 'italic' : 'normal',
                        wordBreak: 'break-word'
                      }}>
                        {highlightMatch(patient.branch || 'N/A', searchText)}
                      </Typography>
                    </Box>
                    
                    {/* Column 4: Overlap Alert */}
                    {hasOverlaps && (
                      <Box sx={{ 
                        bgcolor: 'rgba(254, 226, 226, 0.95)',
                        px: { xs: 1, sm: 1.5 },
                        py: { xs: 0.5, sm: 0.8 },
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 0.5, sm: 0.8 },
                        border: '2px solid #ef4444',
                        flex: { xs: '1 1 45%', sm: '0 0 auto' },
                        minWidth: { xs: '45%', sm: 'auto' }
                      }}>
                        <Typography sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}>⚠️</Typography>
                        <Box>
                          <Typography variant="caption" sx={{ 
                            color: '#dc2626',
                            fontWeight: 700,
                            fontSize: { xs: '0.6rem', sm: '0.7rem' },
                            display: 'block',
                            lineHeight: 1.2
                          }}>
                            OVERLAP
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            color: '#991b1b',
                            fontWeight: 600,
                            fontSize: { xs: '0.55rem', sm: '0.65rem' },
                            lineHeight: 1.2
                          }}>
                            {totalOverlapMins} min
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    
                    {/* Column 5: Contract Name */}
                    <Box sx={{ minWidth: { xs: '45%', sm: '100px', md: '120px' }, flex: { xs: '1 1 45%', sm: '0 1 auto' } }}>
                      <Typography variant="caption" sx={{ 
                        fontWeight: 600, 
                        fontSize: { xs: '0.55rem', sm: '0.65rem' },
                        opacity: 0.85,
                        textTransform: 'uppercase',
                        display: 'block',
                        mb: 0.3
                      }}>
                        Contract
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        fontSize: { xs: '0.65rem', sm: '0.8rem', md: '0.875rem' }, 
                        fontWeight: 600,
                        fontStyle: patient.contract === 'N/A' ? 'italic' : 'normal',
                        wordBreak: 'break-word',
                        lineHeight: 1.3
                      }}>
                        {highlightMatch(patient.contract || 'N/A', searchText)}
                      </Typography>
                    </Box>
                    
                    {/* Column 6: Billed / Billable/Actual Hours */}
                    <Box sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 0.5, sm: 1 },
                      flex: { xs: '1 1 100%', sm: '0 0 auto' },
                      width: { xs: '100%', sm: 'auto' },
                      mt: { xs: 1, sm: 0 }
                    }}>
                      {/* Billed Hours */}
                      <Box sx={{ 
                        textAlign: 'center',
                        bgcolor: '#7c3aed',
                        px: { xs: 1, sm: 1.5 },
                        py: { xs: 0.4, sm: 0.6 },
                        borderRadius: 1.5,
                        border: '2px solid #6d28d9',
                        boxShadow: '0 2px 8px rgba(124,58,237,0.4)',
                        flex: { xs: 1, sm: '0 0 auto' }
                      }}>
                        <Typography variant="caption" sx={{ 
                          fontSize: { xs: '0.5rem', sm: '0.6rem' }, 
                          display: 'block', 
                          mb: 0.2,
                          color: '#ede9fe',
                          fontWeight: 700,
                          textTransform: 'uppercase'
                        }}>✅ Billed</Typography>
                        <Typography variant="h6" sx={{
                          fontWeight: 900,
                          fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem' },
                          color: '#ffffff',
                          lineHeight: 1
                        }}>
                          {(patient.billedHours || 0).toFixed(2)}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: { xs: '1rem', sm: '1.2rem' }, fontWeight: 900, color: 'white', opacity: 0.8 }}>/</Typography>
                      {/* Billable Hours */}
                      <Box sx={{ 
                        textAlign: 'center',
                        bgcolor: '#059669',
                        px: { xs: 1, sm: 1.5 },
                        py: { xs: 0.4, sm: 0.6 },
                        borderRadius: 1.5,
                        border: '2px solid #047857',
                        boxShadow: '0 2px 8px rgba(5,150,105,0.4)',
                        flex: { xs: 1, sm: '0 0 auto' }
                      }}>
                        <Typography variant="caption" sx={{ 
                          fontSize: { xs: '0.5rem', sm: '0.6rem' }, 
                          display: 'block', 
                          mb: 0.2,
                          color: '#d1fae5',
                          fontWeight: 700,
                          textTransform: 'uppercase'
                        }}>💰 Billable</Typography>
                        <Typography variant="h6" sx={{
                          fontWeight: 900,
                          fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem' },
                          color: '#ffffff',
                          lineHeight: 1
                        }}>
                          {(patient.billableHours || 0).toFixed(2)}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: { xs: '1rem', sm: '1.2rem' }, fontWeight: 900, color: 'white', opacity: 0.8 }}>/</Typography>
                      {/* Total Hours */}
                      <Box sx={{ 
                        textAlign: 'center',
                        bgcolor: '#3b82f6',
                        px: { xs: 1, sm: 1.5 },
                        py: { xs: 0.4, sm: 0.6 },
                        borderRadius: 1.5,
                        border: '2px solid #2563eb',
                        boxShadow: '0 2px 8px rgba(59,130,246,0.4)',
                        flex: { xs: 1, sm: '0 0 auto' }
                      }}>
                        <Typography variant="caption" sx={{ 
                          fontSize: { xs: '0.5rem', sm: '0.6rem' }, 
                          display: 'block', 
                          mb: 0.2,
                          color: '#dbeafe',
                          fontWeight: 700,
                          textTransform: 'uppercase'
                        }}>📊 Total</Typography>
                        <Typography variant="h6" sx={{
                          fontWeight: 900,
                          fontSize: { xs: '0.85rem', sm: '1rem', md: '1.1rem' },
                          color: '#ffffff',
                          lineHeight: 1
                        }}>
                          {(patient.totalHours || 0).toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {/* Column 7: Visits */}
                    <Box sx={{ textAlign: { xs: 'left', sm: 'center' }, minWidth: { xs: '45%', sm: '60px' }, flex: { xs: '1 1 45%', sm: '0 0 auto' } }}>
                      <Typography variant="caption" sx={{ 
                        fontWeight: 600, 
                        fontSize: { xs: '0.55rem', sm: '0.65rem' },
                        opacity: 0.85,
                        textTransform: 'uppercase',
                        display: 'block',
                        mb: 0.3
                      }}>
                        Visits
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
                        fontWeight: 700
                      }}>
                        {patient.visits.filter(v => v.remark === "On Time").length}/{patient.visits.length}
                      </Typography>
                    </Box>

                    {/* Column 8: Caregiver Summary Table */}
                    {(() => {
                      const caregiverHours = {};
                      patient.visits.forEach(visit => {
                        const name = (visit.caregiver || 'Unknown').trim();
                        if (!caregiverHours[name]) caregiverHours[name] = 0;
                        caregiverHours[name] += visit.hours || 0;
                      });
                      const caregivers = Object.entries(caregiverHours);
                      
                      if (caregivers.length === 0) return null;
                      
                      return (
                        <Box sx={{ 
                          flex: { xs: '1 1 100%', sm: '0 0 auto' },
                          minWidth: { xs: '100%', sm: 'auto' },
                          maxWidth: { xs: '100%', sm: '300px' },
                          mt: { xs: 1, sm: 0 }
                        }}>
                          <Box sx={{
                            bgcolor: 'white',
                            borderRadius: 2,
                            overflow: 'hidden',
                            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                            border: '1px solid #e5e7eb'
                          }}>
                            {/* Header */}
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              px: 1.5,
                              py: 0.75,
                              bgcolor: '#f9fafb',
                              borderBottom: '1px solid #e5e7eb'
                            }}>
                              <Typography sx={{
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                color: '#374151',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                Caregivers
                              </Typography>
                              <Typography sx={{
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                color: '#6b7280',
                                bgcolor: '#e5e7eb',
                                px: 0.85,
                                py: 0.1,
                                borderRadius: 999,
                                minWidth: 18,
                                textAlign: 'center'
                              }}>
                                {caregivers.length}
                              </Typography>
                            </Box>
                            {/* Caregiver Rows */}
                            {caregivers.map(([name, hours], idx) => {
                              const initial = (name || '?').trim().charAt(0).toUpperCase();
                              return (
                                <Box key={idx} sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  px: 1.5,
                                  py: 0.75,
                                  borderTop: idx === 0 ? 'none' : '1px solid #f3f4f6',
                                  '&:hover': { bgcolor: '#f9fafb' }
                                }}>
                                  <Box sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    bgcolor: '#eef2ff',
                                    color: '#4f46e5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    flexShrink: 0
                                  }}>
                                    {initial}
                                  </Box>
                                  <Typography sx={{
                                    flex: 1,
                                    fontSize: '0.78rem',
                                    fontWeight: 500,
                                    color: '#111827',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {name}
                                  </Typography>
                                  <Typography sx={{
                                    fontSize: '0.78rem',
                                    fontWeight: 600,
                                    color: '#374151',
                                    fontVariantNumeric: 'tabular-nums'
                                  }}>
                                    {formatHoursAsUnits(hours).toFixed(2)}
                                    <Box component="span" sx={{ fontSize: '0.62rem', color: '#9ca3af', ml: 0.4, fontWeight: 500 }}>hrs</Box>
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      );
                    })()}
                    
                  </Box>

                  {/* Content - Always Visible */}
                  <Box>
                    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, bgcolor: '#f9fafb' }}>
                      {/* Visit Details Table - Always Visible */}
                      <Box>
                        <Box sx={{ 
                          overflowX: "auto", 
                          width: '100%', 
                          WebkitOverflowScrolling: 'touch',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                          borderRadius: '12px',
                          border: '2px solid #e2e8f0',
                          '&::-webkit-scrollbar': {
                            height: '8px'
                          },
                          '&::-webkit-scrollbar-track': {
                            background: '#f1f5f9',
                            borderRadius: '4px'
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: '#94a3b8',
                            borderRadius: '4px',
                            '&:hover': {
                              background: '#64748b'
                            }
                          }
                        }}>
                            <table
                              style={{
                                width: "100%",
                                minWidth: "650px",
                                borderCollapse: "separate",
                                borderSpacing: 0,
                                fontSize: "0.9rem",
                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                backgroundColor: 'white'
                              }}
                            >
                              <thead>
                                <tr style={{ background: "#1e293b" }}>
                                  <th style={{ 
                                    padding: "10px 12px", 
                                    textAlign: "left", 
                                    borderBottom: "3px solid #1e3a8a", 
                                    fontWeight: 700, 
                                    color: 'white', 
                                    fontSize: '0.75rem', 
                                    letterSpacing: '0.5px', 
                                    textTransform: 'uppercase',
                                    borderRight: '1px solid rgba(255,255,255,0.15)'
                                  }}>
                                    🔢 Row ID
                                  </th>
                                  <th style={{ 
                                    padding: "10px 12px", 
                                    textAlign: "left", 
                                    borderBottom: "3px solid #1e3a8a", 
                                    fontWeight: 700, 
                                    color: 'white', 
                                    fontSize: '0.75rem', 
                                    letterSpacing: '0.5px', 
                                    textTransform: 'uppercase',
                                    borderRight: '1px solid rgba(255,255,255,0.15)'
                                  }}>
                                    👨‍⚕️ Caregiver
                                  </th>
                                  <th style={{ 
                                    padding: "10px 12px", 
                                    textAlign: "left", 
                                    borderBottom: "3px solid #1e3a8a", 
                                    fontWeight: 700, 
                                    color: 'white', 
                                    fontSize: '0.75rem', 
                                    letterSpacing: '0.5px', 
                                    textTransform: 'uppercase',
                                    borderRight: '1px solid rgba(255,255,255,0.15)'
                                  }}>
                                    📅 Date
                                      </th>
                                      <th style={{ 
                                        padding: "10px 12px", 
                                        textAlign: "center", 
                                        borderBottom: "3px solid #1e3a8a", 
                                        fontWeight: 700, 
                                        color: 'white', 
                                        fontSize: '0.75rem', 
                                        letterSpacing: '0.5px', 
                                        textTransform: 'uppercase',
                                        borderRight: '1px solid rgba(255,255,255,0.15)'
                                      }}>
                                        📋 Scheduled
                                      </th>
                                      <th style={{ 
                                        padding: "10px 12px", 
                                        textAlign: "center", 
                                        borderBottom: "3px solid #1e3a8a", 
                                        fontWeight: 700, 
                                        color: 'white', 
                                        fontSize: '0.75rem', 
                                        letterSpacing: '0.5px', 
                                        textTransform: 'uppercase',
                                        borderRight: '1px solid rgba(255,255,255,0.15)'
                                      }}>
                                        ✅ Actual
                                      </th>
                                      <th style={{ 
                                        padding: "10px 12px", 
                                        textAlign: "center", 
                                        borderBottom: "3px solid #1e3a8a", 
                                        fontWeight: 700, 
                                        color: 'white', 
                                        fontSize: '0.75rem', 
                                        letterSpacing: '0.5px', 
                                        textTransform: 'uppercase',
                                        borderRight: '1px solid rgba(255,255,255,0.15)'
                                      }}>
                                        ⏱️ Actual / Scheduled
                                      </th>
                                      <th style={{ 
                                        padding: "10px 12px", 
                                        textAlign: "center", 
                                        borderBottom: "3px solid #1e3a8a", 
                                        fontWeight: 700, 
                                        color: 'white', 
                                        fontSize: '0.75rem', 
                                        letterSpacing: '0.5px', 
                                        textTransform: 'uppercase',
                                        borderRight: '1px solid rgba(255,255,255,0.15)'
                                      }}>
                                        💰 Billed
                                      </th>
                                      <th style={{ 
                                        padding: "10px 12px", 
                                        textAlign: "left", 
                                        borderBottom: "3px solid #1e3a8a", 
                                        fontWeight: 700, 
                                        color: 'white', 
                                        fontSize: '0.75rem', 
                                        letterSpacing: '0.5px', 
                                        textTransform: 'uppercase',
                                        minWidth: '180px'
                                      }}>
                                        📝 Status
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {patient.visits.map((visit, vIdx) => {
                                      // Check if this visit has overlaps
                                      const visitOverlaps = overlapMap[vIdx] || [];
                                      const hasOverlap = visitOverlaps.length > 0;
                                      
                                      const isMissed = visit.remark === "Missed In/Out";
                                      const hasIssue = visit.remark !== "On Time";
                                      const hasLateIn = visit.remark && visit.remark.includes('Late In');
                                      const hasEarlyIn = visit.remark && visit.remark.includes('Early In');
                                      const hasEarlyOut = visit.remark && visit.remark.includes('Early Out');
                                      const hasLateOut = visit.remark && visit.remark.includes('Late Out');
                                      const hasTimeIssue = hasLateIn || hasEarlyIn || hasEarlyOut || hasLateOut;
                                      const overflow = isVisitOverflow(visit, patient.patientId);
                                      const isOverflow = !!overflow;
                                      // Uses displayed (unit-rounded) values so "0.00 / X.XX" on screen always triggers.
                                      const isZeroWorked = formatHoursAsUnits(visit.scheduledHours || 0) === 0 && formatHoursAsUnits(visit.hours || 0) > 0;
                                      
                                      // Prioritize overlap color over other states
                                      // Use bright yellow for Late In, Early In, Early In, Late Out, and Early Out issues
                                      const rowBgColor = isOverflow ? '#fecaca' : (isZeroWorked ? '#fee2e2' : (hasOverlap ? '#fef2f2' : (isMissed ? '#fef2f2' : (hasTimeIssue ? '#FFF59D' : (hasIssue ? '#fffbeb' : vIdx % 2 === 0 ? '#f8fafc' : 'white')))));
                                      const borderLeftColor = isOverflow ? '#b91c1c' : (isZeroWorked ? '#dc2626' : (hasOverlap ? '#ef4444' : (isMissed ? '#ef4444' : (hasTimeIssue ? '#FFEB3B' : (hasIssue ? '#f59e0b' : 'transparent')))));
                                      const hoverColor = isOverflow ? '#fca5a5' : (isZeroWorked ? '#fecaca' : (hasOverlap ? '#fee2e2' : (isMissed ? '#fee2e2' : (hasTimeIssue ? '#FFEE58' : (hasIssue ? '#fef3c7' : '#ecfdf5')))));
                                      
                                      return (
                                        <tr key={vIdx} style={{ 
                                          borderBottom: `2px solid #e5e7eb`, 
                                          transition: 'all 0.3s ease', 
                                          backgroundColor: rowBgColor, 
                                          borderLeft: (hasIssue || hasOverlap || isOverflow) ? `6px solid ${borderLeftColor}` : 'none' 
                                        }} 
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.background = hoverColor;
                                          e.currentTarget.style.transform = 'translateX(4px)';
                                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                        }} 
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.background = rowBgColor;
                                          e.currentTarget.style.transform = 'translateX(0)';
                                          e.currentTarget.style.boxShadow = 'none';
                                        }}>
                                          <td style={{ 
                                            padding: "10px 12px", 
                                            color: '#4b5563', 
                                            fontWeight: 400,
                                            fontSize: '0.8rem',
                                            borderRight: '1px solid #e5e7eb'
                                          }}>
                                            #{vIdx + 1}
                                          </td>
                                          <td style={{ 
                                            padding: "10px 12px", 
                                            color: '#1f2937', 
                                            fontWeight: 400,
                                            fontSize: '0.8rem',
                                            borderRight: '1px solid #e5e7eb'
                                          }}>
                                            {highlightMatch(visit.caregiver, searchText)}
                                          </td>
                                          <td style={{ 
                                            padding: "10px 12px", 
                                            color: '#4b5563', 
                                            whiteSpace: 'nowrap',
                                            fontSize: '0.75rem',
                                            fontWeight: 400,
                                            borderRight: '1px solid #e5e7eb'
                                          }}>
                                            {highlightMatch(visit.date, searchText)}
                                          </td>
                                          <td style={{ 
                                            padding: "10px 12px", 
                                            color: '#059669', 
                                            fontSize: '0.75rem', 
                                            textAlign: 'center', 
                                            fontWeight: 400,
                                            borderRight: '1px solid #e5e7eb',
                                            backgroundColor: 'rgba(5, 150, 105, 0.03)'
                                          }}>
                                            {highlightMatch(visit.actualSchedule || "", searchText)}
                                          </td>
                                          <td style={{ 
                                            padding: "10px 12px", 
                                            color: '#7c3aed', 
                                            fontSize: '0.75rem', 
                                            textAlign: 'center', 
                                            fontWeight: 400,
                                            borderRight: '1px solid #e5e7eb',
                                            backgroundColor: 'rgba(124, 58, 237, 0.03)'
                                          }}>
                                            {highlightMatch(visit.visitSchedule || "", searchText)}
                                          </td>
                                          <td style={{ 
                                            padding: "10px 12px", 
                                            textAlign: "center", 
                                            fontWeight: isZeroWorked ? 700 : 400, 
                                            color: isZeroWorked ? '#b91c1c' : '#047857', 
                                            fontSize: '0.8rem',
                                            borderRight: '1px solid #e5e7eb',
                                            backgroundColor: isZeroWorked ? 'rgba(220, 38, 38, 0.10)' : 'rgba(16, 185, 129, 0.03)'
                                          }}>
                                            {formatHoursAsUnits(visit.scheduledHours).toFixed(2)} / {formatHoursAsUnits(visit.hours).toFixed(2)}
                                          </td>
                                          <td style={{ 
                                            padding: "10px 12px", 
                                            textAlign: "center",
                                            borderRight: '1px solid #e5e7eb'
                                          }}>
                                            <span
                                              style={{
                                                background: visit.billed === "Yes" ? "#10b981" : "#ef4444",
                                                color: "white",
                                                padding: "4px 10px",
                                                borderRadius: "4px",
                                                fontSize: "0.7rem",
                                                fontWeight: 500
                                              }}
                                            >
                                              {visit.billed === "Yes" ? "Yes" : "No"}
                                            </span>
                                          </td>
                                          <td style={{ padding: "10px 12px", textAlign: "left", fontSize: '0.75rem', minWidth: '180px' }}>
                                            {isOverflow && (
                                              <Box sx={{ mb: 0.5 }}>
                                                <StatusBadge
                                                  variant="danger"
                                                  size="sm"
                                                  pulse
                                                  icon="🚫"
                                                  label="24 Hrs Cross"
                                                  detail={`${overflow.source === 'patient' ? 'Patient' : 'Caregiver'} Sched ${overflow.scheduled.toFixed(1)}h`}
                                                  title={`Same ${overflow.source === 'patient' ? 'patient' : 'caregiver'} on ${visit.date}: scheduled ${overflow.scheduled.toFixed(2)}h, actual ${overflow.actual.toFixed(2)}h (exceeds 24h)`}
                                                />
                                              </Box>
                                            )}
                                            {isZeroWorked && (
                                              <Box sx={{ mb: 0.5 }}>
                                                <StatusBadge
                                                  variant="danger"
                                                  size="sm"
                                                  icon="⛔"
                                                  label="0 Hrs Worked"
                                                  detail={`Sched ${visit.hours.toFixed(2)}h`}
                                                  title={`Caregiver did not work: scheduled ${visit.hours.toFixed(2)}h, actual 0.00h on ${visit.date}`}
                                                />
                                              </Box>
                                            )}
                                            {hasOverlap ? (
                                              <VisitStatusCell
                                                remark={visit.remark}
                                                hasOverlap
                                                overlapMinutes={visitOverlaps.map(o => o.overlapMinutes).reduce((a, b) => a + b, 0)}
                                                size="sm"
                                              />
                                            ) : (
                                              <VisitStatusCell remark={visit.remark} size="sm" />
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                            </Box>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
            
            {/* Load More Button for Table View */}
            {filteredData.length > displayLimit && (
              <Box className="no-print" sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                mt: 4,
                mb: 2
              }}>
                <Button
                  variant="contained"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  sx={{
                    background: '#16a34a',
                    px: 5,
                    py: 1.5,
                    fontWeight: 700,
                    fontSize: '1rem',
                    borderRadius: 3,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                    textTransform: 'none',
                    minWidth: '200px',
                    '&:hover': {
                      background: '#15803d',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                      },
                    '&:disabled': {
                      background: '#9ca3af',
                      boxShadow: 'none'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {isLoadingMore ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          border: '3px solid white',
                          borderTop: '3px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' }
                          }
                        }}
                      />
                      Loading...
                    </Box>
                  ) : (
                    `⬇️ Load More (${Math.min(50, filteredData.length - displayLimit)} more)`
                  )}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Card View */}
        {!tableView && groupedData.length > 0 && (
          <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
            {filteredData.slice(0, Math.min(filteredData.length, displayLimit)).map((patient) => {
              const caregivers = {};
              patient.visits.forEach((v) => {
                const name = (v.caregiver || 'Unknown').trim();
                if (!caregivers[name]) caregivers[name] = [];
                caregivers[name].push(v);
              });

              return (
                <Grid item xs={12} sm={6} md={6} lg={4} key={patient.patientId}>
                  <Card sx={{ 
                    height: '100%',
                    borderRadius: 3,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: '2px solid #10b981',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(5, 150, 105, 0.3)',
                      transform: 'translateY(-4px)'
                    }
                  }}>
                    <CardContent sx={{ p: 0 }}>
                      {/* Card Header */}
                      <Box
                        sx={{
                          background: "#16a34a",
                          color: "white",
                          p: { xs: 1.5, sm: 2, md: 2.5 },
                          cursor: "pointer",
                          borderBottom: '3px solid #047857',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            background: "linear-gradient(135deg, #047857 0%, #059669 100%)",
                          }
                        }}
                        onClick={() => togglePatient(patient.patientId)}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}>
                              {highlightMatch(patient.patientName, searchText)}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1, fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' } }}>
                              ID: {highlightMatch(patient.patientId, searchText)}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
                              <Box sx={{ 
                                bgcolor: "rgba(255,255,255,0.2)",
                                px: { xs: 1, sm: 1.5 },
                                py: { xs: 0.3, sm: 0.5 },
                                borderRadius: 1.5,
                                border: '1px solid rgba(255,255,255,0.3)',
                                backdropFilter: 'blur(10px)',
                                fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.8rem' },
                                fontWeight: 600
                              }}>
                                Billed: {(patient.billedHours || 0).toFixed(2)}
                              </Box>
                              <Box sx={{ 
                                bgcolor: "rgba(255,255,255,0.2)",
                                px: { xs: 1, sm: 1.5 },
                                py: { xs: 0.3, sm: 0.5 },
                                borderRadius: 1.5,
                                border: '1px solid rgba(255,255,255,0.3)',
                                backdropFilter: 'blur(10px)',
                                fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.8rem' },
                                fontWeight: 600
                              }}>
                                Billable: {(patient.billableHours || 0).toFixed(2)}
                              </Box>
                              <Box sx={{ 
                                bgcolor: "rgba(255,255,255,0.2)",
                                px: { xs: 1, sm: 1.5 },
                                py: { xs: 0.3, sm: 0.5 },
                                borderRadius: 1.5,
                                border: '1px solid rgba(255,255,255,0.3)',
                                backdropFilter: 'blur(10px)',
                                fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.8rem' },
                                fontWeight: 600
                              }}>
                                Actual: {(patient.totalHours || 0).toFixed(2)}
                              </Box>
                              <Box sx={{ 
                                bgcolor: "rgba(255,255,255,0.2)",
                                px: { xs: 1, sm: 1.5 },
                                py: { xs: 0.3, sm: 0.5 },
                                borderRadius: 1.5,
                                border: '1px solid rgba(255,255,255,0.3)',
                                backdropFilter: 'blur(10px)',
                                fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.8rem' },
                                fontWeight: 600
                              }}>
                                Visits: {patient.visits.filter(v => v.remark === "On Time").length}/{patient.visits.length}
                              </Box>
                            </Box>
                            {/* Caregiver Hours Summary Table */}
                            {Object.keys(caregivers).length > 0 && (
                              <Box sx={{ 
                                mt: 1.5,
                                bgcolor: 'rgba(255,255,255,0.95)',
                                borderRadius: 1.5,
                                overflow: 'hidden',
                                border: '2px solid rgba(255,255,255,0.5)'
                              }}>
                                {/* Mini Table Header */}
                                <Box sx={{
                                  display: 'flex',
                                  bgcolor: '#047857',
                                  px: 1,
                                  py: 0.4
                                }}>
                                  <Typography sx={{ 
                                    flex: 1,
                                    fontSize: '0.6rem',
                                    fontWeight: 700,
                                    color: 'white',
                                    textTransform: 'uppercase'
                                  }}>
                                    👤 Caregiver
                                  </Typography>
                                  <Typography sx={{ 
                                    width: '55px',
                                    fontSize: '0.6rem',
                                    fontWeight: 700,
                                    color: 'white',
                                    textAlign: 'right',
                                    textTransform: 'uppercase'
                                  }}>
                                    Hours
                                  </Typography>
                                </Box>
                                {/* Rows */}
                                {Object.entries(caregivers).map(([name, visits], idx) => {
                                  const totalHrs = visits.reduce((sum, v) => sum + formatHoursAsUnits(v.hours || 0), 0);
                                  const scheduledHrs = visits.reduce((sum, v) => sum + formatHoursAsUnits(v.scheduledHours || 0), 0);
                                  return (
                                    <Box key={idx} sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      px: 1,
                                      py: 0.3,
                                      bgcolor: idx % 2 === 0 ? '#f0fdf4' : 'white',
                                      borderTop: '1px solid #d1fae5'
                                    }}>
                                      <Typography sx={{ 
                                        flex: 1,
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        color: '#065f46',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }}>
                                        {name}
                                      </Typography>
                                      <Typography sx={{ 
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        color: 'white',
                                        bgcolor: '#059669',
                                        px: 0.8,
                                        py: 0.1,
                                        borderRadius: 0.5,
                                        textAlign: 'center',
                                        minWidth: '55px'
                                      }}>
                                        {scheduledHrs.toFixed(2)}/{totalHrs.toFixed(2)}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                              </Box>
                            )}
                          </Box>
                          <IconButton sx={{ color: "white", mt: -0.5, p: { xs: 0.5, sm: 1 } }}>
                            {expandedPatients[patient.patientId] ? (
                              <ExpandLessIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                            ) : (
                              <ExpandMoreIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                            )}
                          </IconButton>
                        </Box>
                      </Box>
                      
                      {/* Card Content */}
                      <Collapse
                        in={expandedPatients[patient.patientId]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box sx={{ p: 2.5, bgcolor: '#f9fafb' }}>
                          {Object.keys(caregivers).map((cgKey, idx) => {
                            return (
                            <Box key={idx} sx={{ mb: idx < Object.keys(caregivers).length - 1 ? 3 : 0 }}>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1, 
                                mb: 1.5,
                                p: 1.5,
                                bgcolor: 'white',
                                borderRadius: 2,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                                border: '1px solid #e5e7eb'
                              }}>
                                <AssignmentIcon sx={{ color: "#059669", fontSize: '1.2rem' }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937', fontSize: '0.95rem' }}>
                                  {cgKey}
                                </Typography>
                                <Typography variant="caption" sx={{ 
                                  ml: 'auto', 
                                  bgcolor: '#d1fae5', 
                                  color: '#065f46',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontWeight: 600
                                }}>
                                  {caregivers[cgKey].length} visits
                                </Typography>
                              </Box>
                              {caregivers[cgKey].map((visit, vIdx) => {
                                const isMissed = visit.remark === "Missed In/Out";
                                const hasIssue = visit.remark !== "On Time";
                                const hasLateIn = visit.remark && visit.remark.includes('Late In');
                                const hasEarlyIn = visit.remark && visit.remark.includes('Early In');
                                const hasEarlyOut = visit.remark && visit.remark.includes('Early Out');
                                const hasLateOut = visit.remark && visit.remark.includes('Late Out');
                                const hasTimeIssue = hasLateIn || hasEarlyIn || hasEarlyOut || hasLateOut;
                                const overflow = isVisitOverflow({ ...visit, caregiver: visit.caregiver || cgKey }, patient.patientId);
                                const isOverflow = !!overflow;
                                const isZeroWorked = formatHoursAsUnits(visit.scheduledHours || 0) === 0 && formatHoursAsUnits(visit.hours || 0) > 0;
                                const bgColor = isOverflow ? '#fecaca' : (isZeroWorked ? '#fee2e2' : (isMissed ? '#fee2e2' : (hasTimeIssue ? '#FFF59D' : (hasIssue ? '#fef3c7' : 'white'))));
                                const borderColor = isOverflow ? '#b91c1c' : (isZeroWorked ? '#dc2626' : (isMissed ? '#dc2626' : (hasTimeIssue ? '#FFEB3B' : (hasIssue ? '#f59e0b' : '#e5e7eb'))));
                                
                                return (
                                  <Box key={vIdx} sx={{ 
                                    mb: 1,
                                    p: 1.5,
                                    bgcolor: bgColor,
                                    borderRadius: 2,
                                    border: `2px solid ${borderColor}`,
                                    borderLeft: (hasIssue || isOverflow) ? `5px solid ${borderColor}` : `2px solid ${borderColor}`,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                      transform: 'translateX(4px)'
                                    }
                                  }}>
                                    {isOverflow && (
                                      <Box sx={{ mb: 0.75 }}>
                                        <StatusBadge
                                          variant="danger"
                                          size="sm"
                                          pulse
                                          icon="🚫"
                                          label="24 Hrs Cross"
                                          detail={`${overflow.source === 'patient' ? 'Patient' : 'Caregiver'} Sched ${overflow.scheduled.toFixed(1)}h`}
                                          title={`Same ${overflow.source === 'patient' ? 'patient' : 'caregiver'} on ${visit.date}: scheduled ${overflow.scheduled.toFixed(2)}h, actual ${overflow.actual.toFixed(2)}h (exceeds 24h)`}
                                        />
                                      </Box>
                                    )}
                                    {isZeroWorked && (
                                      <Box sx={{ mb: 0.75 }}>
                                        <StatusBadge
                                          variant="danger"
                                          size="sm"
                                          icon="⛔"
                                          label="0 Hrs Worked"
                                          detail={`Sched ${visit.hours.toFixed(2)}h`}
                                          title={`Caregiver did not work: scheduled ${visit.hours.toFixed(2)}h, actual 0.00h on ${visit.date}`}
                                        />
                                      </Box>
                                    )}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#6b7280', fontSize: '0.75rem' }}>
                                        {highlightMatch(visit.date, searchText)}
                                      </Typography>
                                      <Box sx={{ 
                                        bgcolor: visit.billed === "Yes" ? "#059669" : "#ef4444",
                                        color: "white",
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        textTransform: 'uppercase'
                                      }}>
                                        {visit.billed}
                                      </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                      <Typography variant="caption" sx={{ 
                                        fontFamily: 'monospace', 
                                        fontSize: '0.8rem', 
                                        fontWeight: 600,
                                        color: '#1f2937',
                                        bgcolor: 'rgba(5, 150, 105, 0.1)',
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 0.5
                                      }}>
                                        Actual: {visit.actualSchedule}
                                      </Typography>
                                      <Typography variant="caption" sx={{ 
                                        fontFamily: 'monospace', 
                                        fontSize: '0.8rem', 
                                        fontWeight: 600,
                                        color: '#1f2937',
                                        bgcolor: 'rgba(5, 150, 105, 0.1)',
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 0.5
                                      }}>
                                        Scheduled: {visit.visitSchedule}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <Typography variant="caption" sx={{ 
                                        fontWeight: 700, 
                                        color: isZeroWorked ? '#b91c1c' : '#059669',
                                        fontSize: '0.85rem'
                                      }}>
                                        Hours: {visit.hours.toFixed(2)}
                                      </Typography>
                                      <VisitStatusCell remark={visit.remark} size="xs" />
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          );
                          })}
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
            
            {/* Load More Button for Card View */}
            {filteredData.length > displayLimit && (
              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mt: 2
                }}>
                  <Button
                    variant="contained"
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    sx={{
                      background: '#16a34a',
                      px: 5,
                      py: 1.5,
                      fontWeight: 700,
                      fontSize: '1rem',
                      borderRadius: 3,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                      textTransform: 'none',
                      minWidth: '200px',
                      '&:hover': {
                        background: '#15803d',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                        },
                      '&:disabled': {
                        background: '#9ca3af',
                        boxShadow: 'none'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {isLoadingMore ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            border: '3px solid white',
                            borderTop: '3px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            '@keyframes spin': {
                              '0%': { transform: 'rotate(0deg)' },
                              '100%': { transform: 'rotate(360deg)' }
                            }
                          }}
                        />
                        Loading...
                      </Box>
                    ) : (
                      `⬇️ Load More (${Math.min(50, filteredData.length - displayLimit)} more)`
                    )}
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        )}

        {groupedData.length === 0 && gpsData.length === 0 && (
          <Grid container spacing={3}>
            {/* Privacy Card */}
            <Grid item xs={12} md={6}>
              <Paper 
                sx={{ 
                  p: { xs: 2.5, sm: 3 }, 
                  borderRadius: 3,
                  background: '#ffffff',
                  boxShadow: '0 8px 32px rgba(59, 130, 246, 0.25)',
                  border: '3px solid #3b82f6',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(59, 130, 246, 0.35)'
                  }
                }}
              >
                {/* Decorative Background Pattern */}
                <Box sx={{
                  position: 'absolute',
                  top: -50,
                  right: -50,
                  width: 150,
                  height: 150,
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.15)',
                  zIndex: 0
                }} />
                <Box sx={{
                  position: 'absolute',
                  bottom: -30,
                  left: -30,
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.1)',
                  zIndex: 0
                }} />

                <Box sx={{
                  textAlign: 'center',
                  maxWidth: 450,
                  mx: 'auto',
                  position: 'relative',
                  zIndex: 1
                }}>
                  {/* Icon with Pulse Animation */}
                  <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 70, sm: 80 },
                    height: { xs: 70, sm: 80 },
                    borderRadius: '50%',
                    background: '#1e293b',
                    mb: 2,
                    boxShadow: '0 8px 24px rgba(30, 64, 175, 0.4)',
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.05)' }
                    }
                  }}>
                    <UploadFileIcon sx={{ fontSize: { xs: 36, sm: 42 }, color: 'white' }} />
                  </Box>

                  <Typography variant="h5" sx={{ 
                    fontWeight: 800,
                    mb: 2,
                    color: '#1e3a8a',
                    fontSize: { xs: '1.25rem', sm: '1.4rem' },
                    textShadow: '0 2px 4px rgba(30, 58, 138, 0.1)'
                  }}>
                    🔒 100% Private & Secure
                  </Typography>
                  
                  <Box sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    p: 2,
                    borderRadius: 2,
                    mb: 2,
                    backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255, 255, 255, 0.5)'
                  }}>
                    <Typography variant="body2" sx={{ 
                      color: '#1e40af',
                      mb: 1.5,
                      fontSize: { xs: '0.85rem', sm: '0.9rem' },
                      lineHeight: 1.6,
                      fontWeight: 600
                    }}>
                      ✨ <strong>Your Data Never Leaves Your Device</strong>
                    </Typography>
                    
                    <Typography variant="caption" sx={{ 
                      color: '#1e3a8a',
                      fontSize: { xs: '0.75rem', sm: '0.8rem' },
                      lineHeight: 1.5,
                      display: 'block'
                    }}>
                      All processing happens directly in your browser. We never store, save, or transmit your files to any server. Your privacy is our priority.
                    </Typography>
                  </Box>

                  {/* Security Features */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 0.5,
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      border: '1px solid rgba(59, 130, 246, 0.3)'
                    }}>
                      <Typography sx={{ fontSize: '1rem' }}>✅</Typography>
                      <Typography variant="caption" sx={{ color: '#1e40af', fontWeight: 600, fontSize: '0.7rem' }}>
                        No Upload
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 0.5,
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      border: '1px solid rgba(59, 130, 246, 0.3)'
                    }}>
                      <Typography sx={{ fontSize: '1rem' }}>✅</Typography>
                      <Typography variant="caption" sx={{ color: '#1e40af', fontWeight: 600, fontSize: '0.7rem' }}>
                        No Storage
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 0.5,
                      bgcolor: 'rgba(255, 255, 255, 0.6)',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      border: '1px solid rgba(59, 130, 246, 0.3)'
                    }}>
                      <Typography sx={{ fontSize: '1rem' }}>✅</Typography>
                      <Typography variant="caption" sx={{ color: '#1e40af', fontWeight: 600, fontSize: '0.7rem' }}>
                        100% Local
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Instructions Card */}
            <Grid item xs={12} md={6}>
              <Paper 
                sx={{ 
                  p: { xs: 2.5, sm: 3 }, 
                  borderRadius: 3,
                  background: '#ffffff',
                  boxShadow: '0 8px 32px rgba(5, 150, 105, 0.25)',
                  border: '3px solid #10b981',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'auto',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(5, 150, 105, 0.35)'
                  }
                }}
              >
                <Box>
                  {/* Header */}
                  <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                    <Box sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: { xs: 60, sm: 70 },
                      height: { xs: 60, sm: 70 },
                      borderRadius: '50%',
                      background: '#15803d',
                      mb: 1.5,
                      boxShadow: '0 8px 24px rgba(4, 120, 87, 0.4)',
                      animation: 'bounce 2s infinite',
                      '@keyframes bounce': {
                        '0%, 100%': { transform: 'translateY(0)' },
                        '50%': { transform: 'translateY(-8px)' }
                      }
                    }}>
                      <AssignmentIcon sx={{ fontSize: { xs: 32, sm: 38 }, color: 'white' }} />
                    </Box>

                    <Typography variant="h5" sx={{ 
                      fontWeight: 800,
                      color: '#065f46',
                      fontSize: { xs: '1.15rem', sm: '1.3rem' },
                      textShadow: '0 2px 4px rgba(6, 95, 70, 0.1)'
                    }}>
                      🚀 Quick Start Guide
                    </Typography>
                  </Box>
                  
                  <Box sx={{ textAlign: 'left' }}>
                    {/* Requirements Section - Purple */}
                    <Box sx={{ 
                      mb: 2, 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: '#ffffff',
                      border: '2px solid #a855f7',
                      boxShadow: '0 2px 8px rgba(168, 85, 247, 0.15)'
                    }}>
                      <Typography variant="subtitle2" sx={{ 
                        color: '#7e22ce',
                        fontSize: { xs: '0.85rem', sm: '0.9rem' },
                        lineHeight: 1.4,
                        mb: 1,
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <Typography sx={{ fontSize: '1.2rem' }}>📌</Typography> Requirements
                      </Typography>
                      
                      <Typography variant="body2" sx={{ 
                        color: '#6b21a8',
                        fontSize: { xs: '0.75rem', sm: '0.8rem' },
                        lineHeight: 1.5,
                        pl: 1.5,
                        fontWeight: 500
                      }}>
                        • Access to HHA Enterprise with Visit Log Export permissions
                      </Typography>
                    </Box>

                    {/* Download Steps - Orange */}
                    <Box sx={{ 
                      mb: 2, 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: '#ffffff',
                      border: '2px solid #f97316',
                      boxShadow: '0 2px 8px rgba(249, 115, 22, 0.15)'
                    }}>
                      <Typography variant="subtitle2" sx={{ 
                        color: '#c2410c',
                        fontSize: { xs: '0.85rem', sm: '0.9rem' },
                        lineHeight: 1.4,
                        mb: 1,
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <Typography sx={{ fontSize: '1.2rem' }}>📥</Typography> Download from HHA
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                        {[1, 2, 3, 4].map((num, idx) => (
                          <Box key={num} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <Box sx={{
                              minWidth: 20,
                              height: 20,
                              borderRadius: '50%',
                              bgcolor: '#f97316',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              flexShrink: 0
                            }}>
                              {num}
                            </Box>
                            <Typography variant="body2" sx={{ 
                              color: '#9a3412',
                              fontSize: { xs: '0.75rem', sm: '0.8rem' },
                              lineHeight: 1.5,
                              flex: 1,
                              fontWeight: 500
                            }}>
                              {idx === 0 && 'Navigate to: Report → Visits → Visit → Visit Log'}
                              {idx === 1 && 'Set your billing parameters and click "View Report"'}
                              {idx === 2 && 'Click the Save Icon (top right, next to Next button)'}
                              {idx === 3 && 'Select CSV format and save to your computer'}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    {/* Upload Steps - Green */}
                    <Box sx={{ 
                      mb: 2, 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: '#ffffff',
                      border: '2px solid #10b981',
                      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.15)'
                    }}>
                      <Typography variant="subtitle2" sx={{ 
                        color: '#047857',
                        fontSize: { xs: '0.85rem', sm: '0.9rem' },
                        lineHeight: 1.4,
                        mb: 1,
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <Typography sx={{ fontSize: '1.2rem' }}>📤</Typography> Upload to System
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                        {[1, 2, 3].map((num, idx) => (
                          <Box key={num} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <Box sx={{
                              minWidth: 20,
                              height: 20,
                              borderRadius: '50%',
                              bgcolor: '#10b981',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              flexShrink: 0
                            }}>
                              {num}
                            </Box>
                            <Typography variant="body2" sx={{ 
                              color: '#065f46',
                              fontSize: { xs: '0.75rem', sm: '0.8rem' },
                              lineHeight: 1.5,
                              flex: 1,
                              fontWeight: 500
                            }}>
                              {idx === 0 && 'Click the "Import Visit Log" button above'}
                              {idx === 1 && 'Select the downloaded CSV file from your computer'}
                              {idx === 2 && 'Click "Open" to process your data'}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    {/* Export Steps - Blue */}
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: '#ffffff',
                      border: '2px solid #3b82f6',
                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.15)'
                    }}>
                      <Typography variant="subtitle2" sx={{ 
                        color: '#1e40af',
                        fontSize: { xs: '0.85rem', sm: '0.9rem' },
                        lineHeight: 1.4,
                        mb: 1,
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                        <Typography sx={{ fontSize: '1.2rem' }}>📊</Typography> Export Results
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                        {[1, 2, 3, 4].map((num, idx) => (
                          <Box key={num} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <Box sx={{
                              minWidth: 20,
                              height: 20,
                              borderRadius: '50%',
                              bgcolor: '#3b82f6',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              flexShrink: 0
                            }}>
                              {num}
                            </Box>
                            <Typography variant="body2" sx={{ 
                              color: '#1e3a8a',
                              fontSize: { xs: '0.75rem', sm: '0.8rem' },
                              lineHeight: 1.5,
                              flex: 1,
                              fontWeight: 500
                            }}>
                              {idx === 0 && 'Review and filter your data using the search tools'}
                              {idx === 1 && 'Expand patient records to view detailed visit information'}
                              {idx === 2 && 'Click "Export to Excel" to download analyzed data'}
                              {idx === 3 && 'The file includes billing hours, visit details, and status flags'}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Footer */}
        <Box sx={{
          mt: 4,
          py: 3.5,
          textAlign: 'center',
          borderTop: '3px solid',
          borderImage: '#e2e8f0 1',
          background: '#f8fafc',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.05)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative elements */}
          <Box sx={{
            position: 'absolute',
            top: -20,
            left: '10%',
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: 'rgba(59, 130, 246, 0.1)',
            zIndex: 0
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: -15,
            right: '15%',
            width: 30,
            height: 30,
            borderRadius: '50%',
            background: 'rgba(16, 185, 129, 0.1)',
            zIndex: 0
          }} />

          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h6" sx={{
              color: '#0f172a',
              fontSize: { xs: '0.95rem', sm: '1.05rem' },
              fontStyle: 'italic',
              mb: 1.5,
              fontWeight: 600,
              lineHeight: 1.6
            }}>
              ✨ "Simplicity is the ultimate sophistication" ✨
            </Typography>
            <Typography variant="body2" sx={{
              color: '#047857',
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              display: 'block',
              mb: 1,
              fontWeight: 500
            }}>
              We built this tool to make your work <strong>easier, faster, and more accurate</strong>.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{
                color: '#1e40af',
                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                fontWeight: 600
              }}>
                ❤️ Crafted with Care
              </Typography>
              <Typography sx={{ color: '#6b7280' }}>|</Typography>
              <Typography variant="caption" sx={{
                color: '#c2410c',
                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                fontWeight: 600
              }}>
                👨‍⚕️ Empowering Healthcare Professionals
              </Typography>
              <Typography sx={{ color: '#6b7280' }}>|</Typography>
              <Typography variant="caption" sx={{
                color: '#047857',
                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                fontWeight: 600
              }}>
                🚀 Built for Excellence
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
    </>
  );
}
